import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api, getApiErrorMessage } from '../lib/api'
import { unwrapAsyncResult } from '../lib/asyncOperation'
import { formatApiDateTimeDisplay } from '../lib/date'
import { openCustomerCardNewsWindow } from '../lib/customerCardNews'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'
import { AiFeatureBadge, AiGeneratedNotice } from '../components/AiDisclosure'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'
import HealthIntakeFormLayout from '../components/health/HealthIntakeFormLayout'
import HealthDataConsentFields from '../components/health/HealthDataConsentFields'
import { useHealthDataConsentBundle } from '../hooks/useHealthDataConsentBundle'
import {
  HEALTH_DATA_CONSENT_VARIANTS,
  allHealthDataConsentsChecked,
  buildHealthDataConsentPayload,
  createInitialHealthDataConsents,
} from '../lib/healthDataConsent'
import FeatureBillingQuotaBadge from '../components/billing/FeatureBillingQuotaBadge'
import FeatureGateNotice from '../components/billing/FeatureGateNotice'
import {
  FEATURE_BILLING_CODES,
  featureBillingBlockMessage,
  resolveFeatureBillingGate,
} from '../lib/featureBillingGate'

const SHOW_CUSTOMER_CARD_NEWS_BUTTONS = false

const EMPTY_LIST = []

/** 진료정보분석 폼 — 주민번호·연락처 숫자 길이 상한 */
const MAX_IDENTITY_DIGITS = 13
const MAX_PHONE_DIGITS = 12

/** 설계지원 고객 목록에서 수동으로 고객관리에 넣을 때 기본 메모 */
const HEALTH_ANALYSIS_CRM_MEMO = '진료정보 분석 고객관리 등록'
const HEALTH_ANALYSIS_REPORT_CODE = 'HEALTH_ANALYSIS_REPORT'

const pad2 = (n) => String(n).padStart(2, '0')

/** 연락처 숫자만 최대 12자리, 표시용 하이픈 (진료정보분석 폼) */
function formatPhoneDigitsKo(raw) {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '').slice(0, MAX_PHONE_DIGITS)
  if (!digits) return ''
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
}

const deriveBirthDateFromIdentityDigits = (identityDigits) => {
  const id = (identityDigits || '').toString().replaceAll(/[^0-9]/g, '')
  if (id.length < 6) return ''
  const yy = id.slice(0, 2)
  const mm = id.slice(2, 4)
  const dd = id.slice(4, 6)

  if (!/^\d{2}$/.test(yy) || !/^\d{2}$/.test(mm) || !/^\d{2}$/.test(dd)) return ''
  const mmN = Number(mm)
  const ddN = Number(dd)
  if (!(mmN >= 1 && mmN <= 12)) return ''
  if (!(ddN >= 1 && ddN <= 31)) return ''

  const yyN = Number(yy)
  const nowYY = Number(String(new Date().getFullYear()).slice(-2))
  const century = yyN >= 0 && yyN <= nowYY ? '20' : '19'
  return `${century}${yy}${mm}${dd}`
}

/** 숫자만 (최대 13) — 주민번호 상태값 */
const normalizeIdentityDigits = (s) =>
  String(s ?? '')
    .replace(/\D/g, '')
    .slice(0, MAX_IDENTITY_DIGITS)

/** 입력 중인 주민번호를 UI 표시용으로만 포맷 (저장·전송값과 무관). 6자리 이후는 하이픈 뒤에 입력한 숫자를 그대로 보여 입력 확인이 가능하다. */
const formatIdentityDigitsForUi = (identityDigits) => {
  const id = normalizeIdentityDigits(identityDigits)
  if (!id.length) return ''
  if (id.length <= 6) return id
  return `${id.slice(0, 6)}-${id.slice(6)}`
}

const toDateObject = (value) => {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  if (!s) return null

  // yyyyMMdd
  if (/^\d{8}$/.test(s)) {
    const y = Number(s.slice(0, 4))
    const m = Number(s.slice(4, 6))
    const d = Number(s.slice(6, 8))
    const dt = new Date(y, m - 1, d, 0, 0, 0)
    return Number.isNaN(dt.getTime()) ? null : dt
  }

  // yyyyMMddHHmmss
  if (/^\d{14}$/.test(s)) {
    const y = Number(s.slice(0, 4))
    const m = Number(s.slice(4, 6))
    const d = Number(s.slice(6, 8))
    const hh = Number(s.slice(8, 10))
    const mm = Number(s.slice(10, 12))
    const ss = Number(s.slice(12, 14))
    const dt = new Date(y, m - 1, d, hh, mm, ss)
    return Number.isNaN(dt.getTime()) ? null : dt
  }

  // "YYYY-MM-dd HH:mm:ss" 형태를 ISO로 보정
  const normalized = s.includes(' ') && !s.includes('T') ? s.replace(' ', 'T') : s
  const dt = new Date(normalized)
  return Number.isNaN(dt.getTime()) ? null : dt
}

const formatDate = (value) => {
  const dt = toDateObject(value)
  if (!dt) return ''
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
}

/**
 * 설계의뢰/알림 메모용: CODEF·내부 정규화 코드(예: AN760)를 KCD 읽기 형식(예: N76.0)으로 병기한다.
 * - 점(.)이 이미 있으면 대문자·공백만 정리
 * - 선행 A: 두 번째 문자가 A–Z인 경우에만 제거(양방 CODEF, 예: AN760 → N760)
 * - N760, K642, K0518 등: 앞 2자리 숫자 블록 뒤에 점 삽입
 */
const formatKcdDottedForMemo = (codeLike) => {
  if (codeLike == null || codeLike === undefined) return ''
  const raw = String(codeLike).trim()
  const rawUpper = raw.toUpperCase()
  if (!raw || raw === '$' || raw === '-' || rawUpper === 'N/A' || rawUpper === 'NA' || rawUpper === 'NULL') return ''
  const compactText = raw.replace(/\s+/g, '')
  if (compactText === '해당없음' || compactText === '해당 없음' || compactText === '없음') return ''
  if (raw.includes('.')) {
    return raw.replace(/\s/g, '').toUpperCase()
  }
  let c = raw.replace(/[\s.]/g, '').toUpperCase()
  if (!/[A-Z0-9]/.test(c)) return ''
  if (c.length >= 2 && c[0] === 'A' && c[1] >= 'A' && c[1] <= 'Z') {
    c = c.slice(1)
  }
  const m = c.match(/^([A-Z])(\d{2})(\d+)$/)
  if (m) {
    return `${m[1]}${m[2]}.${m[3]}`
  }
  return c
}

const diagnosisCodeLookupKeyForValue = (value) => {
  const code = formatKcdDottedForMemo(value)
  return code ? code.replace(/[^A-Z0-9]/gi, '').toUpperCase() : ''
}

/** 설계의뢰 메모: 입원/수술·구간 "일자/기간"은 최초~최말이며 총 입원일·치료/투약 누적일과 달 수 있음을 알린다. */
const MEMO_FOOTNOTE_INHOSP_DATE_RANGE =
  '※ 참고: "일자"·"기간"은 동일 진단(입원/수술)이 합쳐진 최초 진료(또는 입원)일부터 마지막일까지이며, "총 입원일"·"입원·재원" 일수, (진료/투약) 누적 수치와 1:1로 같지 않을 수 있습니다. (재방문·투약 연장 등)'

const formatDateTime = (value) => {
  const dt = toDateObject(value)
  if (!dt) return ''
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())} ${pad2(
    dt.getHours(),
  )}:${pad2(dt.getMinutes())}:${pad2(dt.getSeconds())}`
}

/** 진료정보 요약(dashboard)용 정수 파싱 — 앱 `_toInt`와 동일 계열 */
const toIntDashboard = (value, fallback = 0) => {
  if (value == null || value === undefined) return fallback
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value)
  const s = String(value).trim()
  if (!s) return fallback
  const n = parseInt(s.replace(/[^0-9-]/g, ''), 10)
  return Number.isFinite(n) ? n : fallback
}

const formatDashboardMoney = (value) => {
  const amount = toIntDashboard(value, 0)
  const negative = amount < 0
  const digits = String(Math.abs(amount))
  const parts = []
  for (let i = 0; i < digits.length; i++) {
    const indexFromEnd = digits.length - i
    parts.push(digits[i])
    if (indexFromEnd > 1 && indexFromEnd % 3 === 1) parts.push(',')
  }
  return `${negative ? '-' : ''}${parts.join('')}원`
}

const normDateDigitsDashboard = (v) => {
  if (!v) return null
  const s = String(v).replaceAll('-', '').trim()
  if (s.length !== 8) return null
  return s
}

const isDrugEpisodeDashboard = (ep) => {
  if (!ep) return false
  const source = String(ep.source || '').trim().toUpperCase()
  if (source === 'PRESCRIBE_DRUG') return true
  const hospital = String(ep.hospitalName || '')
  const treatType = String(ep.treatType || '')
  const treatTypeDetail = String(ep.treatTypeDetail || '')
  return (
    hospital.includes('약국') ||
    treatType.includes('조제') ||
    treatType.includes('처방') ||
    treatTypeDetail.includes('조제') ||
    treatTypeDetail.includes('처방')
  )
}

const isPrescriptionDrugEpisodeDashboard = (ep) =>
  String(ep?.source || '').trim().toUpperCase() === 'PRESCRIBE_DRUG'

const dayOfEpisodeDashboard = (ep) =>
  normDateDigitsDashboard(ep?.startDate) || normDateDigitsDashboard(ep?.endDate)

const hospitalKeyDashboard = (ep) => {
  const norm = (v) => String(v || '').replaceAll(/\s+/g, ' ').trim()
  const name = norm(ep?.hospitalName)
  const code = norm(ep?.hospitalCode)
  return name || code || '_'
}

const displayEncounterCountDashboard = (group) => {
  const display = toIntDashboard(group?.encounterCountDisplay, -1)
  if (display > 0) return display
  const encounter = toIntDashboard(group?.encounterCount, -1)
  if (encounter > 0) return encounter
  const count = toIntDashboard(group?.count, 0)
  return count < 0 ? 0 : count
}

const filterEpisodesWithinDaysDashboard = (episodes, maxDays, baseDate) => {
  if (!Array.isArray(episodes) || episodes.length === 0) return []
  const baseLocal = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate())
  const out = []
  for (const ep of episodes) {
    const digits = dayOfEpisodeDashboard(ep)
    if (!digits) continue
    const dt = toDateObject(digits)
    if (!dt) continue
    const epLocal = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
    const days = Math.floor((baseLocal.getTime() - epLocal.getTime()) / (24 * 60 * 60 * 1000))
    if (days < 0 || days > maxDays) continue
    out.push(ep)
  }
  return out
}

const countDistinctVisitsDashboard = (episodes, predicate) => {
  const visits = new Set()
  for (const ep of episodes || []) {
    if (!ep) continue
    if (predicate && !predicate(ep)) continue
    if (isDrugEpisodeDashboard(ep)) continue
    const day = dayOfEpisodeDashboard(ep)
    if (!day) continue
    visits.add(`${day}|${hospitalKeyDashboard(ep)}`)
  }
  return visits.size
}

const countDentalVisitCountDashboard = (dentalView) => {
  if (!dentalView || typeof dentalView !== 'object') return 0
  let groups =
    Array.isArray(dentalView.all5Y) && dentalView.all5Y.length > 0 ? dentalView.all5Y : dentalView.within5Y
  if (!Array.isArray(groups)) groups = []
  const visits = new Set()
  for (const group of groups) {
    const details = Array.isArray(group?.details) ? group.details : []
    for (const ep of details) {
      if (isDrugEpisodeDashboard(ep)) continue
      const day = dayOfEpisodeDashboard(ep)
      if (!day) continue
      visits.add(`${day}|${hospitalKeyDashboard(ep)}`)
    }
  }
  return visits.size
}

/** 앱 `HealthAnalysisPage._buildDashboardFallback`와 동일한 구조의 폴백 */
function buildHealthDashboardFallback(summary, profile, baseDate) {
  const costStats = summary?.costStats && typeof summary.costStats === 'object' ? summary.costStats : {}
  const healthyView = profile?.healthyView && typeof profile.healthyView === 'object' ? profile.healthyView : {}
  const substandardView =
    profile?.substandardView && typeof profile.substandardView === 'object' ? profile.substandardView : {}
  const dentalView = profile?.dentalView && typeof profile.dentalView === 'object' ? profile.dentalView : {}
  const rawEpisodes = Array.isArray(profile?.allEpisodesRaw) ? profile.allEpisodesRaw : []

  let minDate = null
  let maxDate = null
  for (const ep of rawEpisodes) {
    const start = normDateDigitsDashboard(ep?.startDate)
    const end = normDateDigitsDashboard(ep?.endDate)
    for (const candidate of [start, end]) {
      if (!candidate) continue
      if (minDate === null || candidate < minDate) minDate = candidate
      if (maxDate === null || candidate > maxDate) maxDate = candidate
    }
  }

  const recent3MonthsRaw = filterEpisodesWithinDaysDashboard(rawEpisodes, 90, baseDate)
  const hosp5yGroups = Array.isArray(healthyView.HOSP_5Y) ? healthyView.HOSP_5Y : []
  const drug730Groups = Array.isArray(healthyView.DRUG_7_30_5Y) ? healthyView.DRUG_7_30_5Y : []

  let inpatientCount5y = 0
  let surgeryCount5y = 0
  for (const group of hosp5yGroups) {
    const enc = displayEncounterCountDashboard(group)
    if (group?.inpatient === true) inpatientCount5y += enc
    surgeryCount5y += toIntDashboard(group?.surgeryCount, 0)
  }

  let treat7DiseaseCount = 0
  let drug30DiseaseCount = 0
  for (const group of drug730Groups) {
    if (toIntDashboard(group?.sumTreatmentDays, 0) >= 7) treat7DiseaseCount += 1
    if (toIntDashboard(group?.sumDrugDays, 0) >= 30) drug30DiseaseCount += 1
  }

  const eps5y = filterEpisodesWithinDaysDashboard(rawEpisodes, 365 * 5, baseDate)

  return {
    period: { from: minDate, to: maxDate },
    cost: {
      totalMedicalCost: costStats.totalMedicalCost,
      insuranceBenefit: costStats.insuranceBenefit,
      outOfPocket: costStats.outOfPocket,
    },
    recent3Months: {
      treatCount: recent3MonthsRaw.filter((ep) => !isDrugEpisodeDashboard(ep)).length,
      drugCount: recent3MonthsRaw.filter(isPrescriptionDrugEpisodeDashboard).length,
    },
    fiveYears: {
      inpatientCount: inpatientCount5y,
      surgeryCount: surgeryCount5y,
      dentalVisitCount: countDentalVisitCountDashboard(dentalView),
    },
    sameDisease: {
      treat7DiseaseCount,
      drug30DiseaseCount,
    },
    majorDisease: {
      major11Count: Array.isArray(healthyView.MAJOR11_5Y) ? healthyView.MAJOR11_5Y.length : 0,
      major6Count: Array.isArray(substandardView.MAJOR6_5Y) ? substandardView.MAJOR6_5Y.length : 0,
    },
    carInsurance: {
      enabled: profile?.carInsuranceEnabled === true,
      status: String(profile?.carInsuranceStatus || ''),
      inpatientCount: countDistinctVisitsDashboard(eps5y, (ep) =>
        String(ep?.source || '').startsWith('CAR_') && ep?.inpatient === true,
      ),
      outpatientCount: countDistinctVisitsDashboard(eps5y, (ep) =>
        String(ep?.source || '').startsWith('CAR_') && ep?.inpatient !== true,
      ),
    },
  }
}

export default function HealthAnalysisPage() {
  const { show } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const crmPrefillFromUrlRef = useRef(false)
  const { hasAnyRole } = useAuth() || { hasAnyRole: () => false }
  const isOperator = hasAnyRole && hasAnyRole([Roles.OPERATOR])
  const isGaScopeAdmin = !isOperator && hasAnyRole && hasAnyRole([
    Roles.GA_LEADER,
    Roles.GA_MANAGER,
    Roles.GA_DEPUTY_LEADER,
    Roles.GA_BRANCH_MANAGER,
    Roles.GA_DEPUTY_BRANCH_MANAGER,
    Roles.GA_OTHER_ADMIN,
    Roles.GA_MASTER_ADMIN,
    Roles.GA_MASTER_DEPUTY_ADMIN,
  ])
  const [exporting, setExporting] = useState(false)
  const [pdfExporting, setPdfExporting] = useState(false)
  const [templateImageExporting, setTemplateImageExporting] = useState(false)
  const [templateHtmlExporting, setTemplateHtmlExporting] = useState(false)
  const [reportTemplateOptions, setReportTemplateOptions] = useState([])
  const [reportTemplateId, setReportTemplateId] = useState('')
  const [reportHtmlTemplateOptions, setReportHtmlTemplateOptions] = useState([])
  const [reportHtmlTemplateId, setReportHtmlTemplateId] = useState('')
  const [retryingJobId, setRetryingJobId] = useState(null)
  const [manualRawUploading, setManualRawUploading] = useState(false)
  const manualRawUploadInputRef = useRef(null)
  const [healthCrmRegisterKey, setHealthCrmRegisterKey] = useState(null)
  const [failInfoJob, setFailInfoJob] = useState(null)
  const [activeTab, setActiveTab] = useState('new') // 'new' | 'reports' | 'customers'

  const parseJobFailure = useCallback((raw) => {
    const s = String(raw || '').trim()
    if (!s) return { raw: '', phase: null, httpStatus: null, code: null, message: null, summary: null }

    const phase = (() => {
      const idx = s.indexOf(' 실패')
      if (idx > 0) return s.slice(0, idx).trim()
      return null
    })()

    const httpStatus = (() => {
      const m = s.match(/httpStatus\s*=?\s*(\d{3})/i)
      return m ? m[1] : null
    })()

    const code = (() => {
      const m1 = s.match(/\(code\s*=\s*([^) ,]+)[^)]*\)/i)
      if (m1 && m1[1]) return String(m1[1]).trim()
      const m2 = s.match(/code\s*=\s*([A-Za-z0-9._-]+)/i)
      if (m2 && m2[1]) return String(m2[1]).trim()
      const m3 = s.match(/"code"\s*:\s*"([^"]+)"/i)
      if (m3 && m3[1]) return String(m3[1]).trim()
      const m4 = s.match(/code\s*[:=]\s*"?([^,"\s}]+)/i)
      if (m4 && m4[1]) return String(m4[1]).trim()
      return null
    })()

    const message = (() => {
      const m1 = s.match(/message\s*=\s*([^)]+)\)/i)
      if (m1 && m1[1]) return String(m1[1]).trim()
      const m2 = s.match(/message\s*=\s*([^,}]+)/i)
      if (m2 && m2[1]) return String(m2[1]).trim()
      const m3 = s.match(/"message"\s*:\s*"([^"]+)"/i)
      if (m3 && m3[1]) return String(m3[1]).trim()
      return null
    })()

    const summary = (() => {
      const idx = s.indexOf('실패:')
      if (idx < 0) return null
      const after = s.slice(idx + '실패:'.length).trim()
      const stop = after.indexOf('(code=')
      if (stop >= 0) return after.slice(0, stop).trim()
      return after || null
    })()

    return { raw: s, phase, httpStatus, code, message, summary }
  }, [])

  const copyToClipboard = useCallback(async (text) => {
    const t = String(text || '')
    if (!t) return false
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(t)
        return true
      }
    } catch (_) {
      // fallback below
    }
    try {
      const ta = document.createElement('textarea')
      ta.value = t
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      ta.style.top = '0'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      const ok = document.execCommand('copy')
      ta.remove()
      return Boolean(ok)
    } catch (_) {
      return false
    }
  }, [])

  // OPERATOR/GA관리자 공통 조회 모드: MINE | BY_ORG | ALL
  const [operatorLoadMode, setOperatorLoadMode] = useState('MINE') // MINE | BY_ORG | ALL
  const [operatorOrgId, setOperatorOrgId] = useState('')
  const [operatorOrgOptions, setOperatorOrgOptions] = useState([])
  const [operatorOrgLoading, setOperatorOrgLoading] = useState(false)
  const operatorReady = !isOperator || operatorLoadMode === 'ALL' || operatorLoadMode === 'MINE' || Boolean(operatorOrgId)

  // GA 대표/관리자: 조회 모드
  const [gaLoadMode, setGaLoadMode] = useState('MINE') // MINE | BY_ORG | ALL
  const [gaOrgId, setGaOrgId] = useState('') // ''이면 "내 조직(전체)"
  const [gaOrgOptions, setGaOrgOptions] = useState([])
  const [gaOrgLoading, setGaOrgLoading] = useState(false)
  const gaReady = !isGaScopeAdmin || gaLoadMode === 'ALL' || gaLoadMode === 'MINE' || Boolean(gaOrgId)

  // GA 조직 옵션 로드(내 조직 + 하위) - 필터링 UI에서 사용
  useEffect(() => {
    if (!isGaScopeAdmin) return
    let ignore = false
    async function loadGaOrgs() {
      setGaOrgLoading(true)
      try {
        const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
        const payload = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
        const list = Array.isArray(payload) ? payload : []
        // 안전망: GA 화면에서는 GA 조직만 노출
        const gaOnly = list.filter(o => String(o?.type || '').toUpperCase() === 'GA')
        if (!ignore) setGaOrgOptions(gaOnly)
      } catch (_) {
        if (!ignore) setGaOrgOptions([])
      } finally {
        if (!ignore) setGaOrgLoading(false)
      }
    }
    loadGaOrgs()
    return () => { ignore = true }
  }, [isGaScopeAdmin])
  const [form, setForm] = useState({
    customerId: '',
    name: '',
    phoneNumber: '',
    identity: '',
    birthDate: '',
    telecom: '0',
    authProvider: 'KAKAO',
  })
  const [dataConsents, setDataConsents] = useState({})
  const consentBundleQuery = useHealthDataConsentBundle('health-analysis')
  const consentBundle = consentBundleQuery.data
  const consentItems = consentBundle?.items ?? []

  useEffect(() => {
    if (consentItems.length) {
      setDataConsents(createInitialHealthDataConsents(consentItems))
    }
  }, [consentBundle?.variant, consentBundle?.bundleVersion, consentItems.length])

  useEffect(() => {
    if (crmPrefillFromUrlRef.current) return
    const raw = searchParams.get('customerRefId')
    if (!raw) return
    const id = Number(String(raw).trim())
    if (!Number.isFinite(id) || id <= 0) return
    crmPrefillFromUrlRef.current = true
    ;(async () => {
      try {
        const res = await api.get(`/customer-management/customers/${id}/summary`, { __silent: true })
        const data = res?.data?.data ?? res?.data
        if (!data) {
          crmPrefillFromUrlRef.current = false
          return
        }
        setForm((prev) => ({
          ...prev,
          customerId: String(id),
          name: data.customerName != null ? String(data.customerName).trim() : prev.name,
          phoneNumber: data.customerPhone != null ? formatPhoneDigitsKo(data.customerPhone) : prev.phoneNumber,
        }))
        setActiveTab('new')
        show('고객관리에서 이름·연락처를 불러왔습니다. 주민번호와 인증 수단을 입력한 뒤 진행해 주세요.', 'success')
        const next = new URLSearchParams(searchParams)
        next.delete('customerRefId')
        setSearchParams(next, { replace: true })
      } catch (_) {
        crmPrefillFromUrlRef.current = false
      }
    })()
  }, [searchParams, setSearchParams, show])
  const [crmPickerOpen, setCrmPickerOpen] = useState(false)
  const [crmPickerLoading, setCrmPickerLoading] = useState(false)
  const [crmPickerList, setCrmPickerList] = useState([])
  const [crmPickerErr, setCrmPickerErr] = useState('')

  const deriveBirthDateFromIdentity = useCallback((identityDigits) => {
    return deriveBirthDateFromIdentityDigits(identityDigits)
  }, [])

  const identityDigitsUiDisplay = useMemo(() => {
    const digits = (form.identity || '').toString().replaceAll(/[^0-9]/g, '')
    return formatIdentityDigitsForUi(digits)
  }, [form.identity])
  const [, setLastReport] = useState(null)
  const [selectedReport, setSelectedReport] = useState(null)
  const [selectedReportId, setSelectedReportId] = useState(null)
  const [selectedReportMeta, setSelectedReportMeta] = useState(null) // { customerName?, createdAt? }
  const [search, setSearch] = useState('')
  const [counseling, setCounseling] = useState(null)
  const [counselingLoading, setCounselingLoading] = useState(false)
  const [counselingGenerating, setCounselingGenerating] = useState(false)
  const [counselingModalOpen, setCounselingModalOpen] = useState(false)
  const [notes, setNotes] = useState({})
  const [resultTab, setResultTab] = useState('summary') // 'summary' | 'healthy' | 'substandard' | 'dental' | 'treatments' | 'drugs' | 'hidden'
  const [hiddenSubTab, setHiddenSubTab] = useState('진단비')
  const [treatmentSubTab, setTreatmentSubTab] = useState('1개월')
  const [treatmentSourceSubTab, setTreatmentSourceSubTab] = useState('전체')
  const [drugSubTab, setDrugSubTab] = useState('3개월')
  const [healthySubTab, setHealthySubTab] = useState('3개월')
  const [substandardSubTab, setSubstandardSubTab] = useState('3개월')
  const [dentalSubTab, setDentalSubTab] = useState('틀니')
  const [currentJob, setCurrentJob] = useState(null)
  const [twoWayModalOpen, setTwoWayModalOpen] = useState(false)
  const [twoWayJobId, setTwoWayJobId] = useState(null)
  const [twoWayInfo, setTwoWayInfo] = useState(null)
  const [twoWayInput, setTwoWayInput] = useState({
    secureNo: '',
    secureNoRefresh: '',
    smsAuthNo: '',
    simpleAuth: '1',
  })
  // 사용자가 모달을 닫았으면 같은 Job에서는 자동으로 다시 열지 않는다.
  const dismissedTwoWayModalJobIdsRef = useRef(new Set())
  // 2-way 입력 제출 직후(상태 반영 딜레이)에는 자동 오픈을 잠시 억제한다.
  // - 제출 성공 후에도 폴링/캐시로 인해 잠깐 AUTH_INPUT_REQUIRED가 남아있을 수 있어 즉시 재오픈되는 현상 방지
  // - 이후 status가 AUTH_WAITING 등으로 바뀌면 억제를 해제하며, 다시 AUTH_INPUT_REQUIRED로 돌아오면(인증번호 틀림 등) 재오픈을 허용한다.
  const suppressAutoOpenTwoWayJobIdsRef = useRef(new Set())
  const [detailEpisode, setDetailEpisode] = useState(null)
  const [detailShowHiddenGuide, setDetailShowHiddenGuide] = useState(false)
  const [collapsedDetailDays, setCollapsedDetailDays] = useState(() => new Set())
  const [retryJobId, setRetryJobId] = useState(null)
  const [retryIdentity, setRetryIdentity] = useState('')
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 5
  // 운영자 디버그: FE vs BE 집계 비교 팝업
  const [feBeCompareOpen, setFeBeCompareOpen] = useState(false)
  const [feBeCompareTab, setFeBeCompareTab] = useState('HOSP_5Y') // dataset id
  const [feBeCompareOnlyDiff, setFeBeCompareOnlyDiff] = useState(true)
  const [feBeCompareData, setFeBeCompareData] = useState(null)
  const [feBeCompareRawOpen, setFeBeCompareRawOpen] = useState(false)
  const [feBeCompareRawTitle, setFeBeCompareRawTitle] = useState('')
  const [feBeCompareRawJson, setFeBeCompareRawJson] = useState('')
  const [threeNnModalOpen, setThreeNnModalOpen] = useState(false)
  const [threeNnModalMode, setThreeNnModalMode] = useState('SIMPLE_3N5') // 'SIMPLE_3N5' | 'TEMPLATE_3NN'
  const [threeNnLoading, setThreeNnLoading] = useState(false)
  const [threeNnResult, setThreeNnResult] = useState(null)
  const [threeNnTemplateLoading, setThreeNnTemplateLoading] = useState(false)
  const [threeNnTemplateResult, setThreeNnTemplateResult] = useState(null)
  // 보험사별 예외 적용(예외질환/3NN) UI 상태
  const [selectedInsurerCodesForRequest, setSelectedInsurerCodesForRequest] = useState(() => new Set()) // Set<insurerCode>
  const [, setThreeNnTemplateExpandedTemplates] = useState(() => new Set()) // `${insurerCode}__${templateCode}`
  const [threeNnTemplateCellDetail, setThreeNnTemplateCellDetail] = useState(null) // { insurerCode, insurerName, templateCode, r, asOf? }
  const [threeNnTemplateKcdExpanded, setThreeNnTemplateKcdExpanded] = useState(() => new Set()) // `${insurerCode}__${templateCode}__${kcd}`
  // 기본값: 최근 3개월 진료내역만으로는 “입원/수술/재검사 소견” 여부가 불명확한 경우가 많아
  // 유병자 3N5(1차)에서는 3개월 조건을 기본적으로 예외 처리(무시)하도록 한다.
  const [substandardIgnoreRecent3mOpinion, setSubstandardIgnoreRecent3mOpinion] = useState(true)
  const [questionnaireModalType, setQuestionnaireModalType] = useState(null) // 'healthy' | 'substandard' | null
  const [designRequestModalOpen, setDesignRequestModalOpen] = useState(false)
  const [designCoverageType, setDesignCoverageType] = useState('substandard') // 'healthy' | 'substandard'
  const [healthyAutoSummary, setHealthyAutoSummary] = useState(null)

  // 재분석 버튼 노출 정책: 생성일로부터 7일 이내만 노출
  const canShowReanalyzeButton = useMemo(() => {
    const createdAt = selectedReport?.createdAt || selectedReportMeta?.createdAt || null
    const dt = toDateObject(createdAt)
    if (!dt) return true // createdAt이 없으면 차단하지 않음(데이터 누락 대비)
    const ageMs = Date.now() - dt.getTime()
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
    return ageMs >= 0 && ageMs <= sevenDaysMs
  }, [selectedReport?.createdAt, selectedReportMeta?.createdAt])

  const threeNnModalData = useMemo(() => {
    return threeNnModalMode === 'TEMPLATE_3NN' ? threeNnTemplateResult : threeNnResult
  }, [threeNnModalMode, threeNnTemplateResult, threeNnResult])

  const toStatusCode = (v) => (v || '').toString().trim().toUpperCase()
  const statusLabelKr = (code) => {
    const c = toStatusCode(code)
    if (c === 'OK') return '가능'
    if (c === 'WARN') return '주의'
    if (c === 'REVIEW') return '심사'
    if (c === 'FAIL') return '불가'
    return c || '-'
  }

  // 보험사 표시명(한글) 매핑 (운영에서 마스터 연동 전 임시)
  const insurerNameKrOf = (code, fallbackName) => {
    const c = (code || '').toString().trim().toUpperCase()
    const map = {
      ABL_LIFE: 'ABL생명',
      AIG: 'AIG손해보험',
      AIG_FIRE: 'AIG손해보험',
      DB: 'DB손해보험',
      DB_LIFE: 'DB생명',
      DB_FIRE: 'DB손해보험',
      HANA: '하나생명',
      HANA_LIFE: '하나생명',
      HANA_FIRE: '하나손해보험',
      HANHWALIFE: '한화생명',
      HANWHA_LIFE: '한화생명',
      HANWHA: '한화손해보험',
      HANWHA_FIRE: '한화손해보험',
      HK: '흥국화재',
      HK_FIRE: '흥국화재',
      HK_LIFE: '흥국생명',
      HYUNDAI: '푸본현대생명',
      HYUNDAI_FIRE: '현대해상',
      KB_LIFE: 'KB라이프',
      KB_NONLIFE: 'KB손해보험',
      KB_FIRE: 'KB손해보험',
      KYOBO_LIFE: '교보생명',
      LINA_LIFE: '라이나생명',
      LOTTE_NONLIFE: '롯데손해보험',
      LOTTE_FIRE: '롯데손해보험',
      MERITZ: '메리츠화재',
      MERITZ_FIRE: '메리츠화재',
      MIRAEASSET: '미래에셋생명',
      MIRAEASSET_LIFE: '미래에셋생명',
      NH_FIRE: 'NH농협손해보험',
      NH_LIFE: 'NH농협생명',
      PUBON_LIFE: '푸본현대생명',
      // 중요: 삼성생명/삼성화재는 서로 다른 회사다.
      SAMSUNG: '삼성생명',
      SAMSUNG_LIFE: '삼성생명',
      SAMSUNG_FIRE: '삼성화재',
      SHINHAN_LIFE: '신한라이프',
    }
    if (map[c]) return map[c]
    const fb = (fallbackName || '').toString().trim()
    return fb || c || '-'
  }

  // UI 중복 병합용: 같은 회사의 legacy code와 canonical code를 하나로 묶는다.
  // - 데이터(템플릿/보험사 마스터)에 동일 회사가 서로 다른 insurerCode로 존재하는 케이스가 있어,
  //   한글 표시명이 동일하게 매핑되면서 컬럼이 중복으로 보이는 문제가 발생한다.
  const canonicalInsurerCodeForUi = (code) => {
    const c = (code || '').toString().trim().toUpperCase()
    if (!c) return ''
    const map = {
      AIG: 'AIG_FIRE',
      DB: 'DB_FIRE',
      HANA: 'HANA_LIFE',
      HANHWALIFE: 'HANWHA_LIFE',
      HANWHA: 'HANWHA_FIRE',
      HK: 'HK_FIRE',
      HYUNDAI: 'PUBON_LIFE',
      KB_NONLIFE: 'KB_FIRE',
      LOTTE_NONLIFE: 'LOTTE_FIRE',
      MERITZ: 'MERITZ_FIRE',
      MIRAEASSET: 'MIRAEASSET_LIFE',
      SAMSUNG: 'SAMSUNG_LIFE',
      // 주의: 삼성생명/삼성화재는 서로 다른 회사이므로 병합하면 안 된다.
      // (SAMSUNG_LIFE=삼성생명, SAMSUNG_FIRE=삼성화재)
    }
    if (map[c]) return map[c]
    return c
  }

  // 보험사 카테고리 추정(필터용): LIFE / NONLIFE
  const insurerCategoryOf = (code) => {
    const c = canonicalInsurerCodeForUi(code)
    if (!c) return 'ALL'
    const LIFE = new Set([
      'ABL_LIFE',
      'DB_LIFE',
      'HANA_LIFE',
      'HANWHA_LIFE',
      'HK_LIFE',
      'KB_LIFE',
      'KYOBO_LIFE',
      'LINA_LIFE',
      'MIRAEASSET_LIFE',
      'NH_LIFE',
      'PUBON_LIFE',
      'SAMSUNG_LIFE',
      'SHINHAN_LIFE',
    ])
    const NONLIFE = new Set([
      'AIG_FIRE',
      'DB_FIRE',
      'HANA_FIRE',
      'HANWHA_FIRE',
      'HK_FIRE',
      'HYUNDAI_FIRE',
      'KB_FIRE',
      'LOTTE_FIRE',
      'MERITZ_FIRE',
      'NH_FIRE',
      'SAMSUNG_FIRE',
    ])
    if (LIFE.has(c)) return 'LIFE'
    if (NONLIFE.has(c)) return 'NONLIFE'
    if (c.endsWith('_LIFE') || c.includes('LIFE')) return 'LIFE'
    return 'NONLIFE'
  }

  // NOTE: 아래 computeHealthy* 함수들이 이 변수들을 참조하는데,
  // 파일 구조상 선언 위치가 뒤쪽에 있어 TDZ(선언 전 참조) 에러가 날 수 있다.
  // 따라서 "먼저 선언"하고, parsedSummary 이후에 값만 할당한다.
  let byPeriodFromSummary = null
  let byPeriodGroupedDrugsFromSummary = null
  let healthyViewFromSummary = null
  let rawEpisodesFromSummary = []

  const detailListScrollRef = useRef(null)
  const [detailEpisodeMode, setDetailEpisodeMode] = useState('treatments') // 'treatments' | 'drugs'
  const [detailEpisodeContext, setDetailEpisodeContext] = useState('ALL') // 'ALL' | 'EXAM' | 'INHOSP_SURGERY' | 'HIDDEN_SURGERY' | 'TREATMENTS' | 'DRUGS'
  const [examIncludeImaging, setExamIncludeImaging] = useState(false)
  const [inhospIncludeNonSurgery, setInhospIncludeNonSurgery] = useState(false)
  const [hiddenIncludeAllProcedures, setHiddenIncludeAllProcedures] = useState(false)
  const [drugsShowAllInfo, setDrugsShowAllInfo] = useState(false)

  // 상세 팝업이 열려있는 동안 배경(분석목록) 스크롤을 잠근다.
  // 그렇지 않으면 휠/트랙패드 스크롤이 body로 전달되어 뒤 컨텐츠가 움직인다.
  useEffect(() => {
    if (!detailEpisode) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [detailEpisode])

  useEffect(() => {
    if (resultTab === 'hidden') setResultTab('summary')
  }, [resultTab])

  const closeDetailEpisode = useCallback(() => {
    setDetailEpisode(null)
    setDetailShowHiddenGuide(false)
    setCollapsedDetailDays(new Set())
    setDetailEpisodeMode('treatments')
    setDetailEpisodeContext('ALL')
    setExamIncludeImaging(false)
    setInhospIncludeNonSurgery(false)
    setHiddenIncludeAllProcedures(false)
    setDrugsShowAllInfo(false)
  }, [])

  const openDetailEpisode = useCallback((episode, opts = {}) => {
    setDetailShowHiddenGuide(Boolean(opts.showHiddenGuide))
    // opts.mode가 명시되지 않으면, details에 투약 라인이 있는지로 모드를 자동 결정한다.
    const autoMode = (() => {
      if (!episode) return 'treatments'
      const details = episode?.details
      if (!Array.isArray(details) || details.length === 0) return 'treatments'
      return details.some((x) => x?.source === 'PRESCRIBE_DRUG' || x?.drugName) ? 'drugs' : 'treatments'
    })()
    setDetailEpisodeMode(opts.mode === 'drugs' ? 'drugs' : opts.mode === 'treatments' ? 'treatments' : autoMode)
    setDetailEpisode(episode)
    setCollapsedDetailDays(new Set())
    setDetailEpisodeContext(
      opts.context === 'EXAM' ||
        opts.context === 'INHOSP_SURGERY' ||
        opts.context === 'HIDDEN_SURGERY' ||
        opts.context === 'TREATMENTS' ||
        opts.context === 'DRUGS'
        ? opts.context
        : 'ALL',
    )
    // 상세 목록이 길면 이전 스크롤 위치가 남아 UX가 나빠서, 열 때마다 상단으로 이동
    requestAnimationFrame(() => {
      if (detailListScrollRef.current) detailListScrollRef.current.scrollTop = 0
    })
  }, [])

  const handleEvaluate3nn = useCallback(
    async (reportId, opts = {}) => {
      const { openModal = false, ignoreRecent3mOpinion = true } = opts || {}
      if (!reportId) {
        show('리포트가 선택되지 않았습니다.', 'error')
        return
      }

      setThreeNnLoading(true)
      const parsedSummaryLocal = (() => {
        const raw = selectedReport?.summaryJson
        if (!raw) return null
        try {
          return JSON.parse(raw)
        } catch (e) {
          return null
        }
      })()

      const profile = parsedSummaryLocal?.profile || null
      const byPeriodGroupedDiagnosis = profile?.byPeriodGroupedDiagnosis || null
      const subView = profile?.substandardView || null
      if (!subView) {
        show('분석 결과(유병자 데이터)가 없습니다. 재분석 후 다시 시도해 주세요.', 'error')
        setThreeNnLoading(false)
        return
      }

      const baseAt = parsedSummaryLocal?.createdAt || selectedReport?.createdAt || null
      const baseDate = baseAt ? new Date(baseAt) : new Date()

      const normalizeDateDigitsSafe = (v) => {
        if (!v) return null
        const s = String(v).replaceAll('-', '').trim()
        if (s.length !== 8) return null
        return s
      }
      const dateDigitsToDate = (yyyymmdd) => {
        const s = normalizeDateDigitsSafe(yyyymmdd)
        if (!s) return null
        const y = Number(s.slice(0, 4))
        const m = Number(s.slice(4, 6)) - 1
        const d = Number(s.slice(6, 8))
        const dt = new Date(y, m, d)
        return Number.isFinite(dt.getTime()) ? dt : null
      }
      const daysAgo = (digits) => {
        const dt = dateDigitsToDate(digits)
        if (!dt) return null
        const diff = Math.floor((baseDate.getTime() - dt.getTime()) / (24 * 60 * 60 * 1000))
        return Number.isFinite(diff) ? diff : null
      }

      const hasAnyGroup = (list) => Array.isArray(list) && list.length > 0
      const anyNonDrugWithinDays = (groups, limitDays) => {
        if (!Array.isArray(groups) || groups.length === 0) return false
        for (const g of groups) {
          const details = Array.isArray(g?.details) ? g.details : null
          if (details && details.length > 0) {
            for (const x of details) {
              const src = String(x?.source || '')
              if (src === 'PRESCRIBE_DRUG') continue
              const d = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
              const a = d ? daysAgo(d) : null
              if (a !== null && a >= 0 && a <= limitDays) return true
            }
          }
          const gd = normalizeDateDigitsSafe(g?.startDate || g?.endDate)
          const ga = gd ? daysAgo(gd) : null
          if (ga !== null && ga >= 0 && ga <= limitDays) return true
        }
        return false
      }
      const buildNonDrugExamplesWithinDays = (groups, limitDays, maxItems = 8) => {
        if (!Array.isArray(groups) || groups.length === 0) return []

        const normalizeOrgName = (x) => {
          const raw =
            x?.hospitalName ||
            x?.orgName ||
            x?.orgNm ||
            x?.medicalOrgName ||
            x?.medicalOrgNm ||
            x?.treatOrgName ||
            x?.treatOrgNm ||
            null
          const s = (raw || '').toString().trim()
          return s || null
        }
        const formatYmd = (digits) => {
          const s = normalizeDateDigitsSafe(digits)
          if (!s) return null
          return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
        }

        const out = []
        const push = (x, g) => {
          const src = String(x?.source || g?.source || '')
          if (src === 'PRESCRIBE_DRUG') return
          const d = normalizeDateDigitsSafe(x?.startDate || x?.endDate || g?.startDate || g?.endDate)
          const a = d ? daysAgo(d) : null
          if (a === null || a < 0 || a > limitDays) return

          const code = (x?.diagnosisCode || g?.diagnosisCode || '').toString().trim() || null
          const name = (x?.diagnosisName || g?.diagnosisName || '').toString().trim() || null
          const org = normalizeOrgName(x) || normalizeOrgName(g) || null

          out.push({
            day: d,
            code,
            name,
            org,
          })
        }

        for (const g of groups) {
          const details = Array.isArray(g?.details) ? g.details : null
          if (details && details.length > 0) {
            for (const x of details) push(x, g)
          } else {
            push(g, g)
          }
        }

        // 최신순, (일자/기관/진단코드) 단위로 중복 제거
        out.sort((a, b) => String(b.day || '').localeCompare(String(a.day || '')))
        const seen = new Set()
        const rendered = []
        for (const row of out) {
          if (rendered.length >= maxItems) break
          const key = `${row.day || ''}__${row.org || ''}__${row.code || ''}`
          if (seen.has(key)) continue
          seen.add(key)

          const ymd = formatYmd(row.day) || (row.day || '-')
          const org = row.org || '-'
          // 설계의뢰/요약에서는 진단코드 표기를 생략하고 질병명만 표기한다.
          const diag = row.name || '-'
          rendered.push(`${ymd} / ${org} / ${diag}`)
        }

        return rendered
      }

      const anyInpatientOrSurgeryWithinDays = (groups, limitDays) => {
        if (!Array.isArray(groups) || groups.length === 0) return false

        // NOTE: substandardView의 hosp* 그룹은 "입원/수술(또는 유사)" 후보들을 모아둔 값이지만,
        // 데이터 소스/버전에 따라 단순 진료 그룹이 섞일 수 있다.
        // 따라서 실제로 입원/수술 플래그(입원 true / 수술명 존재)를 가진 항목만 대상으로 기간 체크를 한다.
        const isInpatientOrSurgeryLike = (x) => {
          if (!x) return false
          if (x?.inpatient) return true
          const surgeryName = (x?.surgeryName || '').toString().trim()
          if (surgeryName) return true
          if (Array.isArray(x?.surgeryNames) && x.surgeryNames.length > 0) return true
          return false
        }
        const within = (v) => {
          const d = normalizeDateDigitsSafe(v?.startDate || v?.endDate)
          const a = d ? daysAgo(d) : null
          return a !== null && a >= 0 && a <= limitDays
        }

        for (const g of groups) {
          const details = Array.isArray(g?.details) ? g.details : null
          if (details && details.length > 0) {
            for (const x of details) {
              if (!isInpatientOrSurgeryLike(x)) continue
              if (within(x)) return true
            }
          }
          if (!isInpatientOrSurgeryLike(g)) continue
          if (within(g)) return true
        }
        return false
      }

      // 1.5) 5년 내 14일 이상 투약 (고지 요약용)
      // - 화면의 투약 집계(일자별 최대 투약일수 합산)와 동일 컨셉으로 계산
      const longDrug14Info = (() => {
        const byPeriod = profile?.byPeriod || null
        const rawByPeriod = byPeriod && byPeriod['5Y']
        const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : (Array.isArray(profile?.allEpisodesRaw) ? profile.allEpisodesRaw : [])
        if (!Array.isArray(rawList) || rawList.length === 0) return { has: false, top: [] }

        const normalizeDrugKeyLocal = (line) => {
          const n = (line?.drugName || '').toString().trim().toLowerCase()
          const days = line?.totalDrugDays ?? ''
          return `${n}|${days}`
        }
        const isPharmacyLikeLocal = (line) => {
          const t = (line?.treatTypeDetail || line?.treatType || '').toString()
          const org = (line?.hospitalName || '').toString()
          if (t.includes('조제') || t.includes('처방')) return true
          if (org.includes('약국')) return true
          return false
        }
        const daysBetweenLocal = (aDigits, bDigits) => {
          const a = dateDigitsToDate(aDigits)
          const b = dateDigitsToDate(bDigits)
          if (!a || !b) return null
          const diffMs = Math.abs(a.getTime() - b.getTime())
          return Math.floor(diffMs / (24 * 60 * 60 * 1000))
        }
        const dedupeAndLinkLocal = (drugLines) => {
          if (!Array.isArray(drugLines) || drugLines.length === 0) return []
          const perDayKey = new Map()
          for (const line of drugLines) {
            const day = normalizeDateDigitsSafe(line?.startDate || line?.endDate)
            const key = normalizeDrugKeyLocal(line)
            if (!day || !key || key.startsWith('|')) continue
            const mapKey = `${day}__${key}`
            const prev = perDayKey.get(mapKey)
            if (!prev) {
              perDayKey.set(mapKey, line)
              continue
            }
            const prevIsPharm = isPharmacyLikeLocal(prev)
            const curIsPharm = isPharmacyLikeLocal(line)
            if (!prevIsPharm && curIsPharm) {
              perDayKey.set(mapKey, line)
            }
          }
          const compact = Array.from(perDayKey.values())
          const pharmacies = compact.filter((x) => isPharmacyLikeLocal(x))
          const hospitals = compact.filter((x) => !isPharmacyLikeLocal(x))
          const usedHospital = new Set()
          const enriched = []
          for (const p of pharmacies) {
            const pDay = normalizeDateDigitsSafe(p?.startDate || p?.endDate)
            const key = normalizeDrugKeyLocal(p)
            let best = null
            let bestDelta = null
            for (let i = 0; i < hospitals.length; i++) {
              if (usedHospital.has(i)) continue
              const h = hospitals[i]
              const hDay = normalizeDateDigitsSafe(h?.startDate || h?.endDate)
              if (!pDay || !hDay) continue
              if (normalizeDrugKeyLocal(h) !== key) continue
              const delta = daysBetweenLocal(pDay, hDay)
              if (delta === null) continue
              if (delta > 5) continue
              if (bestDelta === null || delta < bestDelta) {
                bestDelta = delta
                best = { idx: i, h, hDay }
              }
            }
            if (best) {
              usedHospital.add(best.idx)
              enriched.push({
                ...p,
                prescriberOrgName: best.h?.hospitalName || null,
                prescriberDate: best.hDay || null,
              })
            } else {
              enriched.push(p)
            }
          }
          for (let i = 0; i < hospitals.length; i++) {
            if (usedHospital.has(i)) continue
            enriched.push(hospitals[i])
          }
          return enriched
        }

        const dateToCodes = new Map()
        const codeToName = new Map()
        for (const e of rawList) {
          if (!e || e.source !== 'BASIC_TREAT') continue
          const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
          const code = (e.diagnosisCode || '').toString().trim()
          if (!day || !code) continue
          if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
          dateToCodes.get(day).add(code)
          if (!codeToName.has(code) && e.diagnosisName) codeToName.set(code, String(e.diagnosisName).trim())
        }
        const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
        const deduped = dedupeAndLinkLocal(drugRaw)
        const diagGroups = new Map()
        for (const line of deduped) {
          const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
          const explicitCode = (line.diagnosisCode || '').toString().trim()
          let groupCode = explicitCode || null
          if (!groupCode && day && dateToCodes.has(day)) {
            const set = dateToCodes.get(day)
            if (set && set.size === 1) groupCode = Array.from(set)[0]
          }
          if (!groupCode) continue
          if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
          diagGroups.get(groupCode).push(line)
        }
        const computeTotalByDayMax = (lines, valueKey) => {
          if (!Array.isArray(lines) || lines.length === 0) return 0
          const maxByDay = new Map()
          for (const x of lines) {
            const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
            const v = x?.[valueKey]
            const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
            if (!Number.isFinite(n)) continue
            const prev = maxByDay.get(day)
            maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
          }
          let sum = 0
          for (const v of maxByDay.values()) sum += v
          return sum
        }
        const totals = []
        for (const [code, lines] of diagGroups.entries()) {
          const total = computeTotalByDayMax(lines, 'totalDrugDays')
          if (total >= 14) totals.push({ code, total })
        }
        totals.sort((a, b) => b.total - a.total)
        return {
          has: totals.length > 0,
          top: totals.slice(0, 3).map((x) => `${codeToName.get(x.code) || x.code}(투약${x.total})`),
        }
      })()

      // 1) 5년 내 6대 중대질병(3N5의 '5'에 해당): 있으면 즉시 불가
      const major6 = subView?.MAJOR6_5Y
      if (hasAnyGroup(major6)) {
        setThreeNnResult({
          kind: 'SIMPLE_3N5',
          asOf: baseDate.toISOString().slice(0, 10),
          status: 'DECLINED',
          possibleCombos: [],
          reasons: [
            '최근 5년 내 6대 중대질병(암/뇌졸중/심근경색/협심증/심장판막증/간경화) 내역이 확인되었습니다.',
            '3N5(간편심사) 기준에서 해당 조건은 일반적으로 가입 제한(거절) 요인으로 처리됩니다.',
          ],
          message:
            '최근 5년 내 6대 중대질병(암/뇌졸중/심근경색/협심증/심장판막증/간경화) 내역이 있어 3N5 기준의 유병자 상품 가입이 불가능합니다.',
          footnote:
            '입원/수술 이력은 보험사별 예외 질환에 따라 플랜 인수 기준이 상이할 수 있으니 설계매니저에게 문의바랍니다.',
        })
        if (openModal) setThreeNnModalOpen(true)
        setThreeNnLoading(false)
        return
      }

      // 2) 3개월: 유병자 3개월은 "진료 내역(투약 제외)"을 기준으로 단순 판단
      const recent3mGroups = Array.isArray(subView?.recentHighRisk3M) ? subView.recentHighRisk3M : null
      // (구버전 summary에는 substandardView.recentHighRisk3M가 없을 수 있어, byPeriodGroupedDiagnosis['3M']로 fallback)
      const byPeriod3m = byPeriodGroupedDiagnosis?.['3M']
      const recent3mSource = recent3mGroups || (Array.isArray(byPeriod3m) ? byPeriod3m : [])
      const hasRecent3mNonDrug = anyNonDrugWithinDays(recent3mSource, 90)
      const recent3mNonDrugExamples = hasRecent3mNonDrug ? buildNonDrugExamplesWithinDays(recent3mSource, 90, 8) : []
      if (hasRecent3mNonDrug && !ignoreRecent3mOpinion) {
        setThreeNnResult({
          kind: 'SIMPLE_3N5',
          asOf: baseDate.toISOString().slice(0, 10),
          status: 'DECLINED',
          possibleCombos: [],
          reasons: [
            '최근 3개월 내 진료내역이 확인되었습니다. (투약 내역은 제외)',
            '3N5(간편심사)에서 “최근 3개월” 조건은 가입 가능 플랜 산출의 선행 조건으로 사용됩니다.',
          ],
          message: '최근 3개월 내 진료내역이 있어 유병자 상품 가입이 불가능합니다. (투약 제외 기준)',
          footnote:
            '보험사별 예외 질환에 따라 가입 가능성이 있으니 설계매니저에게 문의해주세요.',
        })
        if (openModal) setThreeNnModalOpen(true)
        setThreeNnLoading(false)
        return
      }

      // 3) N년 내 입원/수술 이력 기반으로 315/325/... 산출
      // - 규칙: (3개월/1년) 부적합이면 불가
      // - 그 외에는 "통과 가능한 가장 큰 N"의 3N5를 안내한다.
      const hosp5 = subView?.inpatientOrSurgeryLike5Y
      const hosp4 = subView?.inpatientOrSurgeryLike4Y
      const hosp3 = subView?.inpatientOrSurgeryLike3Y
      const hosp2 = subView?.inpatientOrSurgeryLike2Y
      const hosp1 = subView?.inpatientOrSurgeryLike1Y

      const has1Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp1) ? hosp1 : Array.isArray(hosp2) ? hosp2 : [], 365)
      if (has1Y) {
        setThreeNnResult({
          kind: 'SIMPLE_3N5',
          asOf: baseDate.toISOString().slice(0, 10),
          status: 'POSSIBLE',
          possibleCombos: ['3-0-5'],
          reasons: [
            '최근 1년 내 입원/수술(또는 유사 내역) 이력이 확인되었습니다.',
            '이 흐름(설계의뢰 > 유병자 3N5)에서는 최근 1년 내 입원/수술 이력이 있으면 “3-0-5” 조합을 우선 안내합니다.',
          ],
          message: '최근 1년 내 입원/수술 내역이 있어 “3-0-5” 조합을 우선 추천합니다.',
          footnote:
            '보험사별 예외 질환에 따라 가입 가능성이 있으니 설계매니저에게 문의해주세요.',
        })
        if (openModal) setThreeNnModalOpen(true)
        setThreeNnLoading(false)
        return
      }

      const within2Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp2) ? hosp2 : [], 365 * 2)
      const within3Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp3) ? hosp3 : Array.isArray(hosp5) ? hosp5 : [], 365 * 3)
      const within4Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp4) ? hosp4 : Array.isArray(hosp5) ? hosp5 : [], 365 * 4)
      const within5Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp5) ? hosp5 : [], 365 * 5)

      const possibleCombos = (() => {
        // “가능한 조합 목록만” 노출 (단순 1차 판정)
        if (within2Y) return ['3-1-5']
        if (within3Y) return ['3-2-5']
        if (within4Y) return ['3-3-5']
        if (within5Y) return ['3-4-5']
        // 5년 내 입원/수술 이력이 없으면 3N5 외 확장 조합도 가능할 수 있음(보험사별 상이)
        return ['3-5-5', '3-7-5', '3-10-5', '3-10-10']
      })()

      const reasons = (() => {
        const out = []
        // '최근 5년 내 6대 중대질병 내역이 확인되지 않았습니다.',

        // "없음"은 굳이 이유로 표시하지 않고, 존재하는 경우만 이유로 표기한다.
        if (hasRecent3mNonDrug) {
          out.push(
            ignoreRecent3mOpinion
              ? '최근 3개월 내 진료내역이 확인되었습니다. (투약 제외) - 인수기준 예외 처리로 3N5 “3개월(소견)” 조건은 판정에서 제외했습니다.'
              : '최근 3개월 내 진료내역이 확인되었습니다. (투약 제외 기준)',
          )
        }
        // longDrug14Info 관련 문구는 현재 사용하지 않음(주석 처리 유지)
        if (within2Y) out.push('최근 2년 내 입원/수술 이력이 있어, 보수적으로 “3-1-5” 조합을 우선 안내합니다.')
        else if (within3Y) out.push('최근 3년 내 입원/수술 이력이 있어, “3-2-5” 조합을 우선 안내합니다.')
        else if (within4Y) out.push('최근 4년 내 입원/수술 이력이 있어, “3-3-5” 조합을 우선 안내합니다.')
        else if (within5Y) out.push('최근 5년 내 입원/수술 이력이 있어, “3-4-5” 조합을 우선 안내합니다.')
        // else: 입원/수술 이력이 없으면 이유에 표기하지 않는다.
        return out
      })()

      setThreeNnResult({
        kind: 'SIMPLE_3N5',
        asOf: baseDate.toISOString().slice(0, 10),
        status: 'POSSIBLE',
        possibleCombos,
        reasons,
        exception:
          hasRecent3mNonDrug && ignoreRecent3mOpinion
            ? { ignoreRecent3mOpinion: true, recent3mNonDrugExamples }
            : null,
        message: '유병자 상품 가입 가능 플랜입니다.',
        footnote:
          '보험사별 예외 질환에 따라 가입 가능성이 있으니 설계매니저에게 문의해주세요.',
      })
      if (openModal) setThreeNnModalOpen(true)
      setThreeNnLoading(false)
    },
    [show, selectedReport],
  )

  const handleEvaluate3nnTemplates = useCallback(
    async (reportId, opts = {}) => {
      const { openModal = true, asOf = null, insurerCodes = null } = opts || {}
      if (!reportId) {
        show('리포트를 먼저 선택해 주세요.', 'error')
        return
      }
      setThreeNnTemplateLoading(true)
      try {
        const payload = {
          reportId,
          asOf: asOf || null,
          insurerCodes: Array.isArray(insurerCodes) && insurerCodes.length > 0 ? insurerCodes : null,
        }
        const res = await api.post('/underwriting/3nn/evaluate', payload)
        const data = res.data?.data || res.data
        // data: { reportId, asOf, items:[{insurerCode, insurerName, results:[...], best:{...}}] }
        setThreeNnTemplateResult({ kind: 'TEMPLATE_3NN', ...(data || {}) })
        if (openModal) {
          setThreeNnModalMode('TEMPLATE_3NN')
          setThreeNnModalOpen(true)
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || null
        show(`보험사별 예외 적용 결과를 불러오지 못했습니다.${msg ? ` (${msg})` : ''}`, 'error')
      } finally {
        setThreeNnTemplateLoading(false)
      }
    },
    [show],
  )

  const computeHealthy7305YSummaries = useCallback(() => {
    // 건강체 질문서(5년 내 같은 원인 7일 이상 치료 또는 30일 이상 투약)용 요약을 만든다.
    // - 기존 건강체 탭의 로직과 동일한 기준(일자별 최대 합산) 사용
    const key = '5Y'
    const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[key]
    const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodesFromSummary

    const dateToCodes = new Map()
    const codeToName = new Map()
    for (const e of rawList) {
      if (!e || e.source !== 'BASIC_TREAT') continue
      const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
      const code = (e.diagnosisCode || '').toString().trim()
      if (!day || !code) continue
      if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
      dateToCodes.get(day).add(code)
      if (!codeToName.has(code) && e.diagnosisName) codeToName.set(code, e.diagnosisName)
    }

    // 치료(외래/입원)일수: BASIC_TREAT 기반으로 동일 진단코드 그룹핑(약국 제외)
    const treatGroups = new Map()
    for (const e of rawList) {
      if (!e || e.source !== 'BASIC_TREAT') continue
      if (isPharmacyLike(e)) continue
      const code = (e.diagnosisCode || '').toString().trim()
      if (!code) continue
      if (!treatGroups.has(code)) treatGroups.set(code, [])
      treatGroups.get(code).push(e)
    }

    const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
    const deduped = dedupeAndLinkDrugsPharmacyFirst(drugRaw)

    const diagGroups = new Map()
    for (const line of deduped) {
      const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
      const explicitCode = (line.diagnosisCode || '').toString().trim()
      let groupCode = explicitCode || null
      if (!groupCode && day && dateToCodes.has(day)) {
        const set = dateToCodes.get(day)
        if (set && set.size === 1) groupCode = Array.from(set)[0]
      }
      if (!groupCode) continue
      if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
      diagGroups.get(groupCode).push(line)
    }

    const computeTotalByDayMax = (lines, valueKey) => {
      if (!Array.isArray(lines) || lines.length === 0) return 0
      const maxByDay = new Map()
      let hasAny = false
      for (const x of lines) {
        const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
        const v = x?.[valueKey]
        const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
        if (!Number.isFinite(n)) continue
        const prev = maxByDay.get(day)
        maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
        hasAny = true
      }
      if (!hasAny) return 0
      let sum = 0
      for (const v of maxByDay.values()) sum += Math.max(0, v)
      return sum
    }

    const minMaxDateFromLines = (lines) => {
      let min = null
      let max = null
      for (const x of lines || []) {
        const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
        if (!day) continue
        if (!min || day < min) min = day
        if (!max || day > max) max = day
      }
      return { min, max }
    }

    const summaries = []
    const allCodes = new Set([...Array.from(diagGroups.keys()), ...Array.from(treatGroups.keys())])
    for (const code of allCodes) {
      const drugLines = diagGroups.get(code) || []
      const treatLines = treatGroups.get(code) || []
      const totalDrugDays = computeTotalByDayMax(drugLines, 'totalDrugDays') || 0
      const totalTreatmentDays = computeTotalByDayMax(treatLines, 'totalTreatmentDays') || 0
      if (totalDrugDays < 30 && totalTreatmentDays < 7) continue

      const mergedDetails = [...treatLines, ...drugLines]
      const { min, max } = minMaxDateFromLines(mergedDetails)
      summaries.push({
        id: `DRUG_7_30_5Y-${code}`,
        groupType: 'DRUGS_DIAG',
        diagnosisCode: code,
        diagnosisName: codeToName.get(code) || '진단',
        startDate: min,
        endDate: max,
        count: mergedDetails.length,
        totalDrugDays,
        totalTreatmentDays,
        details: mergedDetails,
      })
    }
    summaries.sort((a, b) => String(a.diagnosisCode || '').localeCompare(String(b.diagnosisCode || '')))
    return summaries
  }, [byPeriodFromSummary, rawEpisodesFromSummary])

  const computeHealthyQuestionnaireSummary = useCallback(() => {
    const hv = healthyViewFromSummary || {}
    const g3m = Array.isArray(hv?.['3M']) ? hv['3M'] : []
    const g1yCheck = Array.isArray(hv?.['1Y_CHECK']) ? hv['1Y_CHECK'] : []
    const gHosp5y = Array.isArray(hv?.['HOSP_5Y']) ? hv['HOSP_5Y'] : []
    const gMajor11 = Array.isArray(hv?.['MAJOR11_5Y']) ? hv['MAJOR11_5Y'] : []
    const g730 = computeHealthy7305YSummaries()

    const extractRange = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return null
      let min = null
      let max = null
      for (const g of arr) {
        const s = normalizeDateDigitsSafe(g?.startDate || g?.endDate)
        const e = normalizeDateDigitsSafe(g?.endDate || g?.startDate)
        if (s) {
          if (!min || s < min) min = s
          if (!max || s > max) max = s
        }
        if (e) {
          if (!min || e < min) min = e
          if (!max || e > max) max = e
        }
      }
      if (!min && !max) return null
      const a = min ? formatDate(min) : '-'
      const b = max ? formatDate(max) : '-'
      return `${a}~${b}`
    }

    const toExample = (g) => {
      const name = (g?.diagnosisName || '').toString().trim()
      const codeDisp = formatKcdDottedForMemo(g?.diagnosisCode)
      const codeName = name || '-'
      const codePart = codeDisp ? ` [${codeDisp}]` : ''
      const treat = g?.totalTreatmentDays ?? ''
      const drug = g?.totalDrugDays ?? ''
      const extra =
        treat || drug
          ? ` (진료${treat || 0} / 투약${drug || 0})`
          : ''
      return `${codeName}${codePart}${extra}`
    }

    const pick = (arr, limit = 3) => (Array.isArray(arr) ? arr.slice(0, limit).map(toExample) : [])

    return {
      recent3m: { has: g3m.length > 0, examples: pick(g3m, 3), range: extractRange(g3m) },
      check1y: { has: g1yCheck.length > 0, examples: pick(g1yCheck, 3), range: extractRange(g1yCheck) },
      treat7orDrug30_5y: { has: g730.length > 0, examples: pick(g730, 3), range: extractRange(g730) },
      hosp5y: { has: gHosp5y.length > 0, examples: pick(gHosp5y, 3), range: extractRange(gHosp5y) },
      major11_5y: { has: gMajor11.length > 0, examples: pick(gMajor11, 3), range: extractRange(gMajor11) },
    }
  }, [healthyViewFromSummary, computeHealthy7305YSummaries])

  const openDesignRequestModal = useCallback(
    async (defaultType = 'substandard') => {
      const t = defaultType === 'healthy' ? 'healthy' : 'substandard'
      setDesignCoverageType(t)
      setSubstandardIgnoreRecent3mOpinion(true)
      setDesignRequestModalOpen(true)
      // 유병자 선택 시: 팝업에서 바로 요약을 보여주기 위해 3N5(1차) 결과를 미리 계산한다.
      if (t === 'substandard' && selectedReportId) {
        try {
          await handleEvaluate3nn(selectedReportId, { openModal: false, ignoreRecent3mOpinion: true })
        } catch (_) {
          // ignore
        }
      }
      // 건강체 선택 시: 질문서 항목별 “있음/없음 + 예시” 요약을 만들어 둔다.
      if (t === 'healthy') {
        try {
          setHealthyAutoSummary(computeHealthyQuestionnaireSummary())
        } catch (_) {
          setHealthyAutoSummary(null)
        }
      }
    },
    [handleEvaluate3nn, selectedReportId, computeHealthyQuestionnaireSummary],
  )

  const openQuestionnaire = useCallback((type) => {
    setQuestionnaireModalType(type)
  }, [])

  const JOB_LIST_PAGE_SIZE = 10
  const [reportsPage, setReportsPage] = useState(0)
  const [customersPage, setCustomersPage] = useState(0)

  // 탭 전환/검색어 변경 시 1페이지로 되돌림
  useEffect(() => {
    setReportsPage(0)
    setCustomersPage(0)
  }, [activeTab, search])

  // 분석 목록(서버 페이징 + 고객명 검색(q))
  const listScopeKey = useMemo(() => {
    if (isOperator) return { operatorLoadMode, operatorOrgId }
    if (isGaScopeAdmin) return { gaLoadMode, gaOrgId }
    return {}
  }, [isOperator, operatorLoadMode, operatorOrgId, isGaScopeAdmin, gaLoadMode, gaOrgId])
  const listReady = isOperator ? operatorReady : (isGaScopeAdmin ? gaReady : true)

  const jobsQuery = useQuery({
    queryKey: ['health-jobs', { page: reportsPage, size: JOB_LIST_PAGE_SIZE, q: search || '', ...listScopeKey }],
    enabled: activeTab === 'reports' && listReady,
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', String(reportsPage))
      params.set('size', String(JOB_LIST_PAGE_SIZE))
      if (search && String(search).trim()) params.set('q', String(search).trim())
      if (isOperator) {
        if (operatorLoadMode === 'ALL') params.set('viewAll', 'true')
        if (operatorLoadMode === 'BY_ORG' && operatorOrgId) params.set('orgId', String(operatorOrgId))
      } else if (isGaScopeAdmin) {
        if (gaLoadMode === 'ALL') {
          params.set('viewAll', 'true')
        } else if (gaLoadMode === 'BY_ORG') {
          if (gaOrgId) params.set('orgId', String(gaOrgId))
        }
      }
      const res = await api.get(`/health-jobs?${params.toString()}`)
      return res.data?.data || res.data
    },
  })

  const billingMeQuery = useQuery({
    queryKey: ['billing-me'],
    queryFn: async () => {
      const res = await api.get('/billing/me', { __silent: true })
      return res.data?.data ?? res.data
    },
    staleTime: 60_000,
    enabled: activeTab === 'new',
  })

  const healthBillingGate = useMemo(
    () => resolveFeatureBillingGate(billingMeQuery.data, FEATURE_BILLING_CODES.HEALTH_ANALYSIS),
    [billingMeQuery.data],
  )
  const healthBillingReady = activeTab === 'new'
    && !(billingMeQuery.isLoading && !billingMeQuery.data)
    && !(billingMeQuery.isError && !billingMeQuery.data)
  const healthBillingBlocked = healthBillingReady && !healthBillingGate.canUse

  useEffect(() => {
    if (activeTab !== 'new') return
    if (billingMeQuery.isLoading && !billingMeQuery.data) return
    if (billingMeQuery.isError && !billingMeQuery.data) return
    if (healthBillingGate.canUse) return
    show(featureBillingBlockMessage(healthBillingGate), 'error')
  }, [activeTab, billingMeQuery.data, billingMeQuery.isError, billingMeQuery.isLoading, healthBillingGate, show])

  const jobs = useMemo(() => jobsQuery.data?.items ?? EMPTY_LIST, [jobsQuery.data])
  const jobsHasNext = Boolean(jobsQuery.data?.hasNext)
  const filteredJobs = jobs // 서버에서 q로 필터링

  // 고객 목록(서버에서 dedup + 페이징)
  const customersQuery = useQuery({
    queryKey: ['health-job-customers', { page: customersPage, size: JOB_LIST_PAGE_SIZE, q: search || '', ...listScopeKey }],
    enabled: activeTab === 'customers' && listReady,
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', String(customersPage))
      params.set('size', String(JOB_LIST_PAGE_SIZE))
      if (search && String(search).trim()) params.set('q', String(search).trim())
      if (isOperator) {
        if (operatorLoadMode === 'ALL') params.set('viewAll', 'true')
        if (operatorLoadMode === 'BY_ORG' && operatorOrgId) params.set('orgId', String(operatorOrgId))
      } else if (isGaScopeAdmin) {
        if (gaLoadMode === 'ALL') {
          params.set('viewAll', 'true')
        } else if (gaLoadMode === 'BY_ORG') {
          if (gaOrgId) params.set('orgId', String(gaOrgId))
        }
      }
      const res = await api.get(`/health-jobs/customers?${params.toString()}`)
      return res.data?.data || res.data
    },
  })

  const customers = useMemo(() => customersQuery.data?.items ?? EMPTY_LIST, [customersQuery.data])
  const customersHasNext = Boolean(customersQuery.data?.hasNext)
  const filteredCustomers = customers

  // OPERATOR 조직 옵션 로드(전체 조직) - 필터링 UI에서 사용
  useEffect(() => {
    if (!isOperator) return
    let ignore = false
    async function loadOpOrgs() {
      setOperatorOrgLoading(true)
      try {
        const res = await api.get('/orgs/options', { params: { scope: 'all' } })
        const payload = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
        if (!ignore) setOperatorOrgOptions(Array.isArray(payload) ? payload : [])
      } catch (_) {
        if (!ignore) setOperatorOrgOptions([])
      } finally {
        if (!ignore) setOperatorOrgLoading(false)
      }
    }
    loadOpOrgs()
    return () => { ignore = true }
  }, [isOperator])

  useEffect(() => {
    if (!isOperator) {
      setReportTemplateOptions([])
      setReportTemplateId('')
      setReportHtmlTemplateOptions([])
      setReportHtmlTemplateId('')
      return undefined
    }
    let ignore = false
    async function loadReportTemplates() {
      try {
        const res = await api.get('/ops/report-templates', {
          params: { reportCode: HEALTH_ANALYSIS_REPORT_CODE },
          __silent: true,
        })
        const payload = res.data?.data !== undefined ? res.data.data : res.data
        const rows = Array.isArray(payload) ? payload : []
        const options = rows.filter((item) => (
          item?.templateStatus === 'PUBLISHED'
          && ['PNG_OVERLAY', 'HYBRID'].includes(item?.renderMode)
        ))
        const htmlOptions = rows.filter((item) => (
          item?.templateStatus === 'PUBLISHED'
          && ['HTML', 'HYBRID'].includes(item?.renderMode)
        ))
        if (!ignore) {
          setReportTemplateOptions(options)
          setReportTemplateId((prev) => {
            if (prev && options.some((item) => String(item.id) === String(prev))) return prev
            const preferred = options.find((item) => item.defaultTemplate) || options[0]
            return preferred?.id ? String(preferred.id) : ''
          })
          setReportHtmlTemplateOptions(htmlOptions)
          setReportHtmlTemplateId((prev) => {
            if (prev && htmlOptions.some((item) => String(item.id) === String(prev))) return prev
            const preferred = htmlOptions.find((item) => item.defaultTemplate) || htmlOptions[0]
            return preferred?.id ? String(preferred.id) : ''
          })
        }
      } catch {
        if (!ignore) {
          setReportTemplateOptions([])
          setReportTemplateId('')
          setReportHtmlTemplateOptions([])
          setReportHtmlTemplateId('')
        }
      }
    }
    loadReportTemplates()
    return () => { ignore = true }
  }, [isOperator])

  const jobStatusLabel = (status) => {
    switch (status) {
      case 'AUTH_REQUESTED':
        return '인증요청'
      case 'AUTH_WAITING':
        return '인증 대기'
      case 'AUTH_INPUT_REQUIRED':
        return '추가입력 필요'
      case 'AUTH_COMPLETED':
        return '인증 완료'
      case 'ANALYSIS_RUNNING':
        return '분석 중'
      case 'ANALYSIS_COMPLETED':
        return '분석 완료'
      case 'FAILED':
        return '실패'
      default:
        return status || '-'
    }
  }

  const isManualRawUploadJob = (job) => Boolean(job?.manualUpload || job?.jobType === 'MANUAL_RAW_UPLOAD')

  const deriveManualRawCustomerName = (file) => {
    const name = String(file?.name || '').trim()
    if (!name) return ''
    const withoutExt = name.replace(/\.json$/i, '')
    const marker = withoutExt.indexOf('_진료내역원본_')
    const inferred = marker > 0 ? withoutExt.slice(0, marker) : withoutExt
    return inferred.trim()
  }

  const handleManualRawUploadClick = () => {
    if (manualRawUploading) return
    manualRawUploadInputRef.current?.click()
  }

  const handleManualRawUploadChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!/\.json$/i.test(file.name || '')) {
      show('JSON 파일만 업로드할 수 있습니다.', 'error')
      return
    }
    const ok = window.confirm(`${file.name} 파일로 수동 분석 이력을 추가할까요?`)
    if (!ok) return

    try {
      setManualRawUploading(true)
      const formData = new FormData()
      formData.append('file', file)
      const inferredName = deriveManualRawCustomerName(file)
      if (inferredName) formData.append('customerName', inferredName)
      await api.post('/health-jobs/manual-raw-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setOperatorLoadMode('MINE')
      setOperatorOrgId('')
      setActiveTab('reports')
      await queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
      await queryClient.invalidateQueries({ queryKey: ['health-job-customers'] })
      show('JSON 업로드 분석 이력을 추가했습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, 'JSON 업로드에 실패했습니다.'), 'error')
    } finally {
      setManualRawUploading(false)
    }
  }

  const parseTwoWayInfo = useCallback((job) => {
    const raw = job?.twoWayInfoJson
    if (!raw) return null
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw
    } catch (_) {
      return null
    }
  }, [])

  const openTwoWayInputModal = useCallback((job) => {
    const info = parseTwoWayInfo(job)
    setTwoWayJobId(job.jobId)
    setTwoWayInfo(info)
    setTwoWayInput((prev) => ({ ...prev, secureNo: '', smsAuthNo: '' }))
    setTwoWayModalOpen(true)
  }, [parseTwoWayInfo])

  const submitTwoWayInput = async () => {
    if (!twoWayJobId) return
    try {
      await api.post(`/health-jobs/${twoWayJobId}/two-way-input`, {
        secureNo: twoWayInput.secureNo || '',
        smsAuthNo: twoWayInput.smsAuthNo || '',
        simpleAuth: twoWayInput.simpleAuth || '1',
      }, { __silent: true, __suppressGlobal409Toast: true })
      show('추가 인증 입력을 제출했습니다. 처리 재개 중...', 'success')
      suppressAutoOpenTwoWayJobIdsRef.current.add(String(twoWayJobId))
      setTwoWayModalOpen(false)
      setActiveTab('reports')
      queryClient.invalidateQueries({ queryKey: ['health-job', twoWayJobId] })
      queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
    } catch (e) {
      show(getApiErrorMessage(e, '추가 인증 입력 제출에 실패했습니다.'), 'error')
    }
  }

  const dismissTwoWayModal = useCallback(() => {
    if (twoWayJobId != null) {
      dismissedTwoWayModalJobIdsRef.current.add(String(twoWayJobId))
    }
    setTwoWayModalOpen(false)
  }, [twoWayJobId])

  // (deprecated) 과거: TTL 만료 시 주민번호 재입력 버튼 노출용
  // - 이제는 FAILED 옆 "재시도" 버튼에서 409(IDENTITY_TTL_EXPIRED) 응답 시 재입력 팝업을 자동으로 띄운다.

  const createReportMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post('/health-jobs', payload)
      const data = res.data?.data || res.data
      return data
    },
    onSuccess: (data) => {
      setCurrentJob({ jobId: data.jobId, status: data.status })
      show(`${form.name} 고객님의 인증 요청이 완료되었습니다. 인증이 완료되기 전까지 진료정보 분석은 시작되지 않습니다.`, 'success')
      // ✅ SMS 인증은 추가 인증 입력(보안문자/SMS 인증번호)까지 이어질 수 있어
      // UX상 "입력 폼 잠금" → "추가 인증 입력" → "분석 시작 시 초기화" 흐름을 따른다.
      if (form.authProvider !== 'SMS') {
        // 인증 요청이 성공했으면 입력 폼은 초기화(다음 고객 입력 대비)
        setForm((prev) => ({
          ...prev,
          customerId: '',
          name: '',
          phoneNumber: '',
          identity: '',
          birthDate: '',
        }))
        setDataConsents(createInitialHealthDataConsents(consentItems))
      }
      // 분석 목록 갱신 및 탭 전환
      queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['billing-me'] })
      // SMS 인증은 인증번호 입력(추가인증)이 뒤따를 수 있어, 운영자 테스트 UX상 "새 분석" 탭을 유지한다.
      // (PASS/KAKAO는 기존대로 목록 탭으로 이동)
      if (form.authProvider === 'SMS') {
        setActiveTab('new')
        // ✅ SMS는 상태 변경 딜레이가 있어, Job 상태와 무관하게 즉시 "추가 인증 입력" 팝업을 띄워 안내한다.
        // - twoWayInfo는 이후 폴링(GET /health-jobs/{id})로 채워진다.
        try {
          dismissedTwoWayModalJobIdsRef.current.delete(String(data.jobId))
        } catch (_) {
          // ignore
        }
        setTwoWayJobId(data.jobId)
        setTwoWayInfo(null)
        setTwoWayModalOpen(true)
        // 즉시 1회 Job 정보를 당겨와(twoWayInfoJson 포함) 팝업에 표시한다.
        ;(async () => {
          try {
            const res = await api.get(`/health-jobs/${data.jobId}`)
            const jd = res.data?.data || res.data
            const info = parseTwoWayInfo(jd)
            setTwoWayInfo(info)
          } catch (_) {
            // ignore (폴링이 뒤에서 갱신함)
          }
        })()
      } else {
        setActiveTab('reports')
      }
    },
    onError: (e) => {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error?.message ||
        e?.message ||
        null
      show(`인증 요청에 실패했습니다. 입력 정보를 확인해 주세요.${msg ? ` (${msg})` : ''}`, 'error')
      // SMS 요청은 UX상 요청 즉시 팝업을 띄우므로, 실패 시 닫아준다.
      if (form.authProvider === 'SMS') {
        setTwoWayModalOpen(false)
      }
    },
  })

  // 고객 직접 인증 요청 (identity 없이)
  const createCustomerAuthRequestMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post('/health-jobs', payload)
      const data = res.data?.data || res.data
      return data
    },
    onSuccess: (data) => {
      setCurrentJob({ jobId: data.jobId, status: data.status })
      show(`${form.name} 고객님께 인증 요청 알림톡이 발송되었습니다. 고객이 직접 인증을 완료해야 분석이 진행됩니다.`, 'success')
      // 입력 폼 초기화
      setForm((prev) => ({
        ...prev,
        customerId: '',
        name: '',
        phoneNumber: '',
        identity: '',
        birthDate: '',
      }))
      // 분석 목록 갱신 및 탭 전환
      queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['billing-me'] })
      setActiveTab('reports')
    },
    onError: (e) => {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error?.message ||
        e?.message ||
        null
      show(`고객 인증 요청 발송에 실패했습니다. 입력 정보를 확인해 주세요.${msg ? ` (${msg})` : ''}`, 'error')
    },
  })

  const jobStatusQuery = useQuery({
    queryKey: ['health-job', currentJob?.jobId],
    queryFn: async () => {
      if (!currentJob?.jobId) return null
      const res = await api.get(`/health-jobs/${currentJob.jobId}`)
      const data = res.data?.data || res.data
      return data
    },
    enabled: !!currentJob?.jobId,
    refetchInterval: currentJob?.jobId ? 5000 : false,
  })

  const jobStatus = jobStatusQuery.data?.status || currentJob?.status || null
  const authInProgress =
    !!currentJob?.jobId &&
    jobStatus &&
    !['ANALYSIS_COMPLETED', 'FAILED'].includes(String(jobStatus).toUpperCase())
  const currentJobFeedback = (() => {
    if (!currentJob?.jobId) return null
    const status = String(jobStatus || '').toUpperCase()
    switch (status) {
      case 'AUTH_REQUESTED':
      case 'AUTH_WAITING':
        return {
          tone: 'blue',
          title: '고객 인증 대기 중',
          description: '고객이 인증을 완료하기 전까지 진료정보 분석은 시작되지 않습니다. 인증 상태는 자동으로 갱신됩니다.',
        }
      case 'AUTH_INPUT_REQUIRED':
        return {
          tone: 'amber',
          title: '추가 인증 입력 필요',
          description: '보안문자 또는 SMS 인증번호 입력이 필요한 상태입니다. 입력을 완료하면 분석 절차가 이어집니다.',
        }
      case 'AUTH_COMPLETED':
      case 'ANALYSIS_RUNNING':
        return {
          tone: 'blue',
          title: '분석 진행 중',
          description: '인증이 완료되어 진료정보 분석을 진행 중입니다. 완료되면 분석 이력에서 결과를 확인할 수 있습니다.',
        }
      case 'ANALYSIS_COMPLETED':
        return {
          tone: 'emerald',
          title: '분석 완료',
          description: '분석 결과가 생성되었습니다. 고객 상담·설계 참고용으로 확인하고, 다운로드 파일은 민감정보로 관리해 주세요.',
        }
      case 'FAILED':
        return {
          tone: 'red',
          title: '분석 실패',
          description: '인증 정보, 고객 휴대폰 번호 또는 추가 인증 입력 상태를 확인한 뒤 다시 요청해 주세요.',
        }
      default:
        return {
          tone: 'slate',
          title: '처리 상태 확인 중',
          description: '현재 인증·분석 상태를 확인하고 있습니다. 잠시 후에도 바뀌지 않으면 새로고침해 주세요.',
        }
    }
  })()
  const currentJobFeedbackClass = {
    blue: 'border-blue-100 bg-blue-50 text-blue-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-900',
    red: 'border-red-100 bg-red-50 text-red-900',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  }[currentJobFeedback?.tone || 'slate']

  // 단건 Job이 AUTH_INPUT_REQUIRED로 내려오면 입력 UI를 노출한다.
  // - 보안문자(reqSecureNo)가 필요한 경우에만 모달을 자동으로 띄운다.
  // - SMS 인증번호만 필요한 경우는 "신규 분석" 입력폼의 SMS 인증번호 칸에서 바로 입력/제출하도록 한다.
  useEffect(() => {
    const job = jobStatusQuery.data
    if (!job) return
    const s = String(job.status || '').toUpperCase()
    const key = job.jobId != null ? String(job.jobId) : null

    // 2-way 제출 직후 "즉시 재오픈" 방지용 억제는,
    // AUTH_INPUT_REQUIRED가 아닌 상태(예: AUTH_WAITING)로 한번이라도 넘어가면 해제한다.
    if (key && s !== 'AUTH_INPUT_REQUIRED' && suppressAutoOpenTwoWayJobIdsRef.current.has(key)) {
      suppressAutoOpenTwoWayJobIdsRef.current.delete(key)
    }

    // 보안문자(reqSecureNo) 요구는 자동인식 실패 케이스로 보고 재인증 유도
    if (s === 'FAILED') {
      const rawErr = (job.lastError || job.last_error || '').toString()
      if (rawErr.includes('SECURE_NO_REQUIRED')) {
        setTwoWayModalOpen(false)
        // 현재 입력값은 유지한 채로, 진행 중 Job만 해제해 다시 "인증 요청하기"를 누를 수 있게 한다.
        setCurrentJob(null)
        show('보안문자 자동인식에 실패했습니다. 다시 인증 요청을 진행해 주세요.', 'error')
        return
      }
    }

    // SMS UX: 분석이 시작되면(=2차 인증 통과) 입력 폼을 초기화한다.
    if (form.authProvider === 'SMS' && (s === 'ANALYSIS_RUNNING' || s === 'ANALYSIS_COMPLETED')) {
      setForm((prev) => ({
        ...prev,
        customerId: '',
        name: '',
        phoneNumber: '',
        identity: '',
        birthDate: '',
      }))
      // 분석이 시작되면 추가 인증 팝업은 닫고 목록으로 이동
      if (twoWayModalOpen) setTwoWayModalOpen(false)
      setActiveTab('reports')
    }

    if (job.status === 'AUTH_INPUT_REQUIRED') {
      // twoWay 컨텍스트는 항상 세팅(인라인 입력/모달 공용)
      const info = parseTwoWayInfo(job)
      setTwoWayJobId(job.jobId)
      setTwoWayInfo(info)

      // ✅ 추가 인증 입력은 팝업으로 단일화한다.
      // - SMS의 경우도 "보안문자 → SMS" 단계형일 수 있어 팝업에서 가이드를 제공한다.
      if (twoWayModalOpen) return
      const key = String(job.jobId)
      // 제출 직후, 상태 반영 딜레이로 인해 잠깐 AUTH_INPUT_REQUIRED가 남아있을 수 있어 재오픈을 억제한다.
      if (suppressAutoOpenTwoWayJobIdsRef.current.has(key)) return
      if (dismissedTwoWayModalJobIdsRef.current.has(key)) return
      openTwoWayInputModal(job)
    }
  }, [form.authProvider, jobStatusQuery.data, openTwoWayInputModal, parseTwoWayInfo, twoWayModalOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    // NOTE: 고객명은 한글 IME(조합 입력) 호환을 위해 onChange에서 강제 정규화/필터링을 하지 않는다.
    // (조합 중 value를 치환하면 입력이 "안 되는" 현상이 발생할 수 있음)
    if (name === 'phoneNumber') {
      setForm((prev) => ({ ...prev, customerId: '', phoneNumber: formatPhoneDigitsKo(value) }))
      return
    }
    // 생년월일은 주민번호로 자동 추출하며, 화면에서는 읽기 전용으로만 보여준다.
    if (name === 'birthDate') return
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleIdentityChange = useCallback(
    (e) => {
      if (authInProgress || createReportMutation.isLoading) return
      const digits = normalizeIdentityDigits(e.target.value)
      setForm((prev) => ({
        ...prev,
        identity: digits,
        birthDate: deriveBirthDateFromIdentity(digits),
      }))
    },
    [authInProgress, createReportMutation.isLoading, deriveBirthDateFromIdentity],
  )

  const handleRetryIdentityChange = useCallback((e) => {
    setRetryIdentity(normalizeIdentityDigits(e.target.value))
  }, [])

  const loadCrmCustomersForPicker = useCallback(async () => {
    setCrmPickerLoading(true)
    setCrmPickerErr('')
    try {
      const res = await api.get('/customer-management/customers', { params: { page: 0, size: 80 } })
      const body = res?.data
      const data = body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body
      setCrmPickerList(Array.isArray(data?.items) ? data.items : [])
    } catch (e) {
      setCrmPickerErr(e?.response?.data?.message || e?.message || '고객관리 목록을 불러오지 못했습니다.')
      setCrmPickerList([])
    } finally {
      setCrmPickerLoading(false)
    }
  }, [])

  const applyCrmCustomerToForm = useCallback(
    (row) => {
      if (!row || row.id == null) return
      setForm((prev) => ({
        ...prev,
        customerId: String(row.id),
        name:
          row.customerName != null && String(row.customerName).trim()
            ? String(row.customerName).trim()
            : prev.name,
        phoneNumber:
          row.customerPhone != null && String(row.customerPhone).trim()
            ? formatPhoneDigitsKo(row.customerPhone)
            : prev.phoneNumber,
      }))
      setCrmPickerOpen(false)
      show('고객관리의 이름·연락처를 반영했습니다. 주민번호와 인증 수단을 입력한 뒤 진행해 주세요.', 'success')
    },
    [show],
  )

  const handleCreateReport = (e) => {
    e.preventDefault()
    if (createReportMutation.isLoading) return

    // --- 길이/형식 검증 ---
    const rawName = (form.name || '').toString()
    const nameTrim = rawName.trim()
    const identityDigits = (form.identity || '').replaceAll(/[^0-9]/g, '')
    const phoneDigits = (form.phoneNumber || '').replaceAll(/[^0-9]/g, '')
    const birthDigits = deriveBirthDateFromIdentity(identityDigits)

    // 고객명은 한글/영어만 허용. (특수문자/숫자/기타 언어는 허용하지 않음)
    // NOTE: onChange에서 강제 필터링하지 않고, 제출 시점에만 검증하여 IME 조합 입력 이슈를 피한다.
    if (!nameTrim) {
      show('고객명을 입력해 주세요.', 'error')
      return
    }
    const nameOk = /^[A-Za-z가-힣\s]+$/.test(nameTrim)
    if (!nameOk) {
      show('고객명에는 한글/영어만 사용 가능합니다.', 'error')
      return
    }
    if (identityDigits.length !== MAX_IDENTITY_DIGITS) {
      show('주민등록번호는 숫자 13자리로 입력해 주세요.', 'error')
      return
    }
    if (birthDigits.length !== 8) {
      show('주민등록번호 앞 6자리(YYMMDD)에서 생년월일을 추출할 수 없습니다. 입력값을 확인해 주세요.', 'error')
      return
    }
    if (!(phoneDigits.length >= 10 && phoneDigits.length <= MAX_PHONE_DIGITS)) {
      show(`연락처는 숫자 10~${MAX_PHONE_DIGITS}자리로 입력해 주세요.`, 'error')
      return
    }
    if (!allHealthDataConsentsChecked(consentItems, dataConsents)) {
      show('필수 동의 항목을 모두 확인해 주세요.', 'error')
      return
    }
    if (!healthBillingGate.canUse) {
      show(featureBillingBlockMessage(healthBillingGate), 'error')
      return
    }

    const payload = {
      customerId: form.customerId ? Number(form.customerId) : null,
      name: nameTrim,
      phoneNumber: phoneDigits,
      identity: identityDigits,
      birthDate: birthDigits,
      telecom: form.telecom,
      authProvider: form.authProvider,
      ...buildHealthDataConsentPayload(consentBundle, dataConsents),
    }

    // 카카오/패스는 "인증 요청" 후 곧바로 목록에서 상태 변화를 보는 UX가 더 자연스럽다.
    if (form.authProvider !== 'SMS') {
      setActiveTab('reports')
    }

    // SMS는 상태 변경 딜레이가 있어, 요청 즉시 팝업을 띄워 안내한다.
    if (form.authProvider === 'SMS') {
      setTwoWayInput((prev) => ({ ...prev, secureNo: '', smsAuthNo: '' }))
      setTwoWayInfo(null)
      setTwoWayJobId(null)
      setTwoWayModalOpen(true)
    }
    createReportMutation.mutate(payload)
  }

  // 고객 직접 인증 요청 핸들러 (identity 없이)
  const handleCreateCustomerAuthRequest = (e) => {
    e.preventDefault()
    if (createCustomerAuthRequestMutation.isLoading) return

    // --- 길이/형식 검증 (고객명, 통신사, 연락처만) ---
    const rawName = (form.name || '').toString()
    const nameTrim = rawName.trim()
    const phoneDigits = (form.phoneNumber || '').replaceAll(/[^0-9]/g, '')

    // 고객명은 한글/영어만 허용
    if (!nameTrim) {
      show('고객명을 입력해 주세요.', 'error')
      return
    }
    const nameOk = /^[A-Za-z가-힣\s]+$/.test(nameTrim)
    if (!nameOk) {
      show('고객명에는 한글/영어만 사용 가능합니다.', 'error')
      return
    }
    if (!(phoneDigits.length >= 10 && phoneDigits.length <= MAX_PHONE_DIGITS)) {
      show(`연락처는 숫자 10~${MAX_PHONE_DIGITS}자리로 입력해 주세요.`, 'error')
      return
    }

    // identity 없이 payload 생성 (고객명, 전화번호만 필수)
    if (!allHealthDataConsentsChecked(consentItems, dataConsents)) {
      show('필수 동의 항목을 모두 확인해 주세요.', 'error')
      return
    }
    if (!healthBillingGate.canUse) {
      show(featureBillingBlockMessage(healthBillingGate), 'error')
      return
    }

    const payload = {
      customerId: form.customerId ? Number(form.customerId) : null,
      name: nameTrim,
      phoneNumber: phoneDigits,
      ...buildHealthDataConsentPayload(consentBundle, dataConsents),
    }

    createCustomerAuthRequestMutation.mutate(payload)
  }

  const handleViewResult = async (reportId, meta = null) => {
    try {
      setSelectedReportMeta(meta)
      const res = await api.get(`/health-reports/${reportId}`)
      const data = res.data?.data || res.data
      setSelectedReport(data)
      setSelectedReportId(reportId)
      setLastReport({ reportId: data.id, riskLevel: data.riskLevel })
      // 생년월일은 HealthReport에 저장되지 않고(보관 정책), HealthJob(birth_date)에 존재한다.
      // 결과 조회 시 jobId가 있으면 해당 Job에서 birthDate를 보강한다.
      const jobId = meta?.jobId
      if (jobId) {
        try {
          const jr = await api.get(`/health-jobs/${jobId}`)
          const jd = jr.data?.data || jr.data
          const bd = jd?.birthDate || jd?.birth_date || null
          if (bd) {
            setSelectedReportMeta((prev) => ({ ...(prev || {}), birthDate: bd }))
          }
        } catch (_) {
          // ignore
        }
      }
      show('분석 결과를 불러왔습니다.', 'success')
    } catch (e) {
      show('분석 결과를 불러오지 못했습니다.', 'error')
    }
  }

  const handleOpenHealthCardNews = (reportId, meta = {}) => {
    const report = selectedReportId === reportId ? selectedReport : null
    const opened = openCustomerCardNewsWindow({
      sourceType: 'health',
      customer: {
        customerName: meta.customerName || selectedReportMeta?.customerName || report?.customerName,
      },
      latestHealth: {
        ...(report || {}),
        id: reportId,
        createdAt: report?.createdAt || meta.createdAt,
        createdAtDisplay: report?.createdAtDisplay || meta.createdAtDisplay || meta.createdAt,
        riskLabel: report?.riskLabel || report?.riskLevel,
        riskScore: report?.riskScore,
      },
    })
    if (!opened) {
      show('팝업 차단을 해제한 뒤 다시 시도해주세요.', 'error')
    }
  }

  // AI 카운셀링 조회
  const loadCounseling = async (reportId) => {
    if (!reportId) return
    setCounselingLoading(true)
    try {
      const res = await api.get(`/health-reports/${reportId}/counseling`)
      const data = res.data?.data || res.data
      setCounseling(data)
    } catch (e) {
      if (e?.response?.status === 404) {
        setCounseling(null) // 아직 생성되지 않음
      } else {
        show('AI 상담 가이드를 불러오지 못했습니다.', 'error')
      }
    } finally {
      setCounselingLoading(false)
    }
  }

  // AI 카운셀링 생성
  const generateCounseling = async (reportId, ageGroup, gender, regenerate = false) => {
    if (!reportId) {
      show('리포트를 먼저 선택해 주세요.', 'error')
      return
    }
    setCounselingGenerating(true)
    try {
      const payload = {}
      if (ageGroup) payload.ageGroup = ageGroup
      if (gender) payload.gender = gender
      if (regenerate) payload.regenerate = true

      const res = await api.post(`/health-reports/${reportId}/counseling`, payload)
      const data = await unwrapAsyncResult(res)
      setCounseling(data)
      show('AI 상담 가이드가 생성되었습니다.', 'success')
    } catch (e) {
      show('AI 상담 가이드 생성에 실패했습니다: ' + (e?.response?.data?.message || e.message), 'error')
    } finally {
      setCounselingGenerating(false)
    }
  }

  // AI 카운셀링 삭제
  const deleteCounseling = async (reportId) => {
    if (!reportId) return
    try {
      await api.delete(`/health-reports/${reportId}/counseling`)
      setCounseling(null)
      show('AI 상담 가이드가 삭제되었습니다.', 'success')
    } catch (e) {
      show('AI 상담 가이드 삭제에 실패했습니다.', 'error')
    }
  }

  const handleGoToRequestNewFrom3nn = useCallback((opts = {}) => {
    const coverageType = opts?.coverageType === 'healthy' ? 'healthy' : 'substandard'
    const selectedInsurerCodes = Array.isArray(opts?.selectedInsurerCodes)
      ? opts.selectedInsurerCodes.map((x) => (x != null ? String(x) : '')).filter((x) => x.trim() !== '')
      : []
    if (!selectedReportId) {
      show('리포트를 먼저 선택해 주세요.', 'error')
      return
    }
    const customerName = ((selectedReportMeta?.customerName || '').toString().trim()) || '고객'
    const title = `${customerName} 고객님 설계부탁드립니다.`
    const parsedSummary = (() => {
      const raw = selectedReport?.summaryJson
      if (!raw) return null
      try {
        return JSON.parse(raw)
      } catch {
        return null
      }
    })()
    const profile = parsedSummary?.profile || null
    const birth = (() => {
      const v = selectedReportMeta?.birthDate || parsedSummary?.profile?.birthDate || parsedSummary?.profile?.birth || null
      const s = v ? String(v).trim() : ''
      if (!s) return '-'
      // yyyyMMdd
      if (/^\d{8}$/.test(s)) return formatDate(s)
      return formatDate(s) || s
    })()

    const takeDxLabels = (groups, limit = 3) => {
      if (!Array.isArray(groups) || groups.length === 0) return []
      const out = []
      for (const g of groups) {
        const name = (g?.diagnosisName || '').toString().trim()
        const codeDisp = formatKcdDottedForMemo(g?.diagnosisCode)
        const label = codeDisp ? `${name} [${codeDisp}]` : name
        if (!label) continue
        const treatDays = g?.totalTreatmentDays ?? g?.sumTreatmentDays ?? ''
        const drugDays = g?.totalDrugDays ?? g?.sumDrugDays ?? ''
        const daysPart =
          (treatDays || drugDays) ? `(${treatDays ? `진료${treatDays}` : ''}${treatDays && drugDays ? '/' : ''}${drugDays ? `투약${drugDays}` : ''})` : ''
        out.push(`${label}${daysPart}`)
        if (out.length >= limit) break
      }
      return out
    }

    const aggregateInhospByDiagnosisCode = (groups) => {
      if (!Array.isArray(groups) || groups.length === 0) return []
      const map = new Map()
      for (const g of groups) {
        const code = (g?.diagnosisCode || '').toString().trim()
        if (!code) continue
        const start = (g?.startDate || '').toString().trim()
        const end = (g?.endDate || '').toString().trim()
        const daysRaw = g?.totalTreatmentDays ?? g?.sumTreatmentDays ?? 0
        const days = Number(daysRaw)
        const key = `${code}|${start}|${end}|${Number.isFinite(days) ? days : String(daysRaw)}`

        if (!map.has(code)) {
          map.set(code, {
            diagnosisCode: code,
            diagnosisName: (g?.diagnosisName || '').toString().trim(),
            totalInpatientDays: 0,
            dateRanges: new Set(),
            dedupeKeys: new Set(),
          })
        }
        const cur = map.get(code)
        if (cur.dedupeKeys.has(key)) continue
        cur.dedupeKeys.add(key)
        if (!cur.diagnosisName && g?.diagnosisName) cur.diagnosisName = String(g.diagnosisName).trim()
        if (Number.isFinite(days)) cur.totalInpatientDays += Math.max(0, days)
        const s = start ? formatDate(start) : ''
        const e = end ? formatDate(end) : ''
        if (s || e) cur.dateRanges.add(`${s || '-'}~${e || '-'}`)
      }
      const out = Array.from(map.values()).map((x) => ({
        diagnosisCode: x.diagnosisCode,
        diagnosisName: x.diagnosisName,
        totalInpatientDays: x.totalInpatientDays,
        dateRanges: Array.from(x.dateRanges),
      }))
      out.sort((a, b) => (b.totalInpatientDays || 0) - (a.totalInpatientDays || 0))
      return out
    }

    const byPeriod = profile?.byPeriodGroupedDiagnosis || {}
    const subView = profile?.substandardView || {}

    // 1) 3개월 내 모든 진료/투약 포함
    const g3m = Array.isArray(byPeriod?.['3M']) ? byPeriod['3M'] : []
    const has3m = g3m.length > 0
    const labels3m = takeDxLabels(g3m, 3)

    // 4) 6대질병
    const major6 = Array.isArray(subView?.MAJOR6_5Y) ? subView.MAJOR6_5Y : []
    const major6Labels = takeDxLabels(major6, 5)

    const lines = []
    lines.push('[설계 유형]')
    lines.push(`- 선택: ${coverageType === 'healthy' ? '표준체(건강체)' : '유병자(간편보험)'}`)
    lines.push('')
    lines.push(`고객명 : ${customerName}(${birth})`)
    // lines.push(`생년월일 : ${birth}`)
    // 분석일은 상담 메모 본문에서는 현재 생략한다.

    const byPeriod5y = Array.isArray(byPeriod?.['5Y']) ? byPeriod['5Y'] : []
    const inhosp5yGroups = byPeriod5y.filter((x) => !!x?.inpatient || (x?.surgeryName && String(x.surgeryName).trim()))
    const inhospAgg5y = aggregateInhospByDiagnosisCode(inhosp5yGroups)

    if (coverageType === 'substandard') {
      lines.push('')
      lines.push('[유병자 질문서 요약]')
      lines.push(
        `- 최근 3개월 내 진료/투약: ${has3m ? `있음${labels3m.length ? ` (${labels3m.join(', ')})` : ''}` : '없음'}`,
      )
      lines.push(`- 최근 5년 내 입원/수술: ${inhospAgg5y.length ? '있음' : '없음'}`)
      if (inhospAgg5y.length) {
        inhospAgg5y.slice(0, 5).forEach((g, idx) => {
          const label = (g?.diagnosisName || '-').toString().trim() || '-'
          const codeDisp = formatKcdDottedForMemo(g?.diagnosisCode)
          const days = g?.totalInpatientDays ?? '-'
          const ranges = Array.isArray(g?.dateRanges) && g.dateRanges.length ? g.dateRanges.join(', ') : '-'
          const line = codeDisp
            ? `    ${idx + 1}) ${label} [${codeDisp}] / 총 입원일:${days} / 일자:${ranges}`
            : `    ${idx + 1}) ${label} / 총 입원일:${days} / 일자:${ranges}`
          lines.push(line)
        })
        lines.push(MEMO_FOOTNOTE_INHOSP_DATE_RANGE)
      }
      lines.push(
        `- 최근 5년 내 6대질병: ${major6.length ? `있음${major6Labels.length ? ` (${major6Labels.join(', ')})` : ''}` : '없음'}`,
      )
      lines.push('')

      // 3N5 결과 (가능 플랜/이유)
      if (threeNnResult?.kind === 'SIMPLE_3N5') {
        const templateCodeToCombo = (templateCode) => {
          const tc = (templateCode || '').toString().trim()
          if (!tc) return null
          // 대표 템플릿(335/345/355/375)
          if (/^\d{3}$/.test(tc)) return `${tc[0]}-${tc[1]}-${tc[2]}`
          // 확장(예: 31010 -> 3-10-10)
          if (/^\d{5}$/.test(tc) && tc.startsWith('3')) return `${tc[0]}-${tc.slice(1, 3)}-${tc.slice(3, 5)}`
          // 그 외는 그대로 노출(형식 미확정)
          return tc
        }
        const possibleCombosFromSelectedInsurers = (() => {
          if (!Array.isArray(selectedInsurerCodes) || selectedInsurerCodes.length === 0) return []
          const items = Array.isArray(threeNnTemplateResult?.items) ? threeNnTemplateResult.items : []
          const byCode = new Map(items.map((it) => [String(it?.insurerCode || ''), it]))
          const out = new Set()
          for (const code of selectedInsurerCodes.slice(0, 200)) {
            const it = byCode.get(String(code)) || null
            const best = it?.best || null
            const combo = templateCodeToCombo(best?.templateCode)
            if (combo) out.add(combo)
          }
          return Array.from(out)
        })()

        const memoPossibleCombos = (() => {
          if (possibleCombosFromSelectedInsurers.length > 0) return possibleCombosFromSelectedInsurers
          if (Array.isArray(threeNnResult?.possibleCombos)) return threeNnResult.possibleCombos
          return null
        })()

        if (memoPossibleCombos) {
          lines.push(`- 가능 플랜: ${memoPossibleCombos.length ? memoPossibleCombos.join(', ') : '없음'}`)
        }
        const reasons = Array.isArray(threeNnResult?.reasons) ? threeNnResult.reasons.slice(0, 3) : []
        // 3N5(단순) 결과가 "최근 3개월" 때문에 DECLINED 이어도,
        // 보험사별 예외질환/3NN 평가 결과로 가능 플랜이 있으면 메모는 그 기준으로 표기한다.
        if (possibleCombosFromSelectedInsurers.length > 0) {
          if (reasons.length >= 2 && String(reasons[1]).includes('선행 조건')) {
            reasons[1] = '보험사별 예외질환/3NN 평가 결과에서는 가입 가능한 조합이 확인되었습니다.'
          } else if (reasons.length >= 2) {
            reasons[1] = '보험사별 예외질환/3NN 평가 결과에서는 가입 가능한 조합이 확인되었습니다.'
          } else if (reasons.length === 1) {
            reasons.push('보험사별 예외질환/3NN 평가 결과에서는 가입 가능한 조합이 확인되었습니다.')
          } else {
            reasons.push('보험사별 예외질환/3NN 평가 결과에서는 가입 가능한 조합이 확인되었습니다.')
          }
        }

        // "최근 N년 내 입원/수술" 근거 문장 추가 (3N5 중 N 결정 이유 설명)
        // - N은 가능 플랜(예: 3-3-5)의 가운데 숫자를 우선 사용
        // - 파싱 실패 시에는 2Y~5Y 중 가장 가까운 입원/수술 감지 구간을 역추정
        const nFromCombo = (() => {
          const combos = memoPossibleCombos || []
          const first = combos.length ? String(combos[0]).trim() : ''
          const m = first.match(/^\s*\d+\s*-\s*(\d+)\s*-\s*\d+\s*$/)
          if (!m) return null
          const n = Number(m[1])
          return Number.isFinite(n) && n >= 1 && n <= 5 ? n : null
        })()
        const nFromDetectedWindow = (() => {
          const windows = [1, 2, 3, 4, 5]
          for (const n of windows) {
            const key = `${n}Y`
            const g = Array.isArray(byPeriod?.[key]) ? byPeriod[key] : []
            const hit = g.filter((x) => !!x?.inpatient || (x?.surgeryName && String(x.surgeryName).trim()))
            if (hit.length > 0) return n
          }
          return 5
        })()
        const nYears = nFromCombo || nFromDetectedWindow
        const byPeriodNy = Array.isArray(byPeriod?.[`${nYears}Y`]) ? byPeriod[`${nYears}Y`] : []
        const inhospNy = byPeriodNy.filter((x) => !!x?.inpatient || (x?.surgeryName && String(x.surgeryName).trim()))
        const nReason =
          inhospNy.length > 0
            ? `최근 ${nYears}년 내 입원/수술 내역이 확인되었습니다. (3N5 중 N을 결정한 근거)`
            : null
        if (nReason) {
          if (reasons.length < 3) reasons.push(nReason)
          else reasons[2] = nReason
        }

        if (reasons.length > 0) {
          lines.push(`- 이유:`)
          reasons.forEach((r, idx) => lines.push(`   ${idx + 1}) ${r}`))
        }
        if (threeNnResult?.exception?.ignoreRecent3mOpinion) {
          lines.push('')
          lines.push('[인수기준 예외 처리]')
          lines.push('- 유병자 상품은 3개월 내 진료이력 발생 시 가입이 어려우므로 예외처리시 체크바랍니다.')
        }
        lines.push('')
      } else if (threeNnResult?.message) {
        lines.push(`- 가능 플랜: -`)
        lines.push(`- 이유:`)
        lines.push(`   1) ${threeNnResult.message}`)
        lines.push('')
      }
    } else {
      lines.push('')
      const hs = healthyAutoSummary || computeHealthyQuestionnaireSummary()
      const hvMemo = profile?.healthyView || {}
      const g3mMemo = Array.isArray(hvMemo['3M']) ? hvMemo['3M'] : []
      const g1yMemo = Array.isArray(hvMemo['1Y_CHECK']) ? hvMemo['1Y_CHECK'] : []

      const labelWithDxCode = (g) => {
        const name = ((g?.diagnosisName || '-').toString().trim()) || '-'
        const codeDisp = formatKcdDottedForMemo(g?.diagnosisCode)
        return codeDisp ? `${name} [${codeDisp}]` : name
      }
      const pushQuestionnaireItemLines = (groups, max = 5, indent = '     ') => {
        if (!Array.isArray(groups) || !groups.length) return
        groups.slice(0, max).forEach((g, idx) => {
          const label = labelWithDxCode(g)
          const treat = Number.isFinite(Number(g?.totalTreatmentDays)) ? Number(g.totalTreatmentDays) : 0
          const drug = Number.isFinite(Number(g?.totalDrugDays)) ? Number(g.totalDrugDays) : 0
          const range = `${formatDate(g?.startDate) || '-'}~${formatDate(g?.endDate) || '-'}`
          lines.push(`${indent}${idx + 1}) [${label} (진료${treat} / 투약${drug})] 기간 : ${range}`)
        })
      }

      lines.push('[건강체(표준체) 질문서 요약]')
      lines.push(
        `- 최근 3개월(진료/검사/치료/입원/수술/투약): ${hs?.recent3m?.has ? '있음' : '없음'}`,
      )
      pushQuestionnaireItemLines(g3mMemo)

      lines.push(
        `- 최근 1년(검사/추가검사/재검사 참고): ${hs?.check1y?.has ? '있음' : '없음'}`,
      )
      pushQuestionnaireItemLines(g1yMemo)

      // 5년(같은 원인 7일+ 치료 또는 30일+ 투약): 있음이면 항목별로 정리
      const g730 = computeHealthy7305YSummaries()
      lines.push(`- 최근 5년(같은 원인 7일+ 치료 또는 30일+ 투약): ${g730.length ? '있음' : '없음'}`)
      if (g730.length) {
        g730.slice(0, 5).forEach((g, idx) => {
          const label = labelWithDxCode(g)
          const treat = Number.isFinite(Number(g?.totalTreatmentDays)) ? Number(g.totalTreatmentDays) : 0
          const drug = Number.isFinite(Number(g?.totalDrugDays)) ? Number(g.totalDrugDays) : 0
          const range = `${formatDate(g?.startDate) || '-'}~${formatDate(g?.endDate) || '-'}`
          lines.push(`     ${idx + 1}) [${label} (진료${treat} / 투약${drug})] 기간 : ${range}`)
        })
      }

      // 5년(입원/수술): 있음이면 항목별로 정리
      const gHosp5y = Array.isArray(hvMemo['HOSP_5Y']) ? hvMemo['HOSP_5Y'] : []
      lines.push(`- 최근 5년(입원/수술): ${gHosp5y.length ? '있음' : '없음'}`)
      if (gHosp5y.length) {
        gHosp5y.slice(0, 5).forEach((g, idx) => {
          const label = labelWithDxCode(g)
          const treat = Number.isFinite(Number(g?.totalTreatmentDays)) ? Number(g.totalTreatmentDays) : 0
          const drug = Number.isFinite(Number(g?.totalDrugDays)) ? Number(g.totalDrugDays) : 0
          const range = `${formatDate(g?.startDate) || '-'}~${formatDate(g?.endDate) || '-'}`
          const inRaw = g?.sumInpatientDays
          const inN =
            inRaw != null && inRaw !== '' && Number.isFinite(Number(inRaw)) ? Math.max(0, Math.round(Number(inRaw))) : null
          const surN = (() => {
            const v = g?.surgeryCount
            return v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0
              ? Math.floor(Number(v))
              : null
          })()
          const inPart = inN != null && inN > 0 ? `입원·재원 ${inN}일` : null
          const surPart = surN != null ? `수술(시술) ${surN}건` : null
          const hMeta = [inPart, surPart].filter(Boolean).join(' / ')
          // 입원/수술 섹션: (진료/투약)는 동일 집계 기준 "치료·투약 누적" (외래+입원)으로 기존 UI와 맞춘다.
          const hMetaSeg = hMeta ? ` | ${hMeta}` : ''
          lines.push(
            `    ${idx + 1}) [${label}${hMetaSeg}] (치료·누적: 진료${treat}일 / 투약${drug}일) | 기간(최초~최말) : ${range}`,
          )
        })
        lines.push(MEMO_FOOTNOTE_INHOSP_DATE_RANGE)
      }

      lines.push(
        `- 최근 5년(11대 중대질병): ${hs?.major11_5y?.has ? '있음' : '없음'}`,
      )
    }
    lines.push('')
    // lines.push('첨부파일: (예정) 진료분석 리포트(PDF)')

    const prefill = {
      customerName,
      title,
      commonNote: lines.join('\n'),
      selectedInsurerCodes: selectedInsurerCodes.length > 0 ? selectedInsurerCodes : null,
    }
    const key = `requestNew.prefill.${Date.now()}`
    try {
      sessionStorage.setItem(key, JSON.stringify(prefill))
    } catch (_) {
      // ignore
    }
    navigate(`/requests/new?prefillKey=${encodeURIComponent(key)}`)
    setThreeNnModalOpen(false)
    setDesignRequestModalOpen(false)
  }, [navigate, selectedReport, selectedReportId, selectedReportMeta, show, threeNnResult, healthyAutoSummary, computeHealthyQuestionnaireSummary])

  const handleReanalyzeReport = async (reportId) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      await api.post(`/health-reports/${targetId}/reanalyze`)
      // 최신 summaryJson 다시 로드
      const res = await api.get(`/health-reports/${targetId}`)
      const data = res.data?.data || res.data
      setSelectedReport(data)
      setSelectedReportId(targetId)
      show('재분석이 완료되었습니다. 최신 룰이 반영되었습니다.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || null
      show(`재분석에 실패했습니다.${msg ? ` (${msg})` : ''}`, 'error')
    }
  }

  const handleDownloadAnalysisResult = async (reportId, customerNameOverride, createdAtOverride) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      setExporting(true)
      const res = await api.get(`/health-reports/${targetId}/export`, { responseType: 'blob' })
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      // 파일명은 서버 Content-Disposition을 우선 사용하고, 없으면 기본값으로 다운로드한다.
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
      const filenameStar =
        cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''
      const customerName = customerNameOverride || selectedReport?.customerName
      const createdAt = createdAtOverride || selectedReport?.createdAt
      const ymd = formatDate(createdAt || new Date())
      const fallbackCustomer = customerName
        ? `${customerName}_진료내역분석_${ymd}.xlsx`
        : `진료내역분석_${targetId}.xlsx`
      const filename = headerFilename && headerFilename !== 'health_report.xlsx' ? headerFilename : fallbackCustomer

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('분석결과 다운로드가 시작되었습니다. 내려받은 파일은 상담·설계 참고용으로만 사용해 주세요.', 'success')
    } catch (e) {
      console.error(e)
      show('분석결과 다운로드에 실패했습니다.', 'error')
    } finally {
      setExporting(false)
    }
  }

  const handleDownloadAnalysisPdf = async (reportId, customerNameOverride, createdAtOverride, type = 'analysis', engine = 'legacy') => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      setPdfExporting(true)
      const t = (type || 'analysis').toString().trim().toLowerCase()
      const e = (engine || 'legacy').toString().trim().toLowerCase() || 'legacy'
      const params = new URLSearchParams()
      if (t) params.set('type', t)
      if (e) params.set('engine', e)
      const qs = params.toString() ? `?${params.toString()}` : ''
      const res = await api.get(`/health-reports/${targetId}/pdf${qs}`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'application/pdf' })

      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
      const filenameStar =
        cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''
      const customerName = customerNameOverride || selectedReport?.customerName
      const createdAt = createdAtOverride || selectedReport?.createdAt
      const ymd = formatDate(createdAt || new Date())
      const fallbackCustomer = customerName
        ? `${customerName}_진료내역분석_${ymd}.pdf`
        : `진료내역분석_${targetId}.pdf`
      const filename = headerFilename && headerFilename !== 'health_report.pdf' ? headerFilename : fallbackCustomer

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      const engineLabel = e === 'html' ? 'HTML/CSS Beta' : 'Legacy'
      show(
        t === 'counseling'
          ? `PDF(상담용, ${engineLabel}) 다운로드가 시작되었습니다. 고객 안내 전 내용을 다시 확인해 주세요.`
          : `PDF(상세, ${engineLabel}) 다운로드가 시작되었습니다. 민감한 진료정보 취급에 유의해 주세요.`,
        'success'
      )
    } catch (e) {
      console.error(e)
      show('PDF 다운로드에 실패했습니다.', 'error')
    } finally {
      setPdfExporting(false)
    }
  }

  const handleDownloadTemplateImage = async (reportId, customerNameOverride, createdAtOverride) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      setTemplateImageExporting(true)
      const res = await api.get(`/health-reports/${targetId}/template-image`, {
        params: reportTemplateId ? { reportTemplateId } : undefined,
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
      const filenameStar =
        cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''
      const customerName = customerNameOverride || selectedReport?.customerName
      const createdAt = createdAtOverride || selectedReport?.createdAt
      const ymd = formatDate(createdAt || new Date())
      const fallbackCustomer = customerName
        ? `${customerName}_진료정보분석_템플릿_${ymd}.png`
        : `진료정보분석_템플릿_${targetId}.png`
      const filename = headerFilename && headerFilename !== 'health_report_template.png' ? headerFilename : fallbackCustomer
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('템플릿 이미지 다운로드가 시작되었습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, '템플릿 이미지 다운로드에 실패했습니다.'), 'error')
    } finally {
      setTemplateImageExporting(false)
    }
  }

  const handleDownloadTemplateHtml = async (reportId, customerNameOverride, createdAtOverride) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      setTemplateHtmlExporting(true)
      const res = await api.get(`/health-reports/${targetId}/template-html`, {
        params: reportHtmlTemplateId ? { reportTemplateId: reportHtmlTemplateId } : undefined,
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'text/html' })
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
      const filenameStar =
        cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''
      const customerName = customerNameOverride || selectedReport?.customerName
      const createdAt = createdAtOverride || selectedReport?.createdAt
      const ymd = formatDate(createdAt || new Date())
      const fallbackCustomer = customerName
        ? `${customerName}_진료정보분석_템플릿_${ymd}.html`
        : `진료정보분석_템플릿_${targetId}.html`
      const filename = headerFilename && headerFilename !== 'health_report_template.html' ? headerFilename : fallbackCustomer
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('HTML 템플릿 다운로드가 시작되었습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, 'HTML 템플릿 다운로드에 실패했습니다.'), 'error')
    } finally {
      setTemplateHtmlExporting(false)
    }
  }

  const handleDownloadRawJson = async (reportId, customerNameOverride, createdAtOverride) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      const res = await api.get(`/health-reports/${targetId}/raw`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'application/json' })

      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
      const filenameStar =
        cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''

      const customerName = customerNameOverride || selectedReport?.customerName
      const createdAt = createdAtOverride || selectedReport?.createdAt
      const ymd = formatDate(createdAt || new Date())
      const fallbackCustomer = customerName
        ? `${customerName}_진료내역원본_${ymd}.json`
        : `진료내역원본_${targetId}.json`
      const filename = headerFilename && headerFilename !== 'health_raw.json' ? headerFilename : fallbackCustomer

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('원본(raw) 다운로드가 시작되었습니다. 원본 진료 데이터는 민감정보이므로 필요한 경우에만 보관해 주세요.', 'success')
    } catch (e) {
      console.error(e)
      const status = e?.response?.status
      if (status === 410) {
        show('원본(raw) 데이터가 만료/삭제되어 다운로드할 수 없습니다. (보관기간 경과)', 'error')
      } else {
        show('원본(raw) 다운로드에 실패했습니다.', 'error')
      }
    }
  }

  const handleDownloadSummaryJson = async (reportId, customerNameOverride, createdAtOverride) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      const res = await api.get(`/health-reports/${targetId}/summary`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'application/json' })

      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
      const filenameStar =
        cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''

      const customerName = customerNameOverride || selectedReport?.customerName
      const createdAt = createdAtOverride || selectedReport?.createdAt
      const ymd = formatDate(createdAt || new Date())
      const fallbackCustomer = customerName
        ? `${customerName}_진료내역요약_${ymd}.json`
        : `진료내역요약_${targetId}.json`
      const filename =
        headerFilename && headerFilename !== 'health_summary.json' ? headerFilename : fallbackCustomer

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('요약(summary) 다운로드가 시작되었습니다.', 'success')
    } catch (e) {
      console.error(e)
      const status = e?.response?.status
      if (status === 410) {
        show('요약(summary) 데이터가 없어 다운로드할 수 없습니다.', 'error')
      } else if (status === 403) {
        show('운영자만 다운로드할 수 있습니다.', 'error')
      } else {
        show('요약(summary) 다운로드에 실패했습니다.', 'error')
      }
    }
  }

  const handleDeleteJob = async (job) => {
    if (!job?.jobId) return
    const label = job.customerName ? `${job.customerName} (${job.jobId})` : `Job ${job.jobId}`
    const ok = window.confirm(`${label} 분석 내역을 삭제할까요?\n삭제 후에는 분석 결과와 관련 원본 데이터를 다시 확인할 수 없습니다.`)
    if (!ok) return
    try {
      await api.delete(`/health-jobs/${job.jobId}`)
      // 선택 중인 결과였다면 닫기
      if (selectedReportId && job.healthReportId && selectedReportId === job.healthReportId) {
        setSelectedReport(null)
        setSelectedReportId(null)
      }
      // 폴링 중인 작업이었다면 초기화
      if (currentJob?.jobId && currentJob.jobId === job.jobId) {
        setCurrentJob(null)
      }
      queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['health-job', job.jobId] })
      show('분석 내역을 삭제했습니다. 삭제된 결과는 다시 확인할 수 없습니다.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || null
      show(`분석 내역을 삭제하지 못했습니다.${msg ? ` (${msg})` : ''}`, 'error')
    }
  }

  const handleEditNote = (customerId) => {
    const current = notes[customerId] || ''
    const next = window.prompt('고객 메모를 입력해 주세요. 주민등록번호, 계좌번호, 건강 진단 상세 등 민감정보는 적지 마세요.', current)
    if (next !== null) {
      setNotes((prev) => ({ ...prev, [customerId]: next }))
    }
  }

  const healthCustomerRowKey = (c) => `${c?.customerName || ''}|${c?.phoneNumber || ''}`

  const registerHealthCustomerToMgmt = async (c) => {
    const rowKey = healthCustomerRowKey(c)
    const name = (c?.customerName || '').trim()
    const phone = String(c?.phoneNumber || '').replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS)
    if (!name) {
      show('고객명이 없어 등록할 수 없습니다.', 'error')
      return
    }
    if (!(phone.length >= 10 && phone.length <= MAX_PHONE_DIGITS)) {
      show(`연락처는 숫자 10~${MAX_PHONE_DIGITS}자리로 등록할 수 있습니다.`, 'error')
      return
    }
    if (c?.customerRefId) {
      show('이미 고객관리에 등록된 고객입니다.', 'error')
      return
    }
    setHealthCrmRegisterKey(rowKey)
    try {
      await api.post('/customer-management/customers', {
        customerName: name,
        customerPhone: phone,
        memo: HEALTH_ANALYSIS_CRM_MEMO,
      }, { __silent: true })
      show('고객관리에 등록되었습니다. 등록만으로 고객에게 알림이 전송되지는 않습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['health-job-customers'] })
    } catch (e) {
      const st = e?.response?.status
      const msg = e?.response?.data?.message || e?.message || '등록에 실패했습니다.'
      show(st === 409 ? '이미 동일 연락처로 등록된 고객이 있습니다.' : msg, 'error')
    } finally {
      setHealthCrmRegisterKey(null)
    }
  }

  const handleGoToReportsForCustomer = (name) => {
    if (name) {
      setSearch(name)
    }
    setActiveTab('reports')
  }

  const handleRetryIdentity = async () => {
    if (!retryJobId) return
    const identity = (retryIdentity || '').replaceAll(/[^0-9]/g, '')
    if (identity.length !== MAX_IDENTITY_DIGITS) {
      show('주민등록번호는 숫자 13자리로 입력해 주세요.', 'error')
      return
    }
    try {
      await api.post(`/health-jobs/${retryJobId}/identity`, { identity }, { __silent: true, __suppressGlobal409Toast: true })
      setRetryJobId(null)
      setRetryIdentity('')
      // 재시도 시작 직후 목록/단건 모두 즉시 갱신(사용자가 "상태가 안 바뀐다"로 느끼는 문제 방지)
      try { await jobsQuery.refetch?.() } catch { }
      try { await jobStatusQuery.refetch?.() } catch { }
      await customersQuery.refetch()
      show('재시도 요청이 접수되었습니다. 잠시 후 상태가 갱신됩니다.', 'success')
    } catch (e) {
      console.error(e)
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || null
      show(`재시도 요청에 실패했습니다.${msg ? ` (${msg})` : ''}`, 'error')
    }
  }

  const handleRetrySameInfo = async (job) => {
    const jobId = job?.jobId
    if (!jobId) return
    try {
      setRetryingJobId(jobId)
      await api.post(`/health-jobs/${jobId}/retry`, undefined, { __silent: true, __suppressGlobal409Toast: true })
      // 재시도 시작 직후 목록/단건 모두 즉시 갱신(사용자가 "상태가 안 바뀐다"로 느끼는 문제 방지)
      try { await jobsQuery.refetch?.() } catch { }
      try { await jobStatusQuery.refetch?.() } catch { }
      await customersQuery.refetch()
      show('재시도 요청이 접수되었습니다. 잠시 후 상태가 갱신됩니다.', 'success')
    } catch (e) {
      console.error(e)
      const code = String(e?.response?.data?.code || e?.response?.data?.message || '')
      if (code === 'IDENTITY_TTL_EXPIRED') {
        setRetryJobId(jobId)
        setRetryIdentity('')
        show('재시도를 위해 주민등록번호를 다시 입력해 주세요. (인증 정보 만료)', 'error')
        return
      }
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || null
      show(`재시도 요청에 실패했습니다.${msg ? ` (${msg})` : ''}`, 'error')
    } finally {
      setRetryingJobId(null)
    }
  }

  const openFailInfo = async (job) => {
    if (!job?.jobId) return
    // 목록에서 lastError가 내려오지만, 운영/지원 시 최신 값을 보기 위해 단건도 한번 조회한다.
    try {
      const res = await api.get(`/health-jobs/${job.jobId}`)
      const body = res?.data
      const data = body?.data || null
      if (body && body.success === false) {
        setFailInfoJob(job)
      } else {
        setFailInfoJob({ ...(job || {}), ...(data || {}) })
      }
    } catch (e) {
      setFailInfoJob(job)
    }
  }

  const parsedSummary = useMemo(() => {
    if (!selectedReport?.summaryJson) return null
    try {
      return JSON.parse(selectedReport.summaryJson)
    } catch (e) {
      return null
    }
  }, [selectedReport])

  const profileFromSummary = parsedSummary?.profile || null
  byPeriodFromSummary = profileFromSummary?.byPeriod || null
  byPeriodGroupedDrugsFromSummary = profileFromSummary?.byPeriodGroupedDrugs || null
  const byPeriodGroupedDiagnosisFromSummary = profileFromSummary?.byPeriodGroupedDiagnosis || null
  const hiddenCompFromSummary = profileFromSummary?.hiddenCompensation || null
  healthyViewFromSummary = profileFromSummary?.healthyView || null
  const substandardViewFromSummary = profileFromSummary?.substandardView || null
  const dentalViewFromSummary = profileFromSummary?.dentalView || null

  const resolvedHealthDashboard = useMemo(() => {
    if (!parsedSummary || !profileFromSummary) return null
    const dash = parsedSummary.dashboard
    if (dash && typeof dash === 'object' && !Array.isArray(dash) && Object.keys(dash).length > 0) {
      return dash
    }
    const base = selectedReport?.createdAt ? new Date(selectedReport.createdAt) : new Date()
    return buildHealthDashboardFallback(parsedSummary, profileFromSummary, base)
  }, [parsedSummary, profileFromSummary, selectedReport?.createdAt])

  const buildFeBeCompare = useCallback(() => {
    if (!parsedSummary?.profile) {
      return {
        error: 'summaryJson(profile)이 없어 비교할 수 없습니다.',
        createdAt: new Date().toISOString(),
      }
    }

    const profile = parsedSummary.profile
    const rawEpisodes = Array.isArray(profile?.allEpisodesRaw) ? profile.allEpisodesRaw : []
    const byPeriodGrouped = profile?.byPeriodGroupedDiagnosis || {}
    const hiddenComp = profile?.hiddenCompensation || {}
    const hv = profile?.healthyView || {}
    const sv = profile?.substandardView || {}
    const dv = profile?.dentalView || {}

    // NOTE: 본 함수는 컴포넌트 하단에 선언된 normalizeDateDigitsSafe/isPharmacyLike보다 먼저 평가될 수 있어(TDZ),
    // 여기서는 FE 로직을 로컬로 재정의한다.
    const normalizeDateDigitsSafeLocal = (v) => {
      if (!v) return null
      const s = String(v).replaceAll('-', '').trim()
      if (s.length !== 8) return null
      return s
    }
    const isPharmacyLikeLocal = (line) => {
      const t = (line?.treatTypeDetail || line?.treatType || '').toString()
      const org = (line?.hospitalName || '').toString()
      if (t.includes('조제') || t.includes('처방')) return true
      if (org.includes('약국')) return true
      return false
    }
    const dayOf = (x) => normalizeDateDigitsSafeLocal(x?.startDate || x?.endDate)
    const isPharm = (x) => isPharmacyLikeLocal(x) || String(x?.hospitalName || '').includes('약국')
    const norm = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
    const hospKey = (x) => {
      const name = norm(x?.hospitalName || '')
      const code = norm(x?.hospitalCode || '')
      return name ? name : (code ? code : '_')
    }

    // 필터 함수들 (UI에서 사용하는 것과 동일한 로직)
    const isInpatientOrSurgeryLikeItemLocal = (x) => {
      if (!x) return false
      if (Boolean(x?.inpatient)) return true
      const surgeryName = (x?.surgeryName || '').toString().trim()
      if (surgeryName) return true
      if (Array.isArray(x?.surgeryNames) && x.surgeryNames.some((s) => String(s || '').trim())) return true
      const sc = Number(x?.surgeryCount)
      if (Number.isFinite(sc) && sc > 0) return true
      return false
    }
    const isDentalLikeItemLocal = (x) => {
      if (!x) return false
      const dm = (x?.dentalMain ?? '').toString().trim()
      const dd = (x?.dentalDetail ?? '').toString().trim()
      if (dm && dm !== '해당 없음') return true
      if (dd && dd !== '해당 없음') return true
      return false
    }
    const isDentalLikeGroupLocal = (g) => {
      if (!g) return false
      if (isDentalLikeItemLocal(g)) return true
      const details = Array.isArray(g?.details) ? g.details : null
      if (!details || details.length === 0) return false
      return details.some(isDentalLikeItemLocal)
    }
    const isInpatientOrSurgeryLikeGroupLocal = (g) => {
      if (!g) return false
      if (isInpatientOrSurgeryLikeItemLocal(g)) return true
      const details = Array.isArray(g?.details) ? g.details : null
      if (!details || details.length === 0) return false
      return details.some(isInpatientOrSurgeryLikeItemLocal)
    }
    const isCarInsuranceLineLocal = (x) => {
      if (!x) return false
      const src = String(x?.source || '')
      return src.startsWith('CAR_')
    }
    const filterByTreatmentSourceLocal = (list, sourceLabel) => {
      if (!Array.isArray(list)) return []
      if (sourceLabel !== '자동차보험') return list
      const out = []
      for (const item of list) {
        const details = item && Array.isArray(item.details) ? item.details : null
        if (details) {
          const filteredDetails = details.filter(isCarInsuranceLineLocal)
          if (filteredDetails.length > 0) out.push({ ...item, details: filteredDetails })
          continue
        }
        if (isCarInsuranceLineLocal(item)) out.push(item)
      }
      return out
    }

    const sumDayMax = (details, valueKey, { skipPharmacyWhenTreatmentDays = false } = {}) => {
      const list = Array.isArray(details) ? details : []
      if (list.length === 0) return null
      const maxByDay = new Map()
      let hasAny = false
      for (const d of list) {
        if (!d) continue
        if (skipPharmacyWhenTreatmentDays && valueKey === 'totalTreatmentDays' && isPharm(d)) continue
        const v = d?.[valueKey]
        const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
        if (!Number.isFinite(n)) continue
        const day = dayOf(d) || '__NO_DAY__'
        const prev = maxByDay.get(day)
        maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
        hasAny = true
      }
      if (!hasAny) return null
      let sum = 0
      for (const x of maxByDay.values()) sum += Math.max(0, x)
      return sum
    }

    const computeEncounterCountFromDetails = (details, rawList, opts = {}) => {
      const { includeRawFallback = true } = opts || {}
      const list = Array.isArray(details) ? details : []
      if (list.length === 0) return null

      const visitSet = new Set()
      const days = new Set()
      for (const x of list) {
        const d = dayOf(x)
        if (d) days.add(d)
        if (isPharm(x)) continue
        if (!d) continue
        visitSet.add(`${d}|${hospKey(x)}`)
      }
      if (visitSet.size > 0) return visitSet.size

      if (!includeRawFallback) return 0
      if (!Array.isArray(rawList) || rawList.length === 0) return 0
      for (const d of days) {
        if (!d) continue
        for (const x of rawList) {
          const src = String(x?.source || '')
          if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT'))) continue
          if (dayOf(x) !== d) continue
          if (isPharm(x)) continue
          visitSet.add(`${d}|${hospKey(x)}`)
        }
      }
      return visitSet.size
    }

    const safeTotalTreatmentDays = (g) => {
      const sumFromDetails = sumDayMax(g?.details, 'totalTreatmentDays', { skipPharmacyWhenTreatmentDays: true })
      if (Number.isFinite(sumFromDetails)) return sumFromDetails
      const v = g?.sumTreatmentDays ?? g?.totalTreatmentDays ?? null
      return v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
    }
    const safeTotalDrugDays = (g) => {
      const sumFromDetails = sumDayMax(g?.details, 'totalDrugDays', { skipPharmacyWhenTreatmentDays: false })
      if (Number.isFinite(sumFromDetails)) return sumFromDetails
      const v = g?.sumDrugDays ?? g?.totalDrugDays ?? null
      return v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
    }

    const feDisplayEncounterCount = (g) => {
      const n = Number(g?.encounterCount)
      if (Number.isFinite(n) && n > 0) return n
      const fb = computeEncounterCountFromDetails(g?.details, rawEpisodes, { includeRawFallback: true })
      if (Number.isFinite(fb) && fb > 0) return fb
      if (Number.isFinite(n)) return Math.max(0, n)
      const c = Number(g?.count)
      return Number.isFinite(c) ? Math.max(0, c) : 0
    }

    const compareGroups = (groups, { labelPrefix = '' } = {}) => {
      const list = Array.isArray(groups) ? groups : []
      const rows = []
      for (const g of list) {
        const code = (g?.diagnosisCode || '').toString().trim()
        const name = (g?.diagnosisName || '').toString().trim()
        const label = `${labelPrefix}${name || code || '-'}${code && name ? ` (${code})` : ''}`
        const feEnc = feDisplayEncounterCount(g)
        const feTreat = safeTotalTreatmentDays(g)
        const feDrug = safeTotalDrugDays(g)

        const beEncDisp = Number(g?.encounterCountDisplay)
        const beEncRaw = Number(g?.encounterCount)
        const beTreat = g?.totalTreatmentDaysDisplay ?? g?.sumTreatmentDays ?? g?.totalTreatmentDays ?? null
        const beDrug = g?.totalDrugDaysDisplay ?? g?.sumDrugDays ?? g?.totalDrugDays ?? null
        const beTreatN = beTreat !== null && beTreat !== undefined && String(beTreat).trim() !== '' ? Number(beTreat) : null
        const beDrugN = beDrug !== null && beDrug !== undefined && String(beDrug).trim() !== '' ? Number(beDrug) : null

        const diffs = {
          encounter: Number.isFinite(beEncDisp) ? feEnc !== beEncDisp : false,
          treatDays: Number.isFinite(feTreat) && Number.isFinite(beTreatN) ? feTreat !== beTreatN : false,
          drugDays: Number.isFinite(feDrug) && Number.isFinite(beDrugN) ? feDrug !== beDrugN : false,
        }
        const hasDiff = Object.values(diffs).some(Boolean)
        rows.push({
          diagnosisCode: code,
          label,
          fe: { encounter: feEnc, treatDays: feTreat, drugDays: feDrug },
          be: { encounterCount: Number.isFinite(beEncRaw) ? beEncRaw : null, encounterDisplay: Number.isFinite(beEncDisp) ? beEncDisp : null, treatDays: beTreatN, drugDays: beDrugN },
          hasDiff,
          raw: g,
        })
      }
      return rows
    }

    // UI(화면) 기준: "5년 내 7일 치료/30일 투약" 탭은 healthyView가 아니라 raw(5Y)+투약 dedupe/link로 summaries를 만든다.
    const buildUiHealthy730Summaries = () => {
      const key = '5Y'
      const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[key]
      const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodes

      const dateToCodes = new Map()
      const codeToName = new Map()
      for (const e of rawList) {
        if (!e || e.source !== 'BASIC_TREAT') continue
        const day = normalizeDateDigitsSafeLocal(e.startDate || e.endDate)
        const code = (e.diagnosisCode || '').toString().trim()
        if (!day || !code) continue
        if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
        dateToCodes.get(day).add(code)
        if (!codeToName.has(code) && e.diagnosisName) codeToName.set(code, e.diagnosisName)
      }

      // 치료(외래/입원)일수: BASIC_TREAT 기반 (약국 제외)
      const treatGroups = new Map()
      for (const e of rawList) {
        if (!e || e.source !== 'BASIC_TREAT') continue
        if (isPharmacyLikeLocal(e)) continue
        const code = (e.diagnosisCode || '').toString().trim()
        if (!code) continue
        if (!treatGroups.has(code)) treatGroups.set(code, [])
        treatGroups.get(code).push(e)
      }

      const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)

      const dateDigitsToDate = (yyyymmdd) => {
        const s = normalizeDateDigitsSafeLocal(yyyymmdd)
        if (!s) return null
        const y = Number(s.slice(0, 4))
        const m = Number(s.slice(4, 6)) - 1
        const d = Number(s.slice(6, 8))
        const dt = new Date(y, m, d)
        return Number.isFinite(dt.getTime()) ? dt : null
      }
      const daysBetweenLocal = (aDigits, bDigits) => {
        const a = dateDigitsToDate(aDigits)
        const b = dateDigitsToDate(bDigits)
        if (!a || !b) return null
        const diffMs = Math.abs(a.getTime() - b.getTime())
        return Math.floor(diffMs / (24 * 60 * 60 * 1000))
      }
      const normalizeDrugKeyLocal = (line) => {
        const n = (line?.drugName || '').toString().trim().toLowerCase()
        const days = line?.totalDrugDays ?? ''
        return `${n}|${days}`
      }

      const PRESCRIBE_FILL_LINK_DAYS_LOCAL = 5
      const dedupeAndLinkDrugsPharmacyFirstLocal = (drugLines) => {
        if (!Array.isArray(drugLines) || drugLines.length === 0) return []
        const perDayKey = new Map()
        for (const line of drugLines) {
          const day = normalizeDateDigitsSafeLocal(line?.startDate || line?.endDate)
          const key = normalizeDrugKeyLocal(line)
          if (!day || !key || key.startsWith('|')) continue
          const mapKey = `${day}__${key}`
          const prev = perDayKey.get(mapKey)
          if (!prev) {
            perDayKey.set(mapKey, line)
            continue
          }
          const prevIsPharm = isPharmacyLikeLocal(prev)
          const curIsPharm = isPharmacyLikeLocal(line)
          if (!prevIsPharm && curIsPharm) perDayKey.set(mapKey, line)
        }
        const compact = Array.from(perDayKey.values())

        const pharmacies = compact.filter((x) => isPharmacyLikeLocal(x))
        const hospitals = compact.filter((x) => !isPharmacyLikeLocal(x))
        const usedHospital = new Set()
        const enriched = []

        for (const p of pharmacies) {
          const pDay = normalizeDateDigitsSafeLocal(p?.startDate || p?.endDate)
          const key = normalizeDrugKeyLocal(p)
          let best = null
          let bestDelta = null
          for (let i = 0; i < hospitals.length; i++) {
            if (usedHospital.has(i)) continue
            const h = hospitals[i]
            const hDay = normalizeDateDigitsSafeLocal(h?.startDate || h?.endDate)
            if (!pDay || !hDay) continue
            if (normalizeDrugKeyLocal(h) !== key) continue
            const delta = daysBetweenLocal(pDay, hDay)
            if (delta === null) continue
            if (delta > PRESCRIBE_FILL_LINK_DAYS_LOCAL) continue
            if (bestDelta === null || delta < bestDelta) {
              bestDelta = delta
              best = { idx: i, h, hDay }
            }
          }
          if (best) {
            usedHospital.add(best.idx)
            enriched.push({
              ...p,
              prescriberOrgName: best.h?.hospitalName || null,
              prescriberDate: best.hDay || null,
            })
          } else {
            enriched.push(p)
          }
        }
        for (let i = 0; i < hospitals.length; i++) {
          if (usedHospital.has(i)) continue
          enriched.push(hospitals[i])
        }
        return enriched
      }

      const deduped = dedupeAndLinkDrugsPharmacyFirstLocal(drugRaw)

      // 투약 라인을 진단코드에 귀속(명시 코드 우선, 같은날 BASIC_TREAT 코드가 1개면 그걸로)
      const diagGroups = new Map()
      for (const line of deduped) {
        const day = normalizeDateDigitsSafeLocal(line.startDate || line.endDate)
        const explicitCode = (line.diagnosisCode || '').toString().trim()
        let groupCode = explicitCode || null
        if (!groupCode && day && dateToCodes.has(day)) {
          const set = dateToCodes.get(day)
          if (set && set.size === 1) groupCode = Array.from(set)[0]
        }
        if (!groupCode) continue
        if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
        diagGroups.get(groupCode).push(line)
      }

      const computeTotalByDayMaxLocal = (lines, valueKey) => {
        if (!Array.isArray(lines) || lines.length === 0) return null
        const maxByDay = new Map()
        let hasAny = false
        for (const x of lines) {
          const day = normalizeDateDigitsSafeLocal(x?.startDate || x?.endDate) || '__NO_DAY__'
          const v = x?.[valueKey]
          const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
          if (!Number.isFinite(n)) continue
          const prev = maxByDay.get(day)
          maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
          hasAny = true
        }
        if (!hasAny) return null
        let sum = 0
        for (const v of maxByDay.values()) sum += Math.max(0, v)
        return sum
      }
      const minMaxDateFromLinesLocal = (lines) => {
        let min = null
        let max = null
        for (const x of lines || []) {
          const day = normalizeDateDigitsSafeLocal(x?.startDate || x?.endDate)
          if (!day) continue
          if (!min || day < min) min = day
          if (!max || day > max) max = day
        }
        return { min, max }
      }

      const summaries = []
      const allCodes = new Set([...Array.from(diagGroups.keys()), ...Array.from(treatGroups.keys())])
      for (const code of allCodes) {
        const drugLines = diagGroups.get(code) || []
        const treatLines = treatGroups.get(code) || []
        const totalDrugDays = computeTotalByDayMaxLocal(drugLines, 'totalDrugDays') || 0
        const totalTreatmentDays = computeTotalByDayMaxLocal(treatLines, 'totalTreatmentDays') || 0
        if (totalDrugDays < 30 && totalTreatmentDays < 7) continue
        const mergedDetails = [...treatLines, ...drugLines]
        const { min, max } = minMaxDateFromLinesLocal(mergedDetails)
        summaries.push({
          id: `DRUG_7_30_5Y-${code}`,
          groupType: 'DRUGS_DIAG',
          diagnosisCode: code,
          diagnosisName: codeToName.get(code) || '진단',
          startDate: min,
          endDate: max,
          count: mergedDetails.length,
          totalDrugDays,
          totalTreatmentDays,
          details: mergedDetails,
        })
      }
      summaries.sort((a, b) => String(a.diagnosisCode || '').localeCompare(String(b.diagnosisCode || '')))
      return summaries
    }

    const addDataset = (datasets, rowsById, id, label, groups, opts = {}) => {
      const rows = compareGroups(groups, opts)
      const diffCount = rows.filter(r => r?.hasDiff).length
      datasets.push({ id, label, total: rows.length, diff: diffCount })
      rowsById[id] = rows
      return rows
    }

    const dxCodeCandidatesForCompare = (codeLike) => {
      const raw = (codeLike || '').toString().trim()
      if (!raw) return []
      const c = raw.replaceAll(/\s+/g, '').toUpperCase()
      if (!c || c === '$' || c === '-' || c === 'NULL' || c === 'N/A') return []
      const out = [c]
      // 일부 데이터는 "양방/한방/치과" 접두사를 진단코드에 붙여 내려오기도 함(예: AM173 = M173, AR42 = R42, AM79108 = M79108).
      // 실제 KCD 코드가 A로 시작하는 경우(A00 등)는 두번째 문자가 숫자이므로 여기서 제거되지 않는다(안전장치).
      if (/^[ABC][A-Z]/.test(c)) out.push(c.slice(1))
      return Array.from(new Set(out))
    }

    const compareFeToBeByCode = (datasets, rowsById, id, label, feGroups, beGroups, opts = {}) => {
      const feList = Array.isArray(feGroups) ? feGroups : []
      const beList = Array.isArray(beGroups) ? beGroups : []
      const beByCode = new Map()
      for (const b of beList) {
        const code = (b?.diagnosisCode || '').toString().trim()
        for (const k of dxCodeCandidatesForCompare(code)) {
          if (k && !beByCode.has(k)) beByCode.set(k, b)
        }
      }
      const rows = []
      for (const g of feList) {
        const codeRaw = (g?.diagnosisCode || '').toString().trim()
        const codeCandidates = dxCodeCandidatesForCompare(codeRaw)
        const code = codeCandidates[0] || ''
        const name = (g?.diagnosisName || '').toString().trim()
        const labelText = `${opts.labelPrefix || ''}${name || code || '-'}${code && name ? ` (${code})` : ''}`

        // 진단코드가 없으면 BE와 1:1 비교가 불가(불일치로 집계하지 않음)
        if (!codeCandidates.length) {
          rows.push({
            diagnosisCode: codeRaw || null,
            label: labelText,
            fe: { encounter: null, treatDays: null, drugDays: null },
            be: { encounterCount: null, encounterDisplay: null, treatDays: null, drugDays: null },
            diffs: { encounter: false, treatDays: false, drugDays: false },
            diffKeys: [],
            hasDiff: false,
            note: 'BE 비교 불가(진단코드 없음)',
            raw: { fe: g, be: null },
          })
          continue
        }

        const feEnc = (() => {
          const fb = computeEncounterCountFromDetails(g?.details, rawEpisodes, { includeRawFallback: true })
          return Number.isFinite(fb) ? fb : 0
        })()
        const feTreat = Number.isFinite(Number(g?.totalTreatmentDays)) ? Number(g.totalTreatmentDays) : safeTotalTreatmentDays(g)
        const feDrug = Number.isFinite(Number(g?.totalDrugDays)) ? Number(g.totalDrugDays) : safeTotalDrugDays(g)

        let b = null
        for (const k of codeCandidates) {
          if (beByCode.has(k)) {
            b = beByCode.get(k)
            break
          }
        }
        const beEncDisp = b ? Number(b?.encounterCountDisplay) : NaN
        const beEncRaw = b ? Number(b?.encounterCount) : NaN
        const beTreat = b?.totalTreatmentDaysDisplay ?? b?.sumTreatmentDays ?? b?.totalTreatmentDays ?? null
        const beDrug = b?.totalDrugDaysDisplay ?? b?.sumDrugDays ?? b?.totalDrugDays ?? null
        const beTreatN = beTreat !== null && beTreat !== undefined && String(beTreat).trim() !== '' ? Number(beTreat) : null
        const beDrugN = beDrug !== null && beDrug !== undefined && String(beDrug).trim() !== '' ? Number(beDrug) : null

        const compareEncounter = opts?.compareEncounter !== false
        const compareTreatDays = opts?.compareTreatDays !== false
        const compareDrugDays = opts?.compareDrugDays !== false
        const diffs = {
          encounter: compareEncounter ? (Number.isFinite(beEncDisp) ? feEnc !== beEncDisp : true) : false,
          treatDays: compareTreatDays ? (Number.isFinite(feTreat) && Number.isFinite(beTreatN) ? feTreat !== beTreatN : true) : false,
          drugDays: compareDrugDays ? (Number.isFinite(feDrug) && Number.isFinite(beDrugN) ? feDrug !== beDrugN : true) : false,
        }
        const diffKeys = Object.entries(diffs).filter(([, v]) => Boolean(v)).map(([k]) => k)
        const hasDiff = diffKeys.length > 0
        rows.push({
          diagnosisCode: code,
          label: labelText,
          fe: { encounter: feEnc, treatDays: feTreat, drugDays: feDrug },
          be: {
            encounterCount: Number.isFinite(beEncRaw) ? beEncRaw : null,
            encounterDisplay: Number.isFinite(beEncDisp) ? beEncDisp : null,
            treatDays: beTreatN,
            drugDays: beDrugN,
          },
          diffs,
          diffKeys,
          hasDiff,
          raw: { fe: g, be: b },
        })
      }
      const diffCount = rows.filter(r => r?.hasDiff).length
      datasets.push({ id, label, total: rows.length, diff: diffCount })
      rowsById[id] = rows
      return rows
    }

    const addUiOnlyDataset = (datasets, rowsById, id, label, items) => {
      const list = Array.isArray(items) ? items : []
      const rows = []
      for (const g of list) {
        const name = (g?.diagnosisName || g?.drugName || '').toString().trim()
        const code = (g?.diagnosisCode || '').toString().trim()
        const labelText = name || code || '-'
        const feDrug = Number.isFinite(Number(g?.totalDrugDays)) ? Number(g.totalDrugDays) : null
        rows.push({
          diagnosisCode: code || null,
          label: labelText,
          fe: { encounter: null, treatDays: null, drugDays: feDrug },
          be: { encounterCount: null, encounterDisplay: null, treatDays: null, drugDays: null },
          hasDiff: false,
          note: 'BE 비교 불가(진단코드 없음)',
          raw: g,
        })
      }
      datasets.push({ id, label, total: rows.length, diff: 0 })
      rowsById[id] = rows
      return rows
    }

    const datasets = []
    const rowsById = {}

    // 1) 기간별 진료내역(그룹) — UI에 실제 노출되는 핵심
    for (const k of ['1M', '3M', '1Y', '3Y', '5Y']) {
      const groups = Array.isArray(byPeriodGrouped?.[k]) ? byPeriodGrouped[k] : []
      addDataset(datasets, rowsById, `BY_PERIOD_${k}`, `기간별 진료내역(${k})`, groups)
      // 자동차보험 필터 적용 버전도 비교 (UI에서 "자동차보험" 선택 시 필터 적용)
      // FE: 필터 적용된 데이터, BE: 원본 데이터
      const filteredCar = filterByTreatmentSourceLocal(groups, '자동차보험')
      if (filteredCar.length !== groups.length || filteredCar.some((g, i) => {
        const orig = groups[i]
        if (!orig) return true
        const origDetails = Array.isArray(orig.details) ? orig.details : []
        const filtDetails = Array.isArray(g.details) ? g.details : []
        return origDetails.length !== filtDetails.length
      })) {
        compareFeToBeByCode(
          datasets,
          rowsById,
          `BY_PERIOD_${k}_CAR`,
          `기간별 진료내역(${k}) - FE(자동차보험 필터) vs BE(원본)`,
          filteredCar,
          groups,
          { compareEncounter: true, compareTreatDays: true, compareDrugDays: true },
        )
      }
    }

    // 2) 건강체 뷰
    const hosp5YGroups = Array.isArray(hv?.['HOSP_5Y']) ? hv['HOSP_5Y'] : []
    addDataset(datasets, rowsById, 'HOSP_5Y', '건강체: 입원/수술(5년)', hosp5YGroups)
    // 입원/수술 필터 적용 버전도 비교 (UI에서 "5년내 입원/수술" 탭 선택 시 필터 적용)
    const filteredHosp5Y = hosp5YGroups.filter((g) => isInpatientOrSurgeryLikeGroupLocal(g) && !isDentalLikeGroupLocal(g))
    if (filteredHosp5Y.length !== hosp5YGroups.length) {
      compareFeToBeByCode(
        datasets,
        rowsById,
        'HOSP_5Y_FILTERED',
        '건강체: 입원/수술(5년) - 필터 적용(실제 입원/수술만)',
        filteredHosp5Y,
        hosp5YGroups,
        { compareEncounter: true, compareTreatDays: true, compareDrugDays: true },
      )
    }
    addDataset(datasets, rowsById, 'DRUG_7_30_5Y', '건강체(서버): 동일상병 7/30(5년)', Array.isArray(hv?.['DRUG_7_30_5Y']) ? hv['DRUG_7_30_5Y'] : [])
    // UI(화면) 기준 7/30 탭 vs 서버 그룹을 코드 기준으로 직접 비교
    const ui730 = buildUiHealthy730Summaries()
    compareFeToBeByCode(
      datasets,
      rowsById,
      'UI_DRUG_7_30_5Y',
      '건강체(화면): 동일상병 7/30(5년)',
      ui730,
      Array.isArray(hv?.['DRUG_7_30_5Y']) ? hv['DRUG_7_30_5Y'] : [],
    )
    addDataset(datasets, rowsById, 'CHECK_1Y', '건강체: 추가검사/재검사(1년)', Array.isArray(hv?.['1Y_CHECK']) ? hv['1Y_CHECK'] : [])
    addDataset(datasets, rowsById, 'MAJOR11_5Y', '건강체: 11대 중대질병(5년)', Array.isArray(hv?.['MAJOR11_5Y']) ? hv['MAJOR11_5Y'] : [])
    addDataset(datasets, rowsById, 'HEALTHY_3M', '건강체: 3개월(전체)', Array.isArray(hv?.['3M']) ? hv['3M'] : [])

    // 2-1) 모든투약내역(화면) — UI는 raw + dedupe/link로 summaries를 만든다.
    const buildUiDrugsTabSummaries = (periodKey) => {
      const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[periodKey]
      const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodes

      const dateToCodes = new Map()
      const codeToName = new Map()
      for (const e of rawList) {
        if (!e || e.source !== 'BASIC_TREAT') continue
        const day = normalizeDateDigitsSafeLocal(e.startDate || e.endDate)
        const code = (e.diagnosisCode || '').toString().trim()
        if (!day || !code) continue
        if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
        dateToCodes.get(day).add(code)
        if (!codeToName.has(code) && e.diagnosisName) codeToName.set(code, e.diagnosisName)
      }

      const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)

      // 최소 로직: dedupe/link는 위에서 만든 함수 재사용
      const dateDigitsToDate = (yyyymmdd) => {
        const s = normalizeDateDigitsSafeLocal(yyyymmdd)
        if (!s) return null
        const y = Number(s.slice(0, 4))
        const m = Number(s.slice(4, 6)) - 1
        const d = Number(s.slice(6, 8))
        const dt = new Date(y, m, d)
        return Number.isFinite(dt.getTime()) ? dt : null
      }
      const daysBetweenLocal = (aDigits, bDigits) => {
        const a = dateDigitsToDate(aDigits)
        const b = dateDigitsToDate(bDigits)
        if (!a || !b) return null
        const diffMs = Math.abs(a.getTime() - b.getTime())
        return Math.floor(diffMs / (24 * 60 * 60 * 1000))
      }
      const normalizeDrugKeyLocal = (line) => {
        const n = (line?.drugName || '').toString().trim().toLowerCase()
        const days = line?.totalDrugDays ?? ''
        return `${n}|${days}`
      }
      const PRESCRIBE_FILL_LINK_DAYS_LOCAL = 5
      const dedupeAndLinkDrugsPharmacyFirstLocal = (drugLines) => {
        if (!Array.isArray(drugLines) || drugLines.length === 0) return []
        const perDayKey = new Map()
        for (const line of drugLines) {
          const day = normalizeDateDigitsSafeLocal(line?.startDate || line?.endDate)
          const key = normalizeDrugKeyLocal(line)
          if (!day || !key || key.startsWith('|')) continue
          const mapKey = `${day}__${key}`
          const prev = perDayKey.get(mapKey)
          if (!prev) {
            perDayKey.set(mapKey, line)
            continue
          }
          const prevIsPharm = isPharmacyLikeLocal(prev)
          const curIsPharm = isPharmacyLikeLocal(line)
          if (!prevIsPharm && curIsPharm) perDayKey.set(mapKey, line)
        }
        const compact = Array.from(perDayKey.values())

        const pharmacies = compact.filter((x) => isPharmacyLikeLocal(x))
        const hospitals = compact.filter((x) => !isPharmacyLikeLocal(x))
        const usedHospital = new Set()
        const enriched = []

        for (const p of pharmacies) {
          const pDay = normalizeDateDigitsSafeLocal(p?.startDate || p?.endDate)
          const key = normalizeDrugKeyLocal(p)
          let best = null
          let bestDelta = null
          for (let i = 0; i < hospitals.length; i++) {
            if (usedHospital.has(i)) continue
            const h = hospitals[i]
            const hDay = normalizeDateDigitsSafeLocal(h?.startDate || h?.endDate)
            if (!pDay || !hDay) continue
            if (normalizeDrugKeyLocal(h) !== key) continue
            const delta = daysBetweenLocal(pDay, hDay)
            if (delta === null) continue
            if (delta > PRESCRIBE_FILL_LINK_DAYS_LOCAL) continue
            if (bestDelta === null || delta < bestDelta) {
              bestDelta = delta
              best = { idx: i, h, hDay }
            }
          }
          if (best) {
            usedHospital.add(best.idx)
            enriched.push({
              ...p,
              prescriberOrgName: best.h?.hospitalName || null,
              prescriberDate: best.hDay || null,
            })
          } else {
            enriched.push(p)
          }
        }
        for (let i = 0; i < hospitals.length; i++) {
          if (usedHospital.has(i)) continue
          enriched.push(hospitals[i])
        }
        return enriched
      }

      const deduped = dedupeAndLinkDrugsPharmacyFirstLocal(drugRaw)

      const diagGroups = new Map()
      const unassignedDrugGroups = new Map()
      for (const line of deduped) {
        const day = normalizeDateDigitsSafeLocal(line.startDate || line.endDate)
        const explicitCode = (line.diagnosisCode || '').toString().trim()
        let groupCode = explicitCode || null
        if (!groupCode && day && dateToCodes.has(day)) {
          const set = dateToCodes.get(day)
          if (set && set.size === 1) groupCode = Array.from(set)[0]
        }
        if (groupCode) {
          if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
          diagGroups.get(groupCode).push(line)
        } else {
          const dk = normalizeDrugKeyLocal(line)
          if (!unassignedDrugGroups.has(dk)) unassignedDrugGroups.set(dk, [])
          unassignedDrugGroups.get(dk).push(line)
        }
      }

      const computeDrugTotalByDayMax = (lines) => {
        if (!Array.isArray(lines) || lines.length === 0) return null
        const maxByDay = new Map()
        let hasAny = false
        for (const x of lines) {
          const day = normalizeDateDigitsSafeLocal(x?.startDate || x?.endDate) || '__NO_DAY__'
          const v = x?.totalDrugDays
          const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
          if (!Number.isFinite(n)) continue
          const prev = maxByDay.get(day)
          maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
          hasAny = true
        }
        if (!hasAny) return null
        let sum = 0
        for (const v of maxByDay.values()) sum += Math.max(0, v)
        return sum
      }
      const minMaxDateFromLinesLocal = (lines) => {
        let min = null
        let max = null
        for (const x of lines || []) {
          const day = normalizeDateDigitsSafeLocal(x?.startDate || x?.endDate)
          if (!day) continue
          if (!min || day < min) min = day
          if (!max || day > max) max = day
        }
        return { min, max }
      }

      const diagSummaries = []
      for (const [code, lines] of diagGroups.entries()) {
        const { min, max } = minMaxDateFromLinesLocal(lines)
        const totalDrugDays = computeDrugTotalByDayMax(lines)
        diagSummaries.push({
          id: `DRUGS_DIAG-${periodKey}-${code}`,
          groupType: 'DRUGS_DIAG',
          diagnosisCode: code,
          diagnosisName: codeToName.get(code) || '진단',
          startDate: min,
          endDate: max,
          count: lines.length,
          totalDrugDays,
          details: lines,
        })
      }
      diagSummaries.sort((a, b) => String(a.diagnosisCode || '').localeCompare(String(b.diagnosisCode || '')))

      const drugKeySummaries = []
      for (const [dk, lines] of unassignedDrugGroups.entries()) {
        const first = lines[0] || {}
        const { min, max } = minMaxDateFromLinesLocal(lines)
        const totalDrugDays = computeDrugTotalByDayMax(lines)
        drugKeySummaries.push({
          id: `DRUGS_DRUGKEY-${periodKey}-${dk}`,
          groupType: 'DRUGS_DRUGKEY',
          drugKey: dk,
          drugName: first.drugName || '투약',
          diagnosisName: first.drugName || '투약',
          diagnosisCode: null,
          startDate: min,
          endDate: max,
          count: lines.length,
          totalDrugDays,
          details: lines,
        })
      }
      drugKeySummaries.sort((a, b) => String(a.drugName || '').localeCompare(String(b.drugName || '')))

      return { diagSummaries, drugKeySummaries }
    }

    for (const p of ['3M', '1Y', '3Y', '5Y']) {
      const { diagSummaries, drugKeySummaries } = buildUiDrugsTabSummaries(p)
      compareFeToBeByCode(
        datasets,
        rowsById,
        `UI_DRUGS_${p}_DIAG`,
        `모든투약내역(화면): ${p} 진단코드 그룹`,
        diagSummaries,
        Array.isArray(byPeriodGrouped?.[p]) ? byPeriodGrouped[p] : [],
        { compareEncounter: false, compareTreatDays: false, compareDrugDays: true },
      )
      addUiOnlyDataset(
        datasets,
        rowsById,
        `UI_DRUGS_${p}_DRUGKEY`,
        `모든투약내역(화면): ${p} 진단코드 없음(약품 기준)`,
        drugKeySummaries,
      )
    }

    // 3) 유병자 뷰
    addDataset(datasets, rowsById, 'SUB_3M', '유병자: 3개월(진료)', Array.isArray(sv?.recentHighRisk3M) ? sv.recentHighRisk3M : [])
    for (const y of [1, 2, 3, 4, 5]) {
      const key = `inpatientOrSurgeryLike${y}Y`
      const groups = Array.isArray(sv?.[key]) ? sv[key] : []
      addDataset(datasets, rowsById, `SUB_HOSP_${y}Y`, `유병자: ${y}년 입원/수술 유사`, groups)
      // 입원/수술 필터 적용 버전도 비교 (UI에서 "입원/수술" 탭 선택 시 필터 적용)
      const filtered = groups.filter((g) => isInpatientOrSurgeryLikeGroupLocal(g) && !isDentalLikeGroupLocal(g))
      if (filtered.length !== groups.length) {
        compareFeToBeByCode(
          datasets,
          rowsById,
          `SUB_HOSP_${y}Y_FILTERED`,
          `유병자: ${y}년 입원/수술 유사 - 필터 적용(실제 입원/수술만)`,
          filtered,
          groups,
          { compareEncounter: true, compareTreatDays: true, compareDrugDays: true },
        )
      }
    }
    addDataset(datasets, rowsById, 'MAJOR6_5Y', '유병자: 6대 중대질병(5년)', Array.isArray(sv?.MAJOR6_5Y) ? sv.MAJOR6_5Y : [])

    // 4) 치아 뷰
    addDataset(datasets, rowsById, 'DENTAL_5Y', '치아: 전체(5년)', Array.isArray(dv?.all5Y) ? dv.all5Y : (Array.isArray(dv?.within5Y) ? dv.within5Y : []), { labelPrefix: '[치아] ' })
    addDataset(datasets, rowsById, 'DENTAL_DENTURE', '치아: 틀니', Array.isArray(dv?.denture) ? dv.denture : [], { labelPrefix: '[치아] ' })
    addDataset(datasets, rowsById, 'DENTAL_WITHIN_1Y', '치아: 1년 내 충치', Array.isArray(dv?.within1Y) ? dv.within1Y : [], { labelPrefix: '[치아] ' })
    addDataset(datasets, rowsById, 'DENTAL_WITHIN_5Y', '치아: 5년 내 치주질환', Array.isArray(dv?.within5Y) ? dv.within5Y : [], { labelPrefix: '[치아] ' })

    // 5) 숨은보상
    const hiddenLabel = {
      carInjury: '자동차부상',
      diagnosisBenefit: '진단비',
      surgeryProcedure: '수술·시술',
      disability: '후유장해',
      dental: '치아',
      etc: '기타',
    }
    for (const key of Object.keys(hiddenLabel)) {
      const groups = Array.isArray(hiddenComp?.[key]) ? hiddenComp[key] : []
      addDataset(datasets, rowsById, `HIDDEN_${key}`, `숨은보상: ${hiddenLabel[key]}`, groups)
    }

    // summary용(기존 유지): 고지 체크에 직접 연결되는 핵심 3개만
    // 요약 카운트는 "화면에서 실제로 보여주는 7/30 탭" 기준으로 계산
    const drugRows = rowsById.UI_DRUG_7_30_5Y || []
    const hospRows = rowsById.HOSP_5Y || []
    const dentalRows = rowsById.DENTAL_5Y || []

    const treat7Fe = drugRows.filter(r => Number.isFinite(r.fe.treatDays) && r.fe.treatDays >= 7).length
    const drug30Fe = drugRows.filter(r => Number.isFinite(r.fe.drugDays) && r.fe.drugDays >= 30).length
    const treat7Be = drugRows.filter(r => Number.isFinite(r.be.treatDays) && r.be.treatDays >= 7).length
    const drug30Be = drugRows.filter(r => Number.isFinite(r.be.drugDays) && r.be.drugDays >= 30).length

    const hospTotalFe = hospRows.reduce((a, r) => a + (Number.isFinite(r.fe.encounter) ? r.fe.encounter : 0), 0)
    const hospTotalBe = hospRows.reduce((a, r) => a + (Number.isFinite(r.be.encounterDisplay) ? r.be.encounterDisplay : 0), 0)
    const dentalTotalFe = dentalRows.reduce((a, r) => a + (Number.isFinite(r.fe.encounter) ? r.fe.encounter : 0), 0)
    const dentalTotalBe = dentalRows.reduce((a, r) => a + (Number.isFinite(r.be.encounterDisplay) ? r.be.encounterDisplay : 0), 0)

    return {
      createdAt: new Date().toISOString(),
      reportId: selectedReportId || selectedReport?.id || null,
      summary: {
        DRUG_7_30_5Y: { treat7: { fe: treat7Fe, be: treat7Be }, drug30: { fe: drug30Fe, be: drug30Be } },
        HOSP_5Y: { totalEncounters: { fe: hospTotalFe, be: hospTotalBe }, groupCount: hospRows.length },
        DENTAL_5Y: { totalEncounters: { fe: dentalTotalFe, be: dentalTotalBe }, groupCount: dentalRows.length },
      },
      datasets,
      rows: rowsById,
    }
  }, [parsedSummary, selectedReportId, selectedReport?.id])

  const openFeBeComparePopup = useCallback(() => {
    const data = buildFeBeCompare()
    setFeBeCompareData(data)
    setFeBeCompareOpen(true)
  }, [buildFeBeCompare])

  const buildFeBeMismatchExportText = useCallback((data) => {
    const d = data || null
    if (!d || d.error) return `FE↔BE 불일치 내역\n- 오류: ${d?.error || '비교 데이터 없음'}\n`
    const datasets = Array.isArray(d.datasets) ? d.datasets : []
    const rowsById = d.rows || {}

    const lines = []
    lines.push('FE↔BE 불일치 내역(운영자)')
    lines.push(`- reportId: ${d.reportId ?? '-'}`)
    lines.push(`- createdAt: ${d.createdAt ?? '-'}`)
    lines.push('')

    const summaryLine = (label, x) => {
      if (!x) return
      lines.push(`## ${label}`)
      for (const [k, v] of Object.entries(x)) {
        if (!v || typeof v !== 'object') continue
        const fe = v?.fe
        const be = v?.be
        if (fe !== undefined || be !== undefined) {
          lines.push(`- ${k}: FE ${fe ?? '-'} / BE ${be ?? '-'}`)
        }
      }
      lines.push('')
    }
    summaryLine('동일상병 7/30(5년)', d?.summary?.DRUG_7_30_5Y)
    summaryLine('입원/수술(5년)', d?.summary?.HOSP_5Y)
    summaryLine('치아(5년)', d?.summary?.DENTAL_5Y)

    const MAX_PER_DATASET = 80
    const MAX_TOTAL = 800
    let totalAdded = 0

    lines.push('## 데이터셋별 불일치(요약)')
    for (const ds of datasets) {
      if (!ds || !ds.id) continue
      if (!ds.diff) continue
      lines.push(`- ${ds.label} (${ds.diff}/${ds.total})`)
    }
    lines.push('')

    for (const ds of datasets) {
      if (!ds || !ds.id) continue
      if (!ds.diff) continue
      const allRows = Array.isArray(rowsById?.[ds.id]) ? rowsById[ds.id] : []
      const mism = allRows.filter(r => r?.hasDiff)
      if (mism.length === 0) continue

      lines.push(`## ${ds.label} (불일치 ${mism.length}/${allRows.length})`)
      let added = 0
      for (const r of mism) {
        if (added >= MAX_PER_DATASET) break
        if (totalAdded >= MAX_TOTAL) break
        const code = (r?.diagnosisCode || '').toString().trim()
        const label = (r?.label || '').toString().trim() || code || '-'
        const feEnc = r?.fe?.encounter ?? '-'
        const feT = Number.isFinite(r?.fe?.treatDays) ? r.fe.treatDays : '-'
        const feD = Number.isFinite(r?.fe?.drugDays) ? r.fe.drugDays : '-'
        const beEnc = Number.isFinite(r?.be?.encounterDisplay) ? r.be.encounterDisplay : '-'
        const beT = Number.isFinite(r?.be?.treatDays) ? r.be.treatDays : '-'
        const beD = Number.isFinite(r?.be?.drugDays) ? r.be.drugDays : '-'

        const diffFields = Array.isArray(r?.diffKeys) ? r.diffKeys : []

        lines.push(`- ${label}${code && !label.includes(code) ? ` (${code})` : ''} :: [${diffFields.join(', ') || 'unknown'}]`)
        lines.push(`  - FE: enc ${feEnc} / t ${feT} / d ${feD}`)
        lines.push(`  - BE: encDisp ${beEnc} / t ${beT} / d ${beD}`)
        added += 1
        totalAdded += 1
      }
      if (mism.length > added) lines.push(`- (… ${mism.length - added}건 생략)`)
      lines.push('')
      if (totalAdded >= MAX_TOTAL) {
        lines.push(`(전체 불일치가 많아 ${MAX_TOTAL}건까지만 포함했습니다.)`)
        break
      }
    }

    return lines.join('\n')
  }, [])

  const buildFeBeMismatchExportJson = useCallback((data) => {
    const d = data || null
    const datasets = Array.isArray(d?.datasets) ? d.datasets : []
    const rowsById = d?.rows || {}
    const pack = {
      kind: 'FE_BE_MISMATCH_EXPORT',
      reportId: d?.reportId ?? null,
      createdAt: d?.createdAt ?? null,
      summary: d?.summary ?? null,
      datasets: [],
    }
    const MAX_PER_DATASET = 300
    for (const ds of datasets) {
      if (!ds || !ds.id) continue
      const allRows = Array.isArray(rowsById?.[ds.id]) ? rowsById[ds.id] : []
      const mism = allRows.filter(r => r?.hasDiff)
      if (mism.length === 0) continue
      pack.datasets.push({
        id: ds.id,
        label: ds.label,
        total: allRows.length,
        diff: mism.length,
        items: mism.slice(0, MAX_PER_DATASET).map((r) => ({
          diagnosisCode: r?.diagnosisCode ?? null,
          label: r?.label ?? null,
          diffKeys: Array.isArray(r?.diffKeys) ? r.diffKeys : null,
          note: r?.note ?? null,
          fe: r?.fe ?? null,
          be: r?.be ?? null,
        })),
        truncated: mism.length > MAX_PER_DATASET,
      })
    }
    return JSON.stringify(pack, null, 2)
  }, [])

  const episodesFromSummary = useMemo(() => {
    const eps = parsedSummary?.profile?.allEpisodes
    // 실제 리포트 조회 화면에서 DEMO fallback을 사용하면
    // "치아/중대질병 탭에 무관한 항목이 섞여 보이는" 것처럼 오해를 만든다.
    // 요약 파싱 실패/데이터 비어있음은 빈 배열로 처리하고, UI에서 경고를 노출한다.
    return Array.isArray(eps) ? eps : []
  }, [parsedSummary])

  rawEpisodesFromSummary = useMemo(() => {
    const eps = parsedSummary?.profile?.allEpisodesRaw
    return Array.isArray(eps) ? eps : []
  }, [parsedSummary])

  // 투약: 약국 우선 dedupe + 처방일↔조제일 연결(±N일)
  const PRESCRIBE_FILL_LINK_DAYS = 5
  const normalizeDateDigitsSafe = (v) => {
    if (!v) return null
    const s = String(v).replaceAll('-', '').trim()
    if (s.length !== 8) return null
    return s
  }
  const dateDigitsToDate = (yyyymmdd) => {
    const s = normalizeDateDigitsSafe(yyyymmdd)
    if (!s) return null
    const y = Number(s.slice(0, 4))
    const m = Number(s.slice(4, 6)) - 1
    const d = Number(s.slice(6, 8))
    const dt = new Date(y, m, d)
    return Number.isFinite(dt.getTime()) ? dt : null
  }
  const daysBetween = (aDigits, bDigits) => {
    const a = dateDigitsToDate(aDigits)
    const b = dateDigitsToDate(bDigits)
    if (!a || !b) return null
    const diffMs = Math.abs(a.getTime() - b.getTime())
    return Math.floor(diffMs / (24 * 60 * 60 * 1000))
  }
  const normalizeDrugKey = (line) => {
    const n = (line?.drugName || '').toString().trim().toLowerCase()
    const days = line?.totalDrugDays ?? ''
    return `${n}|${days}`
  }
  const isPharmacyLike = (line) => {
    const t = (line?.treatTypeDetail || line?.treatType || '').toString()
    const org = (line?.hospitalName || '').toString()
    if (t.includes('조제') || t.includes('처방')) return true
    if (org.includes('약국')) return true
    return false
  }
  const dedupeAndLinkDrugsPharmacyFirst = (drugLines) => {
    if (!Array.isArray(drugLines) || drugLines.length === 0) return []
    // 1) 같은 날짜/같은 약품키 중복 제거(약국 우선)
    const perDayKey = new Map()
    for (const line of drugLines) {
      const day = normalizeDateDigitsSafe(line?.startDate || line?.endDate)
      const key = normalizeDrugKey(line)
      if (!day || !key || key.startsWith('|')) continue
      const mapKey = `${day}__${key}`
      const prev = perDayKey.get(mapKey)
      if (!prev) {
        perDayKey.set(mapKey, line)
        continue
      }
      const prevIsPharm = isPharmacyLike(prev)
      const curIsPharm = isPharmacyLike(line)
      if (!prevIsPharm && curIsPharm) {
        perDayKey.set(mapKey, line)
      }
    }
    const compact = Array.from(perDayKey.values())

    // 2) 처방일(병원) ↔ 조제일(약국) 다른 경우 연결(±N일, 약품키 동일)
    const pharmacies = compact.filter((x) => isPharmacyLike(x))
    const hospitals = compact.filter((x) => !isPharmacyLike(x))
    const usedHospital = new Set()
    const enriched = []

    for (const p of pharmacies) {
      const pDay = normalizeDateDigitsSafe(p?.startDate || p?.endDate)
      const key = normalizeDrugKey(p)
      let best = null
      let bestDelta = null
      for (let i = 0; i < hospitals.length; i++) {
        if (usedHospital.has(i)) continue
        const h = hospitals[i]
        const hDay = normalizeDateDigitsSafe(h?.startDate || h?.endDate)
        if (!pDay || !hDay) continue
        if (normalizeDrugKey(h) !== key) continue
        const delta = daysBetween(pDay, hDay)
        if (delta === null) continue
        if (delta > PRESCRIBE_FILL_LINK_DAYS) continue
        if (bestDelta === null || delta < bestDelta) {
          bestDelta = delta
          best = { idx: i, h, hDay }
        }
      }
      if (best) {
        usedHospital.add(best.idx)
        enriched.push({
          ...p,
          prescriberOrgName: best.h?.hospitalName || null,
          prescriberDate: best.hDay || null,
        })
      } else {
        enriched.push(p)
      }
    }
    // 병원만 있는 기록(약국 데이터 누락/원내조제 등): 그대로 유지
    for (let i = 0; i < hospitals.length; i++) {
      if (usedHospital.has(i)) continue
      enriched.push(hospitals[i])
    }
    return enriched
  }

  const fieldLabels = {
    // id: 'ID',
    // groupType: '그룹 유형',
    diagnosisName: '진단명',
    diagnosisCode: '진단코드',
    diagnosisNames: '진단명(묶음)',
    drugNames: '약품명(묶음)',
    startDate: '진료 시작일',
    endDate: '진료 종료일',
    // NOTE: 기존 count는 "진단코드 그룹에 포함된 row 수"(라인아이템 수)에 가까워 혼란이 있어,
    // UI에서는 '진료건수'로 표기하고 encounterCount(날짜+기관 기준 dedupe)를 우선 노출한다.
    count: '진료건수',
    hiddenGuide: '숨은보상 가이드',
    totalTreatmentDays: '총 진료일',
    totalDrugDays: '총 투약일',
    inpatient: '입원 여부',
    surgeryName: '수술명',
    surgeryNames: '수술명(묶음)',
    diagnosisCodes: '진단코드(묶음)',
    dentalMain: '치아고지명',
    dentalDetail: '치아고지세부명',
    hospitalName: '병원명',
    hospitalCode: '병원코드',
    treatType: '진료형태',
    treatTypeDetail: '진료형태(상세)',
    drugName: '약품명',
    drugEffect: '약품효과/성분',
    sources: '데이터 출처',
    details: '세부 내역',
  }

  const detailFieldOrder = [
    'diagnosisCode',
    'diagnosisName',
    'diagnosisNames',
    'diagnosisCodes',
    'groupType',
    'hospitalName',
    'hospitalCode',
    'startDate',
    'endDate',
    'treatType',
    'treatTypeDetail',
    'drugName',
    'drugNames',
    'drugEffect',
    'totalTreatmentDays',
    'totalDrugDays',
    'inpatient',
    'surgeryName',
    'surgeryNames',
    'dentalMain',
    'dentalDetail',
    'hiddenGuide',
    'sources',
    'details',
    'count',
    'id',
  ]

  const isEmptyDetailValue = (value) => {
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return Object.keys(value).length === 0
    return false
  }

  const normalizeDiagnosisCodeForDisplay = (value) => {
    if (value === null || value === undefined) return ''
    const raw = String(value).trim()
    if (!raw) return ''
    const compact = raw.replace(/\s+/g, '').toUpperCase()
    if (!compact || compact === '$' || compact === '-' || compact === 'N/A' || compact === 'NA' || compact === 'NULL') return ''
    if (compact === '해당없음' || compact === '해당 없음' || compact === '없음') return ''
    if (!/[A-Z0-9]/.test(compact)) return ''
    return formatKcdDottedForMemo(compact)
  }

  const normalizeDiagnosisNameForDisplay = (value) => {
    if (value === null || value === undefined) return ''
    const text = String(value).trim()
    if (!text) return ''
    const compact = text.replace(/\s+/g, '')
    const upper = compact.toUpperCase()
    if (upper === '$' || upper === '-' || upper === 'N/A' || upper === 'NA' || upper === 'NULL') return ''
    if (compact === '해당없음' || compact === '없음' || compact === '진료') return ''
    if (/^A?[A-Z][0-9]{2}(?:\.?[0-9A-Z]{0,4})[†*]?$/i.test(compact)) return ''
    return text
  }

  const diagnosisNameByCode = useMemo(() => {
    const map = new Map()
    const put = (codeValue, nameValue) => {
      const key = diagnosisCodeLookupKeyForValue(codeValue)
      const name = normalizeDiagnosisNameForDisplay(nameValue)
      if (key && name && !map.has(key)) map.set(key, name)
    }
    const eps = parsedSummary?.profile?.allEpisodesRaw
    for (const ep of Array.isArray(eps) ? eps : []) {
      put(ep?.diagnosisCode, ep?.diagnosisName)
      put(ep?.resDiseaseCode, ep?.resDiseaseName)
    }
    return map
  }, [parsedSummary])

  const treatmentTitleForDisplay = (item) => {
    const diagnosisName = normalizeDiagnosisNameForDisplay(item?.diagnosisName)
    if (diagnosisName) return diagnosisName
    if (Array.isArray(item?.diagnosisNames)) {
      const name = item.diagnosisNames.map(normalizeDiagnosisNameForDisplay).find(Boolean)
      if (name) return name
    }
    const indexedName = diagnosisNameByCode.get(diagnosisCodeLookupKeyForValue(item?.diagnosisCode))
    if (indexedName) return indexedName
    if (Array.isArray(item?.diagnosisCodes)) {
      const name = item.diagnosisCodes
        .map((code) => diagnosisNameByCode.get(diagnosisCodeLookupKeyForValue(code)))
        .find(Boolean)
      if (name) return name
    }
    const hospital = String(item?.hospitalName || item?.hospitalCode || '').trim()
    if (hospital) return hospital
    const treatment = String(item?.treatmentType || item?.treatType || item?.treatTypeDetail || '').trim()
    if (treatment) return treatment
    return '진료내역'
  }

  const formatDetailValue = (key, value) => {
    if (key === 'diagnosisCode') return normalizeDiagnosisCodeForDisplay(value)
    if (key === 'diagnosisName') return normalizeDiagnosisNameForDisplay(value)
    if (key === 'diagnosisCodes' && Array.isArray(value)) {
      return value.map(normalizeDiagnosisCodeForDisplay).filter(Boolean).join(', ')
    }
    if (key === 'diagnosisNames' && Array.isArray(value)) {
      return value.map(normalizeDiagnosisNameForDisplay).filter(Boolean).join(', ')
    }
    if (key === 'inpatient') return value ? '있음' : '없음'
    if (key === 'startDate' || key === 'endDate') {
      return formatDate(value) || ''
    }
    const formatSourceLabel = (src) => {
      const s = (src || '').toString()
      if (!s) return ''
      if (s === 'BASIC_TREAT') return '진료(기본)'
      if (s === 'DETAIL_TREAT') return '진료(상세)'
      if (s === 'PRESCRIBE_DRUG') return '처방/조제'
      if (s === 'CAR_BASIC_TREAT') return '자동차보험(기본)'
      if (s === 'CAR_DETAIL_TREAT') return '자동차보험(상세)'
      if (s.startsWith('CAR_')) return '자동차보험'
      return s
    }
    if (key === 'sources') {
      if (Array.isArray(value)) return value.map(formatSourceLabel).filter(Boolean).join(', ')
      return formatSourceLabel(value)
    }
    // details는 팝업에서 카드형으로 별도 렌더링하므로 여기서는 문자열 변환하지 않는다.
    if (Array.isArray(value)) {
      const allPrimitive = value.every((v) => v === null || ['string', 'number', 'boolean'].includes(typeof v))
      if (allPrimitive) return value.filter((v) => v !== null && v !== undefined && String(v).trim() !== '').join(', ')
      return JSON.stringify(value)
    }
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  // 진료내역 탭용 기간 필터
  const filterByPeriodLabel = (episodes, label) => {
    // 1) BE에서 계산된 byPeriod 데이터가 있으면 우선 사용
    if (byPeriodGroupedDiagnosisFromSummary) {
      const key =
        label === '1개월'
          ? '1M'
          : label === '3개월'
          ? '3M'
          : label === '1년'
          ? '1Y'
          : label === '3년'
          ? '3Y'
          : '5Y'
      const serverList = byPeriodGroupedDiagnosisFromSummary[key]
      if (Array.isArray(serverList) && serverList.length > 0) {
        return serverList
      }
    }

    if (byPeriodFromSummary) {
      const key =
        label === '1개월'
          ? '1M'
          : label === '3개월'
          ? '3M'
          : label === '1년'
          ? '1Y'
          : label === '3년'
          ? '3Y'
          : '5Y'
      const serverList = byPeriodFromSummary[key]
      if (Array.isArray(serverList) && serverList.length > 0) {
        return serverList
      }
    }

    // 2) 요약 정보가 없거나 비어 있으면 기존 클라이언트 사이드 필터 사용
    const now = new Date()
    const days = label === '1개월' ? 30 : label === '3개월' ? 90 : label === '1년' ? 365 : label === '3년' ? 365 * 3 : 365 * 5
    const threshold = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)

    return episodes.filter((e) => {
      const d = e.startDate || e.endDate
      if (!d) return false
      // YYYYMMDD 또는 YYYY-MM-DD 모두 지원
      const normalized = d.includes('-') ? d : `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`
      const dt = new Date(normalized)
      if (Number.isNaN(dt.getTime())) return false
      return dt >= threshold && dt <= now
    })
  }

  const isCarInsuranceLine = (line) => {
    const src = line && typeof line.source === 'string' ? line.source : ''
    return src.startsWith('CAR_')
  }

  // 입원/수술 탭에서 "유사 후보"가 섞여 내려오는 케이스가 있어,
  // UI에서는 실제 입원/수술 플래그가 있는 항목만 표시한다.
  const isInpatientOrSurgeryLikeItem = (x) => {
    if (!x) return false
    if (Boolean(x?.inpatient)) return true
    const surgeryName = (x?.surgeryName || '').toString().trim()
    if (surgeryName) return true
    if (Array.isArray(x?.surgeryNames) && x.surgeryNames.some((s) => String(s || '').trim())) return true
    const sc = Number(x?.surgeryCount)
    if (Number.isFinite(sc) && sc > 0) return true
    return false
  }

  // 치아/구강(임플란트 포함) 항목은 "치아 탭"에서만 보이도록,
  // 입원/수술 탭에서는 제외한다.
  const isDentalLikeItem = (x) => {
    if (!x) return false
    const dm = (x?.dentalMain ?? '').toString().trim()
    const dd = (x?.dentalDetail ?? '').toString().trim()
    if (dm && dm !== '해당 없음') return true
    if (dd && dd !== '해당 없음') return true
    return false
  }

  const isDentalLikeGroup = (g) => {
    if (!g) return false
    if (isDentalLikeItem(g)) return true
    const details = Array.isArray(g?.details) ? g.details : null
    if (!details || details.length === 0) return false
    return details.some(isDentalLikeItem)
  }

  const isInpatientOrSurgeryLikeGroup = (g) => {
    if (!g) return false
    if (isInpatientOrSurgeryLikeItem(g)) return true
    const details = Array.isArray(g?.details) ? g.details : null
    if (!details || details.length === 0) return false
    return details.some(isInpatientOrSurgeryLikeItem)
  }

  const filterByTreatmentSource = (list, sourceLabel) => {
    if (!Array.isArray(list)) return []
    if (sourceLabel !== '자동차보험') return list

    // 서버 그룹(진단코드 그룹 + details)도 있을 수 있고, raw 라인아이템일 수도 있다.
    const out = []
    for (const item of list) {
      const details = item && Array.isArray(item.details) ? item.details : null
      if (details) {
        const filteredDetails = details.filter(isCarInsuranceLine)
        if (filteredDetails.length > 0) out.push({ ...item, details: filteredDetails })
        continue
      }
      if (isCarInsuranceLine(item)) out.push(item)
    }
    return out
  }

  const paginated = (list) => {
    const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages - 1)
    const start = safePage * PAGE_SIZE
    const end = start + PAGE_SIZE
    return {
      items: list.slice(start, end),
      totalPages,
      currentPage: safePage,
    }
  }

  const renderPager = (totalPages, currentPage, options = {}) => {
    if (totalPages <= 1) return null
    const className = options.className || 'mt-3 justify-end'
    return (
      <div className={`flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-700 ${className}`}>
        <span className="text-[11px] font-medium text-slate-500">페이지</span>
        <button
          type="button"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-40"
          disabled={currentPage === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          이전
        </button>
        <span className="min-w-[3.75rem] text-center text-sm font-bold text-slate-800">
          {currentPage + 1} / {totalPages}
        </span>
        <button
          type="button"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-40"
          disabled={currentPage >= totalPages - 1}
          onClick={() => setPage((p) => p + 1)}
        >
          다음
        </button>
      </div>
    )
  }
  const renderTreatmentCard = (item, options = {}) => {
    const { showHiddenGuide, showDrugExtra, showInpatientExtra, showDentalExtra, detailContext } = options
    const context = detailContext || 'ALL'
    const computeSumFromDetails = (key) => {
      if (!item || !Array.isArray(item.details)) return null

      const byDayMaxSum = (valueKey) => {
        const maxByDay = new Map()
        let hasAny = false
        for (const d of item.details) {
          // 약국(조제) 라인은 "치료일수" 집계에서 제외
          if (valueKey === 'totalTreatmentDays' && isPharmacyLike(d)) continue
          const v = d ? d[valueKey] : null
          const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
          if (!Number.isFinite(n)) continue
          const day = normalizeDateDigitsSafe(d?.startDate || d?.endDate) || '__NO_DAY__'
          const prev = maxByDay.get(day)
          maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
          hasAny = true
        }
        if (!hasAny) return null
        let sum = 0
        for (const x of maxByDay.values()) sum += Math.max(0, x)
        return sum
      }

      // 총 투약일/총 진료일은 동일일자 중복이 있을 수 있어 "일자별 최대" 합산으로 계산한다.
      if (key === 'totalDrugDays') return byDayMaxSum('totalDrugDays')
      if (key === 'totalTreatmentDays') return byDayMaxSum('totalTreatmentDays')

      let sum = 0
      let hasAny = false
      for (const d of item.details) {
        const v = d ? d[key] : null
        const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
        if (Number.isFinite(n)) {
          sum += Math.max(0, n)
          hasAny = true
        }
      }
      return hasAny ? sum : null
    }

    const safeTotalTreatmentDays = (() => {
      // UI가 “첫번째 상세 내역 값”처럼 보이는 케이스가 있어,
      // 가능한 경우(details가 존재하면) 항상 details 합산을 우선한다.
      const sumFromDetails = computeSumFromDetails('totalTreatmentDays')
      if (Number.isFinite(sumFromDetails)) return sumFromDetails
      return item?.sumTreatmentDays ?? item?.totalTreatmentDays ?? null
    })()

    const safeTotalDrugDays = (() => {
      const sumFromDetails = computeSumFromDetails('totalDrugDays')
      if (Number.isFinite(sumFromDetails)) return sumFromDetails
      return item?.sumDrugDays ?? item?.totalDrugDays ?? null
    })()

    const computeEncounterCountFromDetails = (details, rawEpisodes, opts = {}) => {
      const { includeRawFallback = true } = opts || {}
      const list = Array.isArray(details) ? details : []
      if (list.length === 0) return null

      const dayOf = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate)
      const isPharm = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
      const norm = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
      const hospKey = (x) => {
        const name = norm(x?.hospitalName || '')
        const code = norm(x?.hospitalCode || '')
        return name ? name : (code ? code : '_')
      }

      const visitSet = new Set()
      const days = new Set()
      for (const x of list) {
        const d = dayOf(x)
        if (d) days.add(d)
        if (isPharm(x)) continue
        if (!d) continue
        visitSet.add(`${d}|${hospKey(x)}`)
      }
      if (visitSet.size > 0) return visitSet.size

      if (!includeRawFallback) return 0
      if (!Array.isArray(rawEpisodes) || rawEpisodes.length === 0) return 0
      for (const d of days) {
        if (!d) continue
        for (const x of rawEpisodes) {
          const src = String(x?.source || '')
          if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT'))) continue
          if (dayOf(x) !== d) continue
          if (isPharm(x)) continue
          visitSet.add(`${d}|${hospKey(x)}`)
        }
      }
      return visitSet.size
    }

    // 요약 카드의 "건수"는 기본적으로 서버 encounterCount(약국 제외, 기관+일자 dedupe)를 우선 사용한다.
    // 다만 서버가 진단코드 백필을 못 한 케이스(약국 라인에만 코드가 붙는 경우)엔 encounterCount가 0으로 내려올 수 있어,
    // 그 경우에만 "실제로 화면에 표시되는 병원 방문" 기준으로 0을 보정한다(팝업과 동일 철학).
    const computeEncounterCountFallback = () => {
      const details = Array.isArray(item?.details) ? item.details : []
      if (details.length === 0) return null

      const dayOf = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate)
      const isPharm = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
      const norm = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
      const hospKey = (x) => {
        const name = norm(x?.hospitalName || '')
        const code = norm(x?.hospitalCode || '')
        return name ? name : (code ? code : '_')
      }

      // 1) item.details(=서버 그룹 details) 내에서 병원 방문을 직접 집계
      const visitSet = new Set()
      const days = new Set()
      for (const x of details) {
        const d = dayOf(x)
        if (d) days.add(d)
        if (isPharm(x)) continue
        if (!d) continue
        visitSet.add(`${d}|${hospKey(x)}`)
      }
      if (visitSet.size > 0) return visitSet.size

      // 2) details에 병원 라인이 없고(예: 약국 라인만 포함된 그룹) 팝업처럼 raw를 보강해야 하는 경우
      if (!Array.isArray(rawEpisodesFromSummary) || rawEpisodesFromSummary.length === 0) return 0
      for (const d of days) {
        for (const x of rawEpisodesFromSummary) {
          const src = String(x?.source || '')
          if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT'))) continue
          if (dayOf(x) !== d) continue
          if (isPharm(x)) continue
          visitSet.add(`${d}|${hospKey(x)}`)
        }
      }
      return visitSet.size
    }

    const displayEncounterCount = (() => {
      const n = Number(item?.encounterCount)
      if (Number.isFinite(n) && n > 0) return n
      // 0일 때만 fallback을 시도
      const fb = computeEncounterCountFromDetails(item?.details, rawEpisodesFromSummary, { includeRawFallback: true })
      if (Number.isFinite(fb) && fb > 0) return fb
      if (Number.isFinite(n)) return Math.max(0, n)
      const c = Number(item?.count)
      return Number.isFinite(c) ? Math.max(0, c) : 0
    })()

    const diagnosisCodeDisplay = normalizeDiagnosisCodeForDisplay(item?.diagnosisCode)
    const diagnosisNameDisplay = normalizeDiagnosisNameForDisplay(item?.diagnosisName)
    const treatmentTitle = treatmentTitleForDisplay(item)
    const diagnosisTitleNode = (diagnosisCodeDisplay || diagnosisNameDisplay || treatmentTitle) ? (
      <>
        {diagnosisCodeDisplay ? <span className="font-mono font-bold">{diagnosisCodeDisplay}</span> : null}
        {diagnosisNameDisplay || treatmentTitle ? (
          <span className={diagnosisCodeDisplay ? 'ml-1' : ''}>{diagnosisNameDisplay || treatmentTitle}</span>
        ) : null}
      </>
    ) : null

    // 보험유니버스 기준 그룹(진단코드/병원/수술) 카드
    if (item && item.groupType) {
      if (item.groupType === 'HOSPITAL' || item.groupType === 'HOSPITAL_DX') {
        const displayCount = displayEncounterCount
        return (
          <div key={item.id} className="rounded border px-3 py-1.5 mb-1.5 text-sm bg-white">
            <div className="font-medium text-slate-900">
              {item.hospitalName || (item.hospitalCode ? '기관' : '기관코드 없음')}
            </div>
            <div className="text-xs text-slate-500">
              기관코드: {item.hospitalCode || '-'}
              {diagnosisCodeDisplay || diagnosisNameDisplay ? (
                <>
                  {' '}• 진단:{' '}
                  {diagnosisCodeDisplay ? <span className="font-mono">{diagnosisCodeDisplay}</span> : null}
                  {diagnosisNameDisplay ? <span> {diagnosisNameDisplay}</span> : null}
                </>
              ) : null}
            </div>
            <div className="mt-1 text-xs text-slate-600">
              기간: {formatDate(item.startDate) || '-'} ~ {formatDate(item.endDate) || '-'} / 건수: {displayCount}
            </div>
            {showInpatientExtra && (
              <div className="mt-1 text-xs text-slate-600">
                입원: {item.inpatient ? '있음' : '없음'}
                {Array.isArray(item.surgeryNames) && item.surgeryNames.length > 0
                  ? ` • 수술/시술: ${item.surgeryNames.join(', ')}`
                  : null}
              </div>
            )}
            <div className="mt-2">
              <button
                type="button"
                className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
              >
                상세내역
              </button>
            </div>
          </div>
        )
      }
      if (item.groupType === 'SURGERY') {
        const displayCount = displayEncounterCount
        return (
          <div key={item.id} className="rounded border px-3 py-1.5 mb-1.5 text-sm bg-white">
            {showHiddenGuide && item.hiddenGuide && (
              <div className="mb-1 text-xs font-semibold text-emerald-700">숨은보상: {item.hiddenGuide}</div>
            )}
            <div className="font-medium text-slate-900">{item.surgeryName || '수술'}</div>
            <div className="mt-1 text-xs text-slate-600">
              기간: {formatDate(item.startDate) || '-'} ~ {formatDate(item.endDate) || '-'} / 건수: {displayCount}
            </div>
            <div className="mt-2">
              <button
                type="button"
                className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
              >
                상세내역
              </button>
            </div>
          </div>
        )
      }
      // 기본: 진단코드 그룹
      const displayCount = displayEncounterCount
      return (
        <div key={item.id} className="rounded border px-3 py-1.5 mb-1.5 text-sm bg-white">
          {showHiddenGuide && item.hiddenGuide && (
            <div className="mb-1 text-xs font-semibold text-emerald-700">숨은보상: {item.hiddenGuide}</div>
          )}
          <div className="font-medium text-slate-900">{diagnosisTitleNode || treatmentTitle}</div>
          <div className="mt-1 text-xs text-slate-600">
            기간: {formatDate(item.startDate) || '-'} ~ {formatDate(item.endDate) || '-'} / 건수: {displayCount}
          </div>
          {Number.isFinite(Number(safeTotalTreatmentDays)) && displayEncounterCount > Number(safeTotalTreatmentDays) ? (
            <div className="mt-1 text-[11px] text-slate-500">
              * 같은 날짜에 여러 기관을 방문한 경우, “진료건수”가 “총 진료일”보다 클 수 있습니다.
            </div>
          ) : null}
          {(showDrugExtra || showInpatientExtra) && (
            <div className="mt-1 text-xs text-slate-600">
              {showInpatientExtra && !showDrugExtra ? (
                <>
                  입원일수: {item.sumInpatientDays ?? item.sumTreatmentDays ?? item.totalTreatmentDays ?? '-'}일
                  {' / '}수술건수: {Number.isFinite(Number(item.surgeryCount)) ? Number(item.surgeryCount) : 0}회
                </>
              ) : (
                <>
                  총 진료일: {safeTotalTreatmentDays ?? '-'}일 • 총 투약일:{' '}
                  {safeTotalDrugDays ?? '-'}일
                </>
              )}
            </div>
          )}
          <div className="mt-2">
            <button
              type="button"
              className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
              onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
            >
              상세내역
            </button>
          </div>
        </div>
      )
    }
    return (
      <div key={item.id} className="rounded border px-3 py-1.5 mb-1.5 text-sm bg-white">
        {showHiddenGuide && item.hiddenGuide && (
          <div className="mb-1 text-xs font-semibold text-emerald-700">숨은보상: {item.hiddenGuide}</div>
        )}
        <div className="font-medium text-slate-900">{diagnosisTitleNode || treatmentTitle}</div>
        <div className="mt-1 text-xs text-slate-600">
          진료 시작일: {formatDate(item.startDate) || '-'} / 진료 종료일: {formatDate(item.endDate) || '-'} / 진료건수:{' '}
          {displayEncounterCount}
        </div>
        {showDrugExtra && (
          <div className="mt-1 text-xs text-slate-600">
            총 진료일: {safeTotalTreatmentDays ?? '-'}일 • 총 투약일:{' '}
            {safeTotalDrugDays ?? '-'}일
          </div>
        )}
        {showInpatientExtra && (
          <div className="mt-1 text-xs text-slate-600">
            입원: {item.inpatient ? '있음' : '없음'} {item.surgeryName && `• 수술명: ${item.surgeryName}`}
          </div>
        )}
        {showDentalExtra && (item.dentalMain || item.dentalDetail) && (
          <div className="mt-1 text-xs text-slate-600">
            치아고지명: {item.dentalMain} / 치아고지세부명: {item.dentalDetail}
          </div>
        )}
        <div className="mt-2">
          <button
            type="button"
            className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
            onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
          >
            상세내역
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold text-slate-900">진료정보분석</h1>
          <AiFeatureBadge />
          {activeTab === 'new' ? (
            <FeatureBillingQuotaBadge gate={healthBillingGate} loading={billingMeQuery.isLoading && !billingMeQuery.data} />
          ) : null}
          <MenuGuideButton slug="health" title="진료정보분석 가이드" />
        </div>
      </header>

      {crmPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="flex max-h-[min(520px,80vh)] w-full max-w-lg flex-col rounded-lg bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-base font-semibold text-slate-900">고객관리에서 불러오기</h2>
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-800"
                onClick={() => setCrmPickerOpen(false)}
              >
                닫기
              </button>
            </div>
            {crmPickerErr ? <div className="mx-4 mb-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-800">{crmPickerErr}</div> : null}
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
              {crmPickerLoading ? (
                <p className="px-2 py-6 text-center text-sm text-slate-500">불러오는 중…</p>
              ) : crmPickerList.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-slate-500">표시할 고객이 없습니다.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {crmPickerList.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => applyCrmCustomerToForm(row)}
                      >
                        <span className="font-medium text-slate-900">{row.customerName || '(이름 없음)'}</span>
                        <span className="text-xs text-slate-500">기존 고객</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-2">
              <Link to="/support/customers" className="text-xs font-medium text-brand-navy hover:underline self-center mr-auto">
                고객관리 화면으로
              </Link>
              <button
                type="button"
                className="rounded border px-3 py-1 text-xs hover:bg-slate-50"
                onClick={() => loadCrmCustomersForPicker()}
                disabled={crmPickerLoading}
              >
                새로고침
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 추가 인증 입력 모달 */}
      {twoWayModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded bg-white p-4 shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">추가 인증 입력</h2>
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={dismissTwoWayModal}
              >
                닫기
              </button>
            </div>
            <div className="mt-3 space-y-3">

              <div>
                <label className="block text-slate-700 mb-1 text-sm">SMS 인증번호</label>
                <input
                  type="text"
                  value={twoWayInput.smsAuthNo}
                  onChange={(e) => setTwoWayInput((p) => ({ ...p, smsAuthNo: e.target.value }))}
                  className="w-full rounded border px-2 py-1 text-sm"
                  placeholder="SMS 인증번호(필요 시)"
                />
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm"
                  onClick={dismissTwoWayModal}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy px-3 py-1 text-sm text-white hover:bg-brand-sky"
                  onClick={submitTwoWayInput}
                  disabled={jobStatusQuery.data?.status !== 'AUTH_INPUT_REQUIRED' || !twoWayInfo}
                >
                  인증 확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 운영자 디버그: FE vs BE 집계 비교 */}
      {feBeCompareOpen && isOperator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-5xl rounded bg-white p-4 shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">FE ↔ BE 집계 비교(운영자)</h2>
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => setFeBeCompareOpen(false)}
              >
                닫기
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-600">
              현재 선택된 리포트의 <span className="font-mono">summaryJson.profile</span> 기준으로 FE 계산값과 BE(서버에서 내려준 필드)를 1:1로 비교합니다.
            </p>

            {feBeCompareData?.error ? (
              <div className="mt-3 rounded border bg-amber-50 p-3 text-sm text-amber-800">
                {feBeCompareData.error}
              </div>
            ) : (
              <>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div className="rounded border p-2">
                    <div className="font-semibold text-slate-800">동일상병 7/30(5년)</div>
                    <div className="mt-1 text-slate-700">
                      7일 치료: FE {feBeCompareData?.summary?.DRUG_7_30_5Y?.treat7?.fe ?? '-'} / BE{' '}
                      {feBeCompareData?.summary?.DRUG_7_30_5Y?.treat7?.be ?? '-'}
                    </div>
                    <div className="text-slate-700">
                      30일 투약: FE {feBeCompareData?.summary?.DRUG_7_30_5Y?.drug30?.fe ?? '-'} / BE{' '}
                      {feBeCompareData?.summary?.DRUG_7_30_5Y?.drug30?.be ?? '-'}
                    </div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="font-semibold text-slate-800">입원/수술(5년)</div>
                    <div className="mt-1 text-slate-700">
                      방문합계: FE {feBeCompareData?.summary?.HOSP_5Y?.totalEncounters?.fe ?? '-'} / BE{' '}
                      {feBeCompareData?.summary?.HOSP_5Y?.totalEncounters?.be ?? '-'}
                    </div>
                    <div className="text-slate-700">그룹수: {feBeCompareData?.summary?.HOSP_5Y?.groupCount ?? '-'}</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="font-semibold text-slate-800">치아(5년)</div>
                    <div className="mt-1 text-slate-700">
                      방문합계: FE {feBeCompareData?.summary?.DENTAL_5Y?.totalEncounters?.fe ?? '-'} / BE{' '}
                      {feBeCompareData?.summary?.DENTAL_5Y?.totalEncounters?.be ?? '-'}
                    </div>
                    <div className="text-slate-700">그룹수: {feBeCompareData?.summary?.DENTAL_5Y?.groupCount ?? '-'}</div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <div className="flex items-center gap-2 rounded border p-2">
                    <div className="text-slate-700 font-medium">비교 대상</div>
                    <select
                      className="rounded border px-2 py-1 text-xs"
                      value={feBeCompareTab}
                      onChange={(e) => setFeBeCompareTab(e.target.value)}
                    >
                      {(Array.isArray(feBeCompareData?.datasets) ? feBeCompareData.datasets : []).map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.label} ({d.diff}/{d.total})
                        </option>
                      ))}
                    </select>
                  </div>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={feBeCompareOnlyDiff}
                      onChange={(e) => setFeBeCompareOnlyDiff(e.target.checked)}
                    />
                    불일치만 보기
                  </label>

                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-xs border-slate-400 text-slate-600 hover:bg-slate-100"
                    onClick={() => setFeBeCompareData(buildFeBeCompare())}
                  >
                    다시 계산
                  </button>

                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-xs border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                    onClick={async () => {
                      const base = feBeCompareData?.error ? buildFeBeCompare() : (feBeCompareData || buildFeBeCompare())
                      const text = buildFeBeMismatchExportText(base)
                      try {
                        await navigator.clipboard.writeText(text)
                        show('불일치 결과(전체)를 클립보드에 복사했습니다.', 'success')
                      } catch {
                        show('클립보드 복사에 실패했습니다.', 'error')
                      }
                    }}
                  >
                    불일치 복사(전체)
                  </button>

                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-xs border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                    onClick={async () => {
                      const base = feBeCompareData?.error ? buildFeBeCompare() : (feBeCompareData || buildFeBeCompare())
                      const json = buildFeBeMismatchExportJson(base)
                      try {
                        await navigator.clipboard.writeText(json)
                        show('불일치 결과(JSON)를 클립보드에 복사했습니다.', 'success')
                      } catch {
                        show('클립보드 복사에 실패했습니다.', 'error')
                      }
                    }}
                  >
                    불일치 복사(JSON)
                  </button>
                </div>

                <div className="mt-3 max-h-[55vh] overflow-auto rounded border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 border-b">
                      <tr>
                        <th className="p-2 text-left">항목</th>
                        <th className="p-2 text-left">FE</th>
                        <th className="p-2 text-left">BE</th>
                        <th className="p-2 text-left">차이</th>
                        <th className="p-2 text-left">원본</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(feBeCompareData?.rows?.[feBeCompareTab]) ? feBeCompareData.rows[feBeCompareTab] : [])
                        .filter((r) => (feBeCompareOnlyDiff ? r?.hasDiff : true))
                        .slice(0, 200)
                        .map((r, idx) => {
                          const diffText = Array.isArray(r?.diffKeys) ? r.diffKeys.join(', ') : ''
                          const diagnosisCodeDisplay = normalizeDiagnosisCodeForDisplay(r?.diagnosisCode)
                          return (
                            <tr key={`${r?.diagnosisCode || idx}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                              <td className="p-2 align-top">
                                <div className="font-medium text-slate-900">{r?.label || '-'}</div>
                                <div className="text-[11px] text-slate-500 font-mono">{diagnosisCodeDisplay}</div>
                              </td>
                              <td className="p-2 align-top font-mono text-slate-800">
                                enc:{' '}{r?.fe?.encounter ?? '-'} / t:{' '}{Number.isFinite(r?.fe?.treatDays) ? r.fe.treatDays : '-'} / d:{' '}
                                {Number.isFinite(r?.fe?.drugDays) ? r.fe.drugDays : '-'}
                              </td>
                              <td className="p-2 align-top font-mono text-slate-800">
                                encRaw:{' '}{Number.isFinite(r?.be?.encounterCount) ? r.be.encounterCount : '-'} / encDisp:{' '}
                                {Number.isFinite(r?.be?.encounterDisplay) ? r.be.encounterDisplay : '-'} / t:{' '}
                                {Number.isFinite(r?.be?.treatDays) ? r.be.treatDays : '-'} / d:{' '}
                                {Number.isFinite(r?.be?.drugDays) ? r.be.drugDays : '-'}
                              </td>
                              <td className="p-2 align-top">
                                {diffText ? (
                                  <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800">{diffText}</span>
                                ) : r?.note ? (
                                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{r.note}</span>
                                ) : (
                                  <span className="text-[11px] text-slate-400">-</span>
                                )}
                              </td>
                              <td className="p-2 align-top">
                                <button
                                  type="button"
                                  className="rounded border px-2 py-1 text-[11px] border-slate-300 text-slate-700 hover:bg-slate-100"
                                  onClick={() => {
                                    const title = r?.label || diagnosisCodeDisplay || '원본'
                                    let json = ''
                                    try {
                                      json = JSON.stringify(r?.raw || {}, null, 2)
                                    } catch {
                                      json = String(r?.raw || '')
                                    }
                                    setFeBeCompareRawTitle(title)
                                    setFeBeCompareRawJson(json)
                                    setFeBeCompareRawOpen(true)
                                  }}
                                >
                                  JSON
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      {(Array.isArray(feBeCompareData?.rows?.[feBeCompareTab]) ? feBeCompareData.rows[feBeCompareTab] : []).filter((r) => (feBeCompareOnlyDiff ? r?.hasDiff : true)).length === 0 ? (
                        <tr>
                          <td className="p-3 text-slate-500" colSpan={4}>
                            {feBeCompareOnlyDiff ? '불일치 항목이 없습니다.' : '표시할 항목이 없습니다.'}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  * BE 비교값은 <span className="font-mono">encounterCountDisplay/total*DaysDisplay/sum*Days</span> 우선으로 사용합니다.
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 운영자 디버그: 원본 JSON 보기 */}
      {feBeCompareRawOpen && isOperator && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-4xl rounded bg-white p-4 shadow">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">원본 그룹 JSON</h3>
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => setFeBeCompareRawOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="mt-1 text-xs text-slate-600">{feBeCompareRawTitle || '-'}</div>
            <div className="mt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border px-2 py-1 text-xs border-slate-400 text-slate-600 hover:bg-slate-100"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(feBeCompareRawJson || '')
                    show('JSON을 클립보드에 복사했습니다.', 'success')
                  } catch {
                    show('클립보드 복사에 실패했습니다.', 'error')
                  }
                }}
              >
                복사
              </button>
            </div>
            <pre className="mt-2 max-h-[65vh] overflow-auto rounded border bg-slate-50 p-3 text-[11px] leading-4">
{feBeCompareRawJson || ''}
            </pre>
          </div>
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="border-b flex gap-4 text-sm">
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'new' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('new')}
        >
          신규 분석
        </button>
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'reports' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('reports')}
        >
          분석 이력
        </button>
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'customers' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('customers')}
        >
          분석 고객 목록
        </button>
      </div>

      {activeTab === 'new' && healthBillingBlocked ? (
        <FeatureGateNotice gate={healthBillingGate} quotaLabel="분석 가능 건수" />
      ) : null}

      {/* 1) 신규 분석 탭 */}
      {activeTab === 'new' && !healthBillingBlocked && (
        <HealthIntakeFormLayout
          serviceLabel={HEALTH_DATA_CONSENT_VARIANTS['health-analysis'].serviceLabel}
          onSubmit={handleCreateReport}
          identityPanel={(
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block font-medium text-slate-700">고객명</label>
                <ManagedCustomerAutocomplete
                  name="name"
                  value={form.name}
                  onValueChange={(next) => setForm((prev) => ({ ...prev, customerId: '', name: next }))}
                  selectedCustomerId={form.customerId}
                  onSelectCustomer={(customer) => {
                    setForm((prev) => ({
                      ...prev,
                      customerId: String(customer.id),
                      name: customer.customerName != null ? String(customer.customerName).trim() : prev.name,
                      phoneNumber: customer.customerPhone ? formatPhoneDigitsKo(customer.customerPhone) : prev.phoneNumber,
                    }))
                  }}
                  onClearCustomer={() => setForm((prev) => ({ ...prev, customerId: '' }))}
                  inputClassName="w-full rounded border px-2 py-1.5 text-sm"
                  required
                  disabled={authInProgress || createReportMutation.isLoading}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block font-medium text-slate-700">주민등록번호</label>
                <input
                  type="text"
                  name="identity"
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full rounded border px-2 py-1.5 text-sm font-mono tracking-tight"
                  placeholder="숫자 13자리"
                  value={identityDigitsUiDisplay}
                  onChange={handleIdentityChange}
                  required
                  disabled={authInProgress || createReportMutation.isLoading}
                  aria-label="주민등록번호 13자리"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-slate-700">인증수단</label>
                <select
                  name="authProvider"
                  value={form.authProvider}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  disabled={authInProgress || createReportMutation.isLoading}
                >
                  <option value="KAKAO">카카오톡(간편인증)</option>
                  <option value="SMS">SMS(휴대폰인증)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block font-medium text-slate-700">통신사</label>
                <select
                  name="telecom"
                  value={form.telecom}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  disabled={authInProgress || createReportMutation.isLoading}
                >
                  <option value="0">SKT</option>
                  <option value="1">KT</option>
                  <option value="2">LG U+</option>
                  <option value="3">알뜰폰(SKT)</option>
                  <option value="4">알뜰폰(KT)</option>
                  <option value="5">알뜰폰(LG U+)</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block font-medium text-slate-700">연락처</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  placeholder={`010-1234-5678 (숫자 10~${MAX_PHONE_DIGITS}자리)`}
                  required
                  disabled={authInProgress || createReportMutation.isLoading}
                />
              </div>
            </div>
          )}
          consentPanel={(
            <HealthDataConsentFields
              items={consentItems}
              checked={dataConsents}
              onChange={setDataConsents}
              disabled={authInProgress || createReportMutation.isLoading}
              loading={consentBundleQuery.isLoading}
            />
          )}
          footerActions={(
            <>
              <button
                type="submit"
                className="inline-flex items-center rounded bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-sky disabled:opacity-60"
                disabled={createReportMutation.isLoading || authInProgress || consentBundleQuery.isLoading || !allHealthDataConsentsChecked(consentItems, dataConsents)}
              >
                {createReportMutation.isLoading ? '요청 중...' : (authInProgress ? '인증 진행 중...' : '인증 요청하기')}
              </button>
              {isOperator && (
                <button
                  type="button"
                  onClick={handleCreateCustomerAuthRequest}
                  className="inline-flex items-center rounded border border-brand-navy bg-white px-4 py-1.5 text-sm font-medium text-brand-navy hover:bg-slate-50"
                  disabled={createCustomerAuthRequestMutation.isLoading || createReportMutation.isLoading || authInProgress || consentBundleQuery.isLoading || !allHealthDataConsentsChecked(consentItems, dataConsents)}
                >
                  {createCustomerAuthRequestMutation.isLoading ? '발송 중...' : '고객 직접 인증 요청'}
                </button>
              )}
              {currentJobFeedback ? (
                <div className={`w-full rounded-lg border px-3 py-2 text-xs leading-relaxed ${currentJobFeedbackClass}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-semibold">{currentJobFeedback.title}</div>
                    <div className="font-mono text-[11px]">
                      Job {currentJob.jobId} · {jobStatusLabel(jobStatus)}
                    </div>
                  </div>
                  <p className="mt-1">{currentJobFeedback.description}</p>
                  {jobStatusQuery.data?.healthReportId ? (
                    <button
                      type="button"
                      className="mt-2 font-medium underline"
                      onClick={() => handleViewResult(jobStatusQuery.data.healthReportId)}
                    >
                      완료된 분석 결과 보기
                    </button>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        />
      )}

      {/* 2) 분석 목록 탭 */}
      {activeTab === 'reports' && (
        <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {isOperator && (
                <>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={operatorLoadMode}
                    onChange={(e) => {
                      const v = e.target.value || 'MINE'
                      if (v === 'ALL') {
                        const ok = window.confirm('전체보기는 전체 분석/고객 데이터를 불러오므로 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                        if (!ok) return
                        setOperatorOrgId('')
                      } else if (v === 'MINE') {
                        setOperatorOrgId('')
                      }
                      setOperatorLoadMode(v)
                    }}
                    title="운영자: 조회 모드"
                  >
                    <option value="MINE">내것만 보기</option>
                    <option value="BY_ORG">조직별</option>
                    <option value="ALL">전체보기</option>
                  </select>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={operatorOrgId}
                    disabled={operatorLoadMode !== 'BY_ORG' || operatorOrgLoading}
                    onChange={(e) => {
                      setOperatorOrgId(e.target.value)
                    }}
                    title="운영자: 조직 선택"
                  >
                    <option value="">{operatorOrgLoading ? '조직 불러오는 중...' : '조직 선택(필수)'}</option>
                    {operatorOrgOptions.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </>
              )}
              {isGaScopeAdmin && (
                <>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={gaLoadMode}
                    onChange={(e) => {
                      const v = e.target.value || 'MINE'
                      if (v === 'MINE') {
                        setGaOrgId('')
                      } else if (v === 'ALL') {
                        const ok = window.confirm('전체보기는 조회 범위가 넓어 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                        if (!ok) return
                        setGaOrgId('')
                      }
                      setGaLoadMode(v)
                    }}
                    title="GA: 조회 범위"
                  >
                    <option value="MINE">내것만 보기</option>
                    <option value="BY_ORG">조직별</option>
                    <option value="ALL">전체보기</option>
                  </select>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={gaOrgId}
                    disabled={gaLoadMode !== 'BY_ORG' || gaOrgLoading}
                    onChange={(e) => setGaOrgId(e.target.value)}
                    title="GA: 조직 선택"
                  >
                    <option value="">{gaOrgLoading ? '조직 불러오는 중...' : '내 조직(전체)'}</option>
                    {gaOrgOptions.map(o => (
                      <option key={o.id} value={o.id}>{o.name} (ID {o.id})</option>
                    ))}
                  </select>
                </>
              )}
              <input
                type="text"
                className="rounded border px-2 py-1 text-sm"
                placeholder="고객명 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => jobsQuery.refetch()}
              >
                새로고침
              </button>
            </div>
            {isOperator && (
              <div className="flex justify-end">
                <input
                  ref={manualRawUploadInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleManualRawUploadChange}
                />
                <button
                  type="button"
                  className="rounded border border-brand-navy px-3 py-1 text-sm font-medium text-brand-navy hover:bg-slate-50 disabled:opacity-40"
                  onClick={handleManualRawUploadClick}
                  disabled={manualRawUploading}
                >
                  {manualRawUploading ? '업로드 중...' : 'JSON 업로드'}
                </button>
              </div>
            )}
          </div>

          {isOperator && operatorLoadMode === 'BY_ORG' && !operatorOrgId && (
            <p className="text-xs text-slate-500">운영자는 먼저 조직을 선택한 뒤 목록이 조회됩니다.</p>
          )}
          {isGaScopeAdmin && gaLoadMode === 'BY_ORG' && gaOrgLoading && (
            <p className="text-xs text-slate-500">조직 옵션을 불러오는 중입니다...</p>
          )}

          {jobsQuery.isLoading ? (
            <p className="text-xs text-slate-500">분석 목록을 불러오는 중입니다...</p>
          ) : jobsQuery.isError ? (
            <p className="text-xs text-red-500">분석 목록을 불러오지 못했습니다.</p>
          ) : (
            <>
              <div className="space-y-2 text-sm">
                {filteredJobs.map((c) => (
                  <div key={c.jobId} className="rounded border px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-slate-900">
                            {c.customerName ? `고객명: ${c.customerName}` : '고객 (이름 미상)'}
                          </span>
                          {isManualRawUploadJob(c) && (
                            <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
                              [수동]
                            </span>
                          )}
                        </div>
                        {/* <div className="text-xs text-slate-500">
                          고객 ID: {c.customerId ?? '-'}
                        </div> */}
                        <div className="text-xs text-slate-500">
                          요청일시: {formatApiDateTimeDisplay(c.createdAtDisplay, c.createdAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-600">
                          상태:{' '}
                          {c.status === 'FAILED' ? (
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 text-xs border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                              onClick={() => openFailInfo(c)}
                              title="실패 사유 보기"
                            >
                              실패
                            </button>
                          ) : (
                            jobStatusLabel(c.status)
                          )}
                        </span>

                        {c.status === 'FAILED' && (
                          <button
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-40"
                            onClick={() => handleRetrySameInfo(c)}
                            disabled={retryingJobId === c.jobId}
                            title="동일 정보로 재시도"
                          >
                            {retryingJobId === c.jobId ? '재시도 중...' : '재시도'}
                          </button>
                        )}
                        {c.status === 'AUTH_INPUT_REQUIRED' && (
                          <button
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/health-jobs/${c.jobId}`)
                                const data = res.data?.data || res.data
                                openTwoWayInputModal(data)
                              } catch (_) {
                                show('추가 인증 정보를 불러오지 못했습니다.', 'error')
                              }
                            }}
                          >
                            추가 인증 입력
                          </button>
                        )}
                        {c.healthReportId && (
                          <>
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                              onClick={() => {
                                if (selectedReportId === c.healthReportId) {
                                  setSelectedReport(null)
                                  setSelectedReportId(null)
                                } else {
                                  handleViewResult(c.healthReportId, {
                                    customerName: c.customerName,
                                    birthDate: c.birthDate || c.customerBirthDate || null,
                                    jobId: c.jobId,
                                    createdAt: c.createdAt,
                                  })
                                }
                              }}
                            >
                              {selectedReportId === c.healthReportId ? '결과 접기' : '결과 조회'}
                            </button>
                            {/* {c.healthReportId && (
                              <>
                                <select
                                  className="rounded border px-2 py-0.5 text-xs bg-white"
                                  value={recommendCoverageType}
                                  onChange={(e) => setRecommendCoverageType(e.target.value)}
                                >
                                  <option value="HEALTH">건강체</option>
                                  <option value="CANCER">암</option>
                                </select>
                                <button
                                  type="button"
                                  className="rounded border px-2 py-0.5 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                                  onClick={() => handleRecommendForReport(c.healthReportId)}
                                  disabled={recommendMutation.isLoading}
                                >
                                  상품 자동 추천
                                </button>
                              </>
                            )} */}
                          </>
                        )}
                        {(c.healthReportId || c.status === 'FAILED' || c.status === 'AUTH_INPUT_REQUIRED') && (
                          <button
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => handleDeleteJob(c)}
                          >
                            삭제
                          </button>
                        )}
                      </div>
                    </div>

                    {selectedReport && selectedReportId === c.healthReportId && (
                      <div className="mt-3 rounded-lg border bg-slate-50 px-3 py-3 space-y-3">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
                          <div>
                            {/* <div className="font-semibold text-slate-900">선택된 분석 결과</div>
                            <div className="text-xs text-slate-600">
                              리포트 ID:{' '}
                              <span className="font-mono">
                                {selectedReport.reportId ?? selectedReport.id}
                              </span>{' '}
                              • 위험도{' '}
                              <span className="font-mono">
                                {selectedReport.riskScore ?? '-'}점 / {selectedReport.riskLevel || '-'}
                              </span>
                            </div> */}
                            <div className="text-xs text-slate-500">
                              생성일시:{' '}
                              <span className="font-mono">
                                {formatApiDateTimeDisplay(selectedReport.createdAtDisplay, selectedReport.createdAt)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {canShowReanalyzeButton && (
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                                onClick={() => handleReanalyzeReport(selectedReportId)}
                              >
                                재분석(7일 이내만 가능)
                              </button>
                            )}
                            <button
                              type="button"
                              className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white flex-1 min-w-0 h-[2.5rem] flex items-center justify-center"
                              onClick={() => openDesignRequestModal('substandard')}
                            >
                              설계의뢰
                            </button>
                            {isOperator && SHOW_CUSTOMER_CARD_NEWS_BUTTONS ? (
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-teal-400 bg-teal-50 text-teal-800 hover:bg-teal-100 flex-1 min-w-0 h-[2.5rem] flex items-center justify-center"
                                onClick={() => handleOpenHealthCardNews(selectedReportId, {
                                  customerName: c.customerName,
                                  createdAt: selectedReport?.createdAt,
                                  createdAtDisplay: selectedReport?.createdAtDisplay,
                                })}
                              >
                                카드뉴스
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40 whitespace-pre-line leading-tight flex-1 min-w-0 h-[2.5rem] flex items-center justify-center"
                              onClick={() =>
                                handleDownloadAnalysisResult(selectedReportId, c.customerName, selectedReport?.createdAt)
                              }
                              disabled={exporting}
                            >
                              {exporting ? '다운로드\n준비 중...' : '분석결과\n다운로드'}
                            </button>
                            <button
                              type="button"
                              className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40 whitespace-pre-line leading-tight flex-1 min-w-0 h-[2.5rem] flex items-center justify-center"
                              onClick={() =>
                                handleDownloadAnalysisPdf(selectedReportId, c.customerName, selectedReport?.createdAt, 'counseling')
                              }
                              disabled={pdfExporting}
                            >
                              {pdfExporting ? 'PDF\n준비 중...' : '리포트\n다운로드'}
                            </button>
                          </div>
                          <>
                            {/* <button
                              type="button"
                              className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40"
                              onClick={() =>
                                handleDownloadAnalysisPdf(selectedReportId, c.customerName, selectedReport?.createdAt, 'analysis')
                              }
                              disabled={pdfExporting}
                            >
                              {pdfExporting ? 'PDF 준비 중...' : 'PDF(상세)'}
                            </button> */}
                            {isOperator && (
                              <>
                                <button
                                  type="button"
                                  className="rounded border px-2 py-1 text-xs border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100 disabled:opacity-40"
                                  onClick={() =>
                                    handleDownloadAnalysisPdf(
                                      selectedReportId,
                                      c.customerName,
                                      selectedReport?.createdAt,
                                      'counseling',
                                      'legacy'
                                    )
                                  }
                                  disabled={pdfExporting}
                                  title="운영자 테스트용 신규 버튼입니다. 기존 리포트 다운로드 버튼은 그대로 유지합니다."
                                >
                                  {pdfExporting ? '출력물 준비 중...' : '고객상담 출력물'}
                                </button>
                                {reportTemplateOptions.length > 0 ? (
                                  <select
                                    className="max-w-[190px] rounded border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-800 disabled:opacity-50"
                                    value={reportTemplateId}
                                    onChange={(e) => setReportTemplateId(e.target.value)}
                                    disabled={templateImageExporting}
                                    title="운영자 전용 진료정보분석 템플릿"
                                  >
                                    <option value="">운영 기본 템플릿</option>
                                    {reportTemplateOptions.map((template) => (
                                      <option key={template.id} value={template.id}>
                                        {template.effectiveSelectionLabel || template.selectionLabel || template.title || '출력물 템플릿'}
                                      </option>
                                    ))}
                                  </select>
                                ) : null}
                                <button
                                  type="button"
                                  className="rounded border px-2 py-1 text-xs border-indigo-500 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 disabled:opacity-40"
                                  onClick={() => handleDownloadTemplateImage(selectedReportId, c.customerName, selectedReport?.createdAt)}
                                  disabled={templateImageExporting || reportTemplateOptions.length === 0}
                                  title={reportTemplateOptions.length > 0 ? '운영자 전용 출력물 템플릿 이미지' : '게시된 이미지 템플릿이 없습니다.'}
                                >
                                  {templateImageExporting ? '이미지 준비 중...' : '템플릿 이미지'}
                                </button>
                                {reportHtmlTemplateOptions.length > 0 ? (
                                  <select
                                    className="max-w-[190px] rounded border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-800 disabled:opacity-50"
                                    value={reportHtmlTemplateId}
                                    onChange={(e) => setReportHtmlTemplateId(e.target.value)}
                                    disabled={templateHtmlExporting}
                                    title="운영자 전용 진료정보분석 HTML 템플릿"
                                  >
                                    <option value="">운영 기본 HTML</option>
                                    {reportHtmlTemplateOptions.map((template) => (
                                      <option key={template.id} value={template.id}>
                                        {template.effectiveSelectionLabel || template.selectionLabel || template.title || '출력물 템플릿'}
                                      </option>
                                    ))}
                                  </select>
                                ) : null}
                                <button
                                  type="button"
                                  className="rounded border px-2 py-1 text-xs border-indigo-500 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 disabled:opacity-40"
                                  onClick={() => handleDownloadTemplateHtml(selectedReportId, c.customerName, selectedReport?.createdAt)}
                                  disabled={templateHtmlExporting || reportHtmlTemplateOptions.length === 0}
                                  title={reportHtmlTemplateOptions.length > 0 ? '운영자 전용 출력물 HTML' : '게시된 HTML 템플릿이 없습니다.'}
                                >
                                  {templateHtmlExporting ? 'HTML 준비 중...' : 'HTML'}
                                </button>
                                <button
                                  type="button"
                                  className="rounded border px-2 py-1 text-xs border-purple-500 text-purple-700 hover:bg-purple-50"
                                  onClick={() => {
                                    setCounselingModalOpen(true)
                                    if (selectedReportId && !counseling) {
                                      loadCounseling(selectedReportId)
                                    }
                                  }}
                                >
                                  AI 상담 가이드
                                </button>
                              </>
                            )}
                          </>
                          {isOperator && (
                            <>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                                onClick={() =>
                                  handleDownloadAnalysisPdf(
                                    selectedReportId,
                                    c.customerName,
                                    selectedReport?.createdAt,
                                    'counseling',
                                    'html'
                                  )
                                }
                                disabled={pdfExporting}
                              >
                                {pdfExporting ? 'PDF 준비 중...' : 'PDF HTML/CSS(beta)'}
                              </button>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-cyan-500 text-cyan-700 hover:bg-cyan-50"
                                onClick={() =>
                                  handleDownloadAnalysisPdf(
                                    selectedReportId,
                                    c.customerName,
                                    selectedReport?.createdAt,
                                    'analysis',
                                    'html'
                                  )
                                }
                                disabled={pdfExporting}
                              >
                                {pdfExporting ? 'PDF 준비 중...' : 'PDF 상세 HTML(beta)'}
                              </button>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-slate-400 text-slate-600 hover:bg-slate-100"
                                onClick={() =>
                                  handleDownloadRawJson(selectedReportId, c.customerName, selectedReport?.createdAt)
                                }
                              >
                                raw_json 다운로드
                              </button>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-slate-400 text-slate-600 hover:bg-slate-100"
                                onClick={() =>
                                  handleDownloadSummaryJson(selectedReportId, c.customerName, selectedReport?.createdAt)
                                }
                              >
                                summary_json 다운로드
                              </button>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                                onClick={openFeBeComparePopup}
                              >
                                FE↔BE 비교
                              </button>
                            </>
                          )}
                        </div>
                        {/* 결과 세부 탭 (앱과 동일: 요약 정보 → … → 숨은보상 맨 끝) */}
                        <div className="border-b mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs md:text-sm">
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'summary'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('summary')
                              setPage(0)
                            }}
                          >
                            요약 정보
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'healthy'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('healthy')
                              setPage(0)
                            }}
                          >
                            건강체
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'substandard'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('substandard')
                              setPage(0)
                            }}
                          >
                            유병자
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'dental'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('dental')
                              setPage(0)
                            }}
                          >
                            치아
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'treatments'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('treatments')
                              setPage(0)
                            }}
                          >
                            모든진료내역
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'drugs' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('drugs')
                              setPage(0)
                            }}
                          >
                            모든투약내역
                          </button>
                        </div>



                        {/* 이하 결과 탭 내용 */}
                        {/* 0. 요약 정보 */}
                        {resultTab === 'summary' && resolvedHealthDashboard && (() => {
                          const d = resolvedHealthDashboard
                          const period = d.period || {}
                          const cost = d.cost || {}
                          const recent3Months = d.recent3Months || {}
                          const fiveYears = d.fiveYears || {}
                          const sameDisease = d.sameDisease || {}
                          const majorDisease = d.majorDisease || {}
                          const carInsurance = d.carInsurance || {}

                          const periodFrom =
                            period.from != null && String(period.from).trim() !== ''
                              ? formatDate(period.from)
                              : '-'
                          const periodTo =
                            period.to != null && String(period.to).trim() !== ''
                              ? formatDate(period.to)
                              : '-'

                          const carInpatient = toIntDashboard(carInsurance.inpatientCount, 0)
                          const carOutpatient = toIntDashboard(carInsurance.outpatientCount, 0)
                          const treat3m = toIntDashboard(recent3Months.treatCount, 0)
                          const drug3m = toIntDashboard(recent3Months.drugCount, 0)
                          const surgeryCount = toIntDashboard(fiveYears.surgeryCount, 0)
                          const inpatientCount = toIntDashboard(fiveYears.inpatientCount, 0)
                          const treat7Count = toIntDashboard(sameDisease.treat7DiseaseCount, 0)
                          const drug30Count = toIntDashboard(sameDisease.drug30DiseaseCount, 0)
                          const major11Count = toIntDashboard(majorDisease.major11Count, 0)
                          const major6Count = toIntDashboard(majorDisease.major6Count, 0)
                          const carInsuranceStatusRaw = String(carInsurance.status || '').trim()
                          const carInsuranceStatusDisplay =
                            !carInsuranceStatusRaw || carInsuranceStatusRaw === 'EMPTY_OR_ERROR'
                              ? '-'
                              : carInsuranceStatusRaw
                          const dentalVisitCount = toIntDashboard(fiveYears.dentalVisitCount, 0)

                          const card = 'rounded-lg border border-slate-200/80 bg-white px-3 py-2.5 shadow-sm'
                          const titleCls = 'text-xs font-semibold text-slate-600'
                          const subCls = 'mt-0.5 text-[10px] text-slate-500'
                          const valCls = 'mt-1.5 text-[19px] font-extrabold text-slate-900'
                          const valEm = 'mt-1.5 text-[21px] font-extrabold text-brand-navy'

                          return (
                            <div className="mt-3 space-y-2 text-xs">
                              <div className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-800">
                                조회 기간 {periodFrom} ~ {periodTo}
                              </div>
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                <div className={card}>
                                  <div className={titleCls}>총 진료비</div>
                                  <div className={valEm}>{formatDashboardMoney(cost.totalMedicalCost)}</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>건강보험부담금</div>
                                  <div className={subCls}>공통 집계 기준</div>
                                  <div className={valCls}>{formatDashboardMoney(cost.insuranceBenefit)}</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>본인부담금</div>
                                  <div className={subCls}>공통 집계 기준</div>
                                  <div className={valCls}>{formatDashboardMoney(cost.outOfPocket)}</div>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div className={card}>
                                  <div className={titleCls}>자동차보험 입원</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{carInpatient === 0 ? '없음' : `${carInpatient}회`}</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>자동차보험 통원</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{carOutpatient === 0 ? '없음' : `${carOutpatient}회`}</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>3개월 통원·치료력</div>
                                  <div className={subCls}>최근 3개월</div>
                                  <div className={valCls}>{treat3m}회</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>3개월 약물력</div>
                                  <div className={subCls}>최근 3개월</div>
                                  <div className={valCls}>{drug3m}회</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>처치 및 수술내역</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{surgeryCount}회</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>입원내역</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{inpatientCount}회</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>동일상병 7회 치료내역</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{treat7Count}상병</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>동일상병 30일 투약내역</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{drug30Count}상병</div>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div className={card}>
                                  <div className={titleCls}>11대 중대질병</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{major11Count}건</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>6대 중대질병</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{major6Count}건</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>치아 관련 내역</div>
                                  <div className={subCls}>5년</div>
                                  <div className={valCls}>{dentalVisitCount}건</div>
                                </div>
                                <div className={card}>
                                  <div className={titleCls}>자동차보험 상태</div>
                                  <div className={subCls}>연동 상태</div>
                                  <div className={valCls}>{carInsuranceStatusDisplay}</div>
                                </div>
                              </div>
                              <p className="text-[11px] text-slate-500">
                                요약 정보는 공통 dashboard 집계 기준으로 표시됩니다. 기존 리포트는 일부 값이 보수적으로 보일 수 있습니다.
                              </p>
                            </div>
                          )
                        })()}
                        {resultTab === 'summary' && !resolvedHealthDashboard && (
                          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            요약 정보를 구성할 수 없습니다. summary_json에 profile이 없거나 파싱에 실패했을 수 있습니다.
                          </div>
                        )}

                        {/* 1. 숨은보상찾기 */}
                        {resultTab === 'hidden' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary
                                if (hiddenCompFromSummary) {
                                  const key =
                                    hiddenSubTab === '자동차부상'
                                      ? 'carInjury'
                                      : hiddenSubTab === '진단비'
                                      ? 'diagnosisBenefit'
                                      : hiddenSubTab === '수술·시술'
                                      ? 'surgeryProcedure'
                                      : hiddenSubTab === '후유장해'
                                      ? 'disability'
                                      : hiddenSubTab === '치아'
                                      ? 'dental'
                                      : 'etc'
                                  const serverList = hiddenCompFromSummary[key]
                                  // 서버가 빈 배열을 내려주면 "없음"이 정상인데, 기존엔 fallback 되어 서브탭 필터가 깨졌음
                                  if (Array.isArray(serverList)) {
                                    filtered = serverList
                                  }
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                              {['자동차부상', '진단비', '수술·시술', '후유장해', '치아', '기타'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    hiddenSubTab === label
                                      ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setHiddenSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                                      {renderPager(totalPages, currentPage, { className: 'justify-end' })}
                                    </div>
                                    <div>
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showHiddenGuide: true,
                                          detailContext: hiddenSubTab === '수술·시술' ? 'HIDDEN_SURGERY' : 'ALL',
                                        }),
                                      )}
                                    </div>
                                    {renderPager(totalPages, currentPage)}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}


                        {/* 2. 진료내역 */}
                        {resultTab === 'treatments' && (
                          <div className="mt-3 space-y-3 text-xs">
                            {(() => {
                              const byPeriod = filterByPeriodLabel(episodesFromSummary, treatmentSubTab)
                              const filtered = filterByTreatmentSource(byPeriod, treatmentSourceSubTab)
                              const { items, totalPages, currentPage } = paginated(filtered)
                              return (
                                <>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                              {['1개월', '3개월', '1년', '3년', '5년'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    treatmentSubTab === label
                                      ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setTreatmentSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                                      {renderPager(totalPages, currentPage, { className: 'justify-end' })}
                                    </div>
                            <div className="flex flex-wrap gap-2">
                              {['전체', '자동차보험'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    treatmentSourceSubTab === label
                                      ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setTreatmentSourceSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-slate-600">
                              선택한 기간 내에 발생한 모든 진료 내역을 기간 기준으로만 나누어 보여줍니다.
                            </p>
                                  <div>{items.map((item) => renderTreatmentCard(item))}</div>
                                  {renderPager(totalPages, currentPage)}
                                </>
                              )
                            })()}
                          </div>
                        )}

                        {/* 2-1. 모든투약내역 */}
                        {resultTab === 'drugs' && (
                          <div className="mt-3 space-y-3 text-xs">

                            {(() => {
                              const key = drugSubTab === '3개월' ? '3M' : drugSubTab === '1년' ? '1Y' : drugSubTab === '3년' ? '3Y' : '5Y'
                              const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[key]
                              const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodesFromSummary

                              const dateToCodes = new Map()
                              const codeToName = new Map()
                              for (const e of rawList) {
                                if (!e || e.source !== 'BASIC_TREAT') continue
                                const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
                                const code = normalizeDiagnosisCodeForDisplay(e.diagnosisCode)
                                if (!day || !code) continue
                                if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
                                dateToCodes.get(day).add(code)
                                const name = normalizeDiagnosisNameForDisplay(e.diagnosisName)
                                if (!codeToName.has(code) && name) codeToName.set(code, name)
                              }

                              const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
                              const deduped = dedupeAndLinkDrugsPharmacyFirst(drugRaw)

                              const diagGroups = new Map()
                              const unassignedDrugGroups = new Map()
                              for (const line of deduped) {
                                const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
                                const explicitCode = normalizeDiagnosisCodeForDisplay(line.diagnosisCode)
                                let groupCode = explicitCode || null
                                if (!groupCode && day && dateToCodes.has(day)) {
                                  const set = dateToCodes.get(day)
                                  if (set && set.size === 1) {
                                    groupCode = Array.from(set)[0]
                                  }
                                }

                                if (groupCode) {
                                  if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
                                  diagGroups.get(groupCode).push(line)
                                } else {
                                  const dk = normalizeDrugKey(line)
                                  if (!unassignedDrugGroups.has(dk)) unassignedDrugGroups.set(dk, [])
                                  unassignedDrugGroups.get(dk).push(line)
                                }
                              }

                              const groupItems = []
                              for (const [code, lines] of diagGroups.entries()) {
                                groupItems.push({
                                  kind: 'DIAG',
                                  diagnosisCode: code,
                                  diagnosisName: codeToName.get(code) || null,
                                  lines,
                                })
                              }
                              // 약품키 그룹(진단코드 없음/복수 진단코드 등)
                              for (const [dk, lines] of unassignedDrugGroups.entries()) {
                                const first = lines[0] || {}
                                groupItems.push({
                                  kind: 'DRUG',
                                  drugKey: dk,
                                  drugName: first.drugName || '-',
                                  lines,
                                })
                              }

                              // 정렬: 진단코드 그룹 먼저, 그 다음 약품키 그룹
                              groupItems.sort((a, b) => {
                                if (a.kind !== b.kind) return a.kind === 'DIAG' ? -1 : 1
                                const ax = a.kind === 'DIAG' ? a.diagnosisCode : a.drugName
                                const bx = b.kind === 'DIAG' ? b.diagnosisCode : b.drugName
                                return String(ax || '').localeCompare(String(bx || ''))
                              })

                              const computeDrugTotalByDayMax = (lines) => {
                                if (!Array.isArray(lines) || lines.length === 0) return null
                                const maxByDay = new Map()
                                let hasAny = false
                                for (const x of lines) {
                                  const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
                                  const v = x?.totalDrugDays
                                  const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
                                  if (!Number.isFinite(n)) continue
                                  const prev = maxByDay.get(day)
                                  maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                                  hasAny = true
                                }
                                if (!hasAny) return null
                                let sum = 0
                                for (const v of maxByDay.values()) sum += Math.max(0, v)
                                return sum
                              }
                              const minMaxDateFromLines = (lines) => {
                                let min = null
                                let max = null
                                for (const x of lines || []) {
                                  const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
                                  if (!day) continue
                                  if (!min || day < min) min = day
                                  if (!max || day > max) max = day
                                }
                                return { min, max }
                              }

                              let summaries = groupItems.map((g, idx) => {
                                const lines = Array.isArray(g.lines) ? g.lines : []
                                const { min, max } = minMaxDateFromLines(lines)
                                const totalDrugDays = computeDrugTotalByDayMax(lines)
                                const diagnosisName =
                                  g.kind === 'DIAG'
                                    ? g.diagnosisName || '진단'
                                    : g.drugName || '투약'
                                const diagnosisCode = g.kind === 'DIAG' ? g.diagnosisCode : null
                                return {
                                  id: `${g.kind}-${idx}`,
                                  groupType: g.kind === 'DIAG' ? 'DRUGS_DIAG' : 'DRUGS_DRUGKEY',
                                  diagnosisName,
                                  diagnosisCode,
                                  startDate: min,
                                  endDate: max,
                                  count: lines.length,
                                  totalDrugDays,
                                  details: lines,
                                }
                              })

                              if (summaries.length === 0) {
                                const serverDrugGroups = byPeriodGroupedDrugsFromSummary && byPeriodGroupedDrugsFromSummary[key]
                                if (Array.isArray(serverDrugGroups)) {
                                  summaries = serverDrugGroups.map((g, idx) => ({
                                    ...(g || {}),
                                    id: g?.id ?? `SERVER-DRUG-${idx}`,
                                    details: Array.isArray(g?.details) ? g.details : [],
                                  }))
                                }
                              }
                              const { items, totalPages, currentPage } = paginated(summaries)

                              return (
                                <>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                              {['3개월', '1년', '3년', '5년'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    drugSubTab === label
                                      ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setDrugSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                                      {renderPager(totalPages, currentPage, { className: 'justify-end' })}
                                    </div>
                            <p className="text-slate-600">
                              투약 내역은 <span className="font-semibold">약국(조제)</span> 기록을 우선으로 중복 제거하며, 처방일과 조제일이 다른 경우
                              <span className="font-semibold"> ±{PRESCRIBE_FILL_LINK_DAYS}일</span> 이내이면 동일 처방으로 연결해 표시합니다.
                            </p>
                            <p className="text-[11px] text-slate-500">
                              * 진단코드 기준 그룹핑은 “일자 기준”으로 매핑합니다. 동일 일자에 복수 진단코드가 존재하면, 일부 투약은 진단코드로 귀속이
                              어려워 “약품 기준”으로 표시될 수 있습니다.
                            </p>
                            <p className="text-[11px] text-slate-500">
                              * 동일 일자에 2개 이상의 질병코드가 있을 경우, 투약 이력이 1개의 질병코드 기준으로만 표시될 수 있습니다.
                            </p>
                                  {items.length === 0 && <p className="text-xs text-slate-500">표시할 투약 내역이 없습니다.</p>}
                                  <div className="space-y-2">
                                    {items.map((g) => (
                                      <div key={g.id} className="rounded border bg-white px-3 py-1.5">
                                        <div className="text-sm font-semibold text-slate-900">
                                          {normalizeDiagnosisCodeForDisplay(g?.diagnosisCode) ? <span className="font-mono font-semibold">{normalizeDiagnosisCodeForDisplay(g?.diagnosisCode)}</span> : <span className="text-slate-500">진단코드 없음</span>}
                                          <span className="text-slate-700"> {g.diagnosisName}</span>
                                        </div>
                                        <div className="mt-1 text-xs text-slate-600">
                                          기간: {formatDate(g.startDate) || '-'} ~ {formatDate(g.endDate) || '-'} / 건수: {g.count}
                                        </div>
                                        <div className="mt-1 text-xs text-slate-600">
                                          총 투약일수: {g.totalDrugDays ?? '-'}일 (일자별 최대 투약일수 합산)
                                        </div>
                                        <div className="mt-2">
                                          <button
                                            type="button"
                                            className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                                            onClick={() => openDetailEpisode(g, { mode: 'drugs', context: 'DRUGS' })}
                                          >
                                            상세내역
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {renderPager(totalPages, currentPage)}
                                </>
                              )
                            })()}
                          </div>
                        )}

                        {/* 3. 건강체 */}
                        {resultTab === 'healthy' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary

                                // 요구사항: [5년내 7일 치료/30일 투약]은 [모든투약내역]의 5년 기준 "총 투약일수(일자별 최대 합산) >= 30"인
                                // "진단코드 그룹" 중, (총 투약일수 >= 30) 또는 (총 치료일수 >= 7) 인 항목을 모두 보여준다.
                                if (healthySubTab.includes('7일') && healthySubTab.includes('투약')) {
                                  const key = '5Y'
                                  const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[key]
                                  const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodesFromSummary

                                  const dateToCodes = new Map()
                                  const codeToName = new Map()
                                  for (const e of rawList) {
                                    if (!e || e.source !== 'BASIC_TREAT') continue
                                    const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
                                    const code = normalizeDiagnosisCodeForDisplay(e.diagnosisCode)
                                    if (!day || !code) continue
                                    if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
                                    dateToCodes.get(day).add(code)
                                    const name = normalizeDiagnosisNameForDisplay(e.diagnosisName)
                                    if (!codeToName.has(code) && name) codeToName.set(code, name)
                                  }

                                  // 치료(외래/입원)일수: BASIC_TREAT 기반으로 동일 진단코드 그룹핑
                                  const treatGroups = new Map()
                                  for (const e of rawList) {
                                    if (!e || e.source !== 'BASIC_TREAT') continue
                                    // 약국(조제) 라인은 치료일수 집계에서 제외
                                    if (isPharmacyLike(e)) continue
                                    const code = normalizeDiagnosisCodeForDisplay(e.diagnosisCode)
                                    if (!code) continue
                                    if (!treatGroups.has(code)) treatGroups.set(code, [])
                                    treatGroups.get(code).push(e)
                                  }

                                  const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
                                  const deduped = dedupeAndLinkDrugsPharmacyFirst(drugRaw)

                                  const diagGroups = new Map()
                                  for (const line of deduped) {
                                    const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
                                    const explicitCode = normalizeDiagnosisCodeForDisplay(line.diagnosisCode)
                                    let groupCode = explicitCode || null
                                    if (!groupCode && day && dateToCodes.has(day)) {
                                      const set = dateToCodes.get(day)
                                      if (set && set.size === 1) groupCode = Array.from(set)[0]
                                    }
                                    if (!groupCode) continue
                                    if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
                                    diagGroups.get(groupCode).push(line)
                                  }

                                  const computeTotalByDayMax = (lines, valueKey) => {
                                    if (!Array.isArray(lines) || lines.length === 0) return null
                                    const maxByDay = new Map()
                                    let hasAny = false
                                    for (const x of lines) {
                                      const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
                                      const v = x?.[valueKey]
                                      const n =
                                        typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
                                      if (!Number.isFinite(n)) continue
                                      const prev = maxByDay.get(day)
                                      maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                                      hasAny = true
                                    }
                                    if (!hasAny) return null
                                    let sum = 0
                                    for (const v of maxByDay.values()) sum += Math.max(0, v)
                                    return sum
                                  }
                                  const minMaxDateFromLines = (lines) => {
                                    let min = null
                                    let max = null
                                    for (const x of lines || []) {
                                      const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
                                      if (!day) continue
                                      if (!min || day < min) min = day
                                      if (!max || day > max) max = day
                                    }
                                    return { min, max }
                                  }

                                  const summaries = []
                                  const allCodes = new Set([
                                    ...Array.from(diagGroups.keys()),
                                    ...Array.from(treatGroups.keys()),
                                  ])
                                  for (const code of allCodes) {
                                    const drugLines = diagGroups.get(code) || []
                                    const treatLines = treatGroups.get(code) || []
                                    const totalDrugDays = computeTotalByDayMax(drugLines, 'totalDrugDays') || 0
                                    const totalTreatmentDays = computeTotalByDayMax(treatLines, 'totalTreatmentDays') || 0
                                    if (totalDrugDays < 30 && totalTreatmentDays < 7) continue

                                    const mergedDetails = [...treatLines, ...drugLines]
                                    const { min, max } = minMaxDateFromLines(mergedDetails)
                                    summaries.push({
                                      id: `DRUG_7_30_5Y-${code}`,
                                      groupType: 'DRUGS_DIAG',
                                      diagnosisCode: code,
                                      diagnosisName: codeToName.get(code) || '진단',
                                      startDate: min,
                                      endDate: max,
                                      count: mergedDetails.length,
                                      totalDrugDays,
                                      totalTreatmentDays,
                                      details: mergedDetails,
                                    })
                                  }
                                  summaries.sort((a, b) => String(a.diagnosisCode || '').localeCompare(String(b.diagnosisCode || '')))
                                  filtered = summaries
                                } else if (healthyViewFromSummary) {
                                  let key = null
                                  if (healthySubTab === '3개월') {
                                    key = '3M'
                                  } else if (healthySubTab === '1년내 검사') {
                                    key = '1Y_CHECK'
                                  } else if (healthySubTab === '5년내 입원/수술') {
                                    key = 'HOSP_5Y'
                                  } else if (healthySubTab === '5년내 11대 중대질병') {
                                    key = 'MAJOR11_5Y'
                                  }
                                  // 서버가 없거나(구버전/부분 생성) 배열이 아니면, "전체 fallback" 대신 빈 목록으로 취급한다.
                                  // 그렇지 않으면(특히 중대질병 탭) 다른 탭(입원/수술)과 동일 리스트가 표시되어 규칙이 섞여 보일 수 있다.
                                  if (key) {
                                    const serverList = healthyViewFromSummary[key]
                                    filtered = Array.isArray(serverList) ? serverList : []
                                  }
                                }
                                // 입원/수술 탭은 "유사 후보"를 제거하고 실제 입원/수술만 노출
                                if (healthySubTab === '5년내 입원/수술') {
                                  filtered = Array.isArray(filtered)
                                    ? filtered.filter((g) => isInpatientOrSurgeryLikeGroup(g) && !isDentalLikeGroup(g))
                                    : []
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap gap-2">
                                {['3개월', '1년내 검사', '5년 내 7일 치료/30일 투약', '5년내 입원/수술', '5년내 11대 중대질병'].map(
                                  (label) => (
                                    <button
                                      key={label}
                                      type="button"
                                      className={`rounded-full border px-3 py-1 text-xs ${
                                        healthySubTab === label
                                          ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                                          : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                      }`}
                                      onClick={() => {
                                        setHealthySubTab(label)
                                        setPage(0)
                                      }}
                                    >
                                      {label}
                                    </button>
                                  ),
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                {renderPager(totalPages, currentPage, { className: 'justify-end' })}
                                <button
                                  type="button"
                                  className="rounded border px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                                  onClick={() => openQuestionnaire('healthy')}
                                >
                                  질문서 보기
                                </button>
                              </div>
                            </div>
                            {healthySubTab === '5년내 11대 중대질병' && (
                              <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
                                11대 중대질병: 암/백혈병/고혈압/협심증/심근경색/심장판막증/간경화/뇌졸중(뇌출혈·뇌경색)/당뇨병/에이즈(HIV)/만성폐질환
                              </p>
                            )}
                            {healthySubTab === '1년내 검사' && (
                              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                진료내역만으로는 추가검사/재검사 여부 파악이 어려워 진료내역 중 ‘검사’ 가 포함된 내역이 조회됩니다.
                              </p>
                            )}
                            {healthySubTab.includes('7일') && healthySubTab.includes('투약') && (
                              <p className="text-[11px] text-slate-500">
                                * 동일 일자에 2개 이상의 질병코드가 있을 경우, 투약 이력이 1개의 질병코드 기준으로만 표시될 수 있습니다.
                              </p>
                            )}
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                      <p className="text-slate-600">
                                        표준체 질문서 기준으로 진료이력을 정리해드립니다.
                                      </p></div>
                                    <div>
                                      {items.length === 0 && (
                                        <p className="text-xs text-slate-500">표시할 내역이 없습니다.</p>
                                      )}
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showDrugExtra: healthySubTab.includes('투약'),
                                          showInpatientExtra: healthySubTab.includes('입원/수술'),
                                          detailContext:
                                            healthySubTab === '1년내 검사'
                                              ? 'EXAM'
                                              : healthySubTab.includes('입원/수술')
                                              ? 'INHOSP_SURGERY'
                                              : healthySubTab.includes('7일')
                                              ? 'TREATMENTS'
                                              : 'ALL',
                                        }),
                                      )}
                                    </div>
                                    {renderPager(totalPages, currentPage)}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}

                        {/* 4. 유병자 */}
                        {resultTab === 'substandard' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary
                                if (substandardViewFromSummary) {
                                  let key = null
                                  if (substandardSubTab === '3개월') {
                                    key = 'recentHighRisk3M'
                                  } else if (substandardSubTab === '1년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike1Y'
                                  } else if (substandardSubTab === '2년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike2Y'
                                  } else if (substandardSubTab === '3년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike3Y'
                                  } else if (substandardSubTab === '4년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike4Y'
                                  } else if (substandardSubTab === '5년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike5Y'
                                  } else if (substandardSubTab === '5년내 6대 중대질병') {
                                    key = 'MAJOR6_5Y'
                                  }
                                  // 서버가 없거나(구버전/부분 생성) 배열이 아니면, "전체 fallback" 대신 빈 목록으로 취급한다.
                                  if (key) {
                                    const serverList = substandardViewFromSummary[key]
                                    filtered = Array.isArray(serverList) ? serverList : []
                                  }
                                }
                                // 유병자 입원/수술 탭도 "유사 후보" 제거(실제 입원/수술만)
                                if (substandardSubTab.includes('입원/수술')) {
                                  filtered = Array.isArray(filtered)
                                    ? filtered.filter((g) => isInpatientOrSurgeryLikeGroup(g) && !isDentalLikeGroup(g))
                                    : []
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap gap-2">
                              {[
                                '3개월',
                                '1년내 입원/수술',
                                '2년내 입원/수술',
                                '3년내 입원/수술',
                                '4년내 입원/수술',
                                '5년내 입원/수술',
                                '5년내 6대 중대질병',
                              ].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    substandardSubTab === label
                                      ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setSubstandardSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                {renderPager(totalPages, currentPage, { className: 'justify-end' })}
                                <button
                                  type="button"
                                  className="rounded border px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                                  onClick={() => openQuestionnaire('substandard')}
                                >
                                  질문서 보기
                                </button>
                              </div>
                            </div>
                            {substandardSubTab === '5년내 6대 중대질병' && (
                              <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
                                6대 중대질병: 암/뇌졸중/심근경색/협심증/심장판막증/간경화
                              </p>
                            )}
                            {substandardSubTab === '3개월' && (
                              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                               3개월 내 진료내역은 건강보험심사평가원의 심사기간이 약 3개월 소요되므로 일부 내역이 표시되지 않을 수 있으니 고객과 면담시 반드시 확인이 필요합니다.
                              </p>
                            )}
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                      <p className="text-slate-600">
                                        유병자 질문서 기준으로 진료이력을 정리해드립니다.
                                      </p></div>
                                    <div>
                                      {items.length === 0 && (
                                        <p className="text-xs text-slate-500">표시할 내역이 없습니다.</p>
                                      )}
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showDrugExtra: substandardSubTab.includes('투약'),
                                          showInpatientExtra: substandardSubTab.includes('입원/수술'),
                                          detailContext: substandardSubTab.includes('입원/수술') ? 'INHOSP_SURGERY' : 'ALL',
                                        }),
                                      )}
                                    </div>
                                    {renderPager(totalPages, currentPage)}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}

                        {/* 6. 치아 */}
                        {resultTab === 'dental' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary
                                if (dentalViewFromSummary) {
                                  let key = null
                                  if (dentalSubTab === '틀니') {
                                    key = 'denture'
                                  } else if (dentalSubTab === '1년 내 충치') {
                                    key = 'within1Y'
                                  } else if (dentalSubTab === '5년 내 치주질환') {
                                    key = 'within5Y'
                                  }
                                  // 서버가 빈 배열을 내려주면 "없음"이 정상인데, 기존엔 전체 episodes로 fallback 되어 일반진단이 섞였음
                                  if (key && Array.isArray(dentalViewFromSummary[key])) {
                                    filtered = dentalViewFromSummary[key]
                                  }
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                              {['틀니', '1년 내 충치', '5년 내 치주질환'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    dentalSubTab === label
                                      ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setDentalSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                                      {renderPager(totalPages, currentPage, { className: 'justify-end' })}
                                    </div>
                            <p className="text-slate-600">
                              틀니, 스케일링, 보철 등 치과 관련 진료내역을 기간·유형별로 나누어 보여주며, 치아고지명/세부명을 함께
                              확인할 수 있습니다.
                            </p>
                                    <div>
                                      {items.length === 0 && (
                                        <p className="text-xs text-slate-500">표시할 내역이 없습니다.</p>
                                      )}
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showDentalExtra: true,
                                        }),
                                      )}
                                    </div>
                                    {renderPager(totalPages, currentPage)}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {filteredJobs.length === 0 && (
                  <p className="text-xs text-slate-500">표시할 분석 내역이 없습니다.</p>
                )}
              </div>
              <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={reportsPage === 0}
                  onClick={() => setReportsPage((p) => Math.max(0, p - 1))}
                >
                  이전
                </button>
                <span>
                  {reportsPage + 1} / {jobsQuery.data?.totalPages || 1}
                  {typeof jobsQuery.data?.totalElements === 'number' ? ` (총 ${jobsQuery.data.totalElements}건)` : ''}
                </span>
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={!jobsHasNext || (typeof jobsQuery.data?.totalPages === 'number' && reportsPage >= (jobsQuery.data.totalPages - 1))}
                  onClick={() => setReportsPage((p) => p + 1)}
                >
                  다음
                </button>
              </div>

            </>
          )}
        </section>
      )}

      {/* 3) 고객 목록 탭 */}
      {activeTab === 'customers' && (
        <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {isOperator && (
                <>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={operatorLoadMode}
                    onChange={(e) => {
                      const v = e.target.value || 'MINE'
                      if (v === 'ALL') {
                        const ok = window.confirm('전체보기는 전체 분석/고객 데이터를 불러오므로 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                        if (!ok) return
                        setOperatorOrgId('')
                      } else if (v === 'MINE') {
                        setOperatorOrgId('')
                      }
                      setOperatorLoadMode(v)
                    }}
                    title="운영자: 조회 모드"
                  >
                    <option value="MINE">내것만 보기</option>
                    <option value="BY_ORG">조직별</option>
                    <option value="ALL">전체보기</option>
                  </select>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={operatorOrgId}
                    disabled={operatorLoadMode !== 'BY_ORG' || operatorOrgLoading}
                    onChange={(e) => {
                      setOperatorOrgId(e.target.value)
                    }}
                    title="운영자: 조직 선택"
                  >
                    <option value="">{operatorOrgLoading ? '조직 불러오는 중...' : '조직 선택(필수)'}</option>
                    {operatorOrgOptions.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </>
              )}
              {isGaScopeAdmin && (
                <>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={gaLoadMode}
                    onChange={(e) => {
                      const v = e.target.value || 'MINE'
                      if (v === 'MINE') {
                        setGaOrgId('')
                      } else if (v === 'ALL') {
                        const ok = window.confirm('전체보기는 조회 범위가 넓어 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                        if (!ok) return
                        setGaOrgId('')
                      }
                      setGaLoadMode(v)
                    }}
                    title="GA: 조회 범위"
                  >
                    <option value="MINE">내것만 보기</option>
                    <option value="BY_ORG">조직별</option>
                    <option value="ALL">전체보기</option>
                  </select>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={gaOrgId}
                    disabled={gaLoadMode !== 'BY_ORG' || gaOrgLoading}
                    onChange={(e) => setGaOrgId(e.target.value)}
                    title="GA: 조직 선택"
                  >
                    <option value="">{gaOrgLoading ? '조직 불러오는 중...' : '내 조직(전체)'}</option>
                    {gaOrgOptions.map(o => (
                      <option key={o.id} value={o.id}>{o.name} (ID {o.id})</option>
                    ))}
                  </select>
                </>
              )}
              <input
                type="text"
                className="rounded border px-2 py-1 text-sm"
                placeholder="고객명/연락처 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => customersQuery.refetch()}
              >
                새로고침
              </button>
            </div>
          </div>

          {isOperator && operatorLoadMode === 'BY_ORG' && !operatorOrgId && (
            <p className="text-xs text-slate-500">운영자는 먼저 조직을 선택한 뒤 목록이 조회됩니다.</p>
          )}
          {isGaScopeAdmin && gaLoadMode === 'BY_ORG' && gaOrgLoading && (
            <p className="text-xs text-slate-500">조직 옵션을 불러오는 중입니다...</p>
          )}

          {customersQuery.isLoading ? (
            <p className="text-xs text-slate-500">고객 목록을 불러오는 중입니다...</p>
          ) : customersQuery.isError ? (
            <p className="text-xs text-red-500">고객 목록을 불러오지 못했습니다.</p>
          ) : (
            <>
            <div className="space-y-2 text-sm">
              {filteredCustomers.map((c) => (
                <div key={healthCustomerRowKey(c)} className="rounded border px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">
                        {c.customerName ? `고객명: ${c.customerName}` : '고객 (이름 미상)'}
                      </div>
                      <div className="text-xs text-slate-500">연락처: {c.phoneNumber || '-'}</div>
                      {c.customerRefId != null && (
                        <div className="text-[11px] text-emerald-700 mt-0.5">고객관리에 등록된 고객입니다.</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-xs text-slate-500">
                        메모: {notes[c.customerName || ''] ? notes[c.customerName || ''] : '등록된 메모가 없습니다.'}
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                          onClick={() => handleEditNote(c.customerName || '')}
                        >
                          메모
                        </button>
                        {!c.customerRefId && (
                          <button
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs border-emerald-700 text-emerald-900 hover:bg-emerald-50 disabled:opacity-50"
                            disabled={healthCrmRegisterKey === healthCustomerRowKey(c)}
                            onClick={() => registerHealthCustomerToMgmt(c)}
                          >
                            {healthCrmRegisterKey === healthCustomerRowKey(c) ? '등록 중…' : '고객등록'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="rounded border px-2 py-0.5 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                          onClick={() => handleGoToReportsForCustomer(c.customerName || '')}
                        >
                          분석목록조회
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredCustomers.length === 0 && (
                <p className="text-xs text-slate-500">표시할 고객이 없습니다.</p>
              )}
            </div>
              <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={customersPage === 0}
                  onClick={() => setCustomersPage((p) => Math.max(0, p - 1))}
                >
                  이전
                </button>
                <span>
                  {customersPage + 1} / {customersQuery.data?.totalPages || 1}
                  {typeof customersQuery.data?.totalElements === 'number' ? ` (총 ${customersQuery.data.totalElements}건)` : ''}
                </span>
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={!customersHasNext || (typeof customersQuery.data?.totalPages === 'number' && customersPage >= (customersQuery.data.totalPages - 1))}
                  onClick={() => setCustomersPage((p) => p + 1)}
                >
                  다음
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* 상세 내역 팝업 */}
      {detailEpisode && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 overscroll-contain"
          onMouseDown={(e) => {
            // 배경 클릭 시 닫기 (팝업 내부 클릭은 무시)
            if (e.target === e.currentTarget) closeDetailEpisode()
          }}
        >
          {/* 팝업 자체는 고정 높이/비스크롤. 스크롤은 하단 세부내역(details) 리스트만. */}
          <div
            className="w-full max-w-lg rounded-lg bg-white shadow-lg p-4 text-sm max-h-[80vh] overflow-hidden flex flex-col overscroll-contain"
            onWheel={(e) => {
              // 모달 헤더/상단 필드 영역에서 스크롤해도 "세부내역 리스트"만 스크롤되도록 유도
              const target = e.target
              const overDetails =
                target && typeof target.closest === 'function'
                  ? target.closest('[data-detail-scroll]')
                  : null
              if (overDetails) return
              const el = detailListScrollRef.current
              if (!el) return
              e.preventDefault()
              e.stopPropagation()
              el.scrollTop += e.deltaY
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-slate-900">진료/투약 상세 내역</div>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                onClick={closeDetailEpisode}
                aria-label="상세내역 닫기"
              >
                <span className="text-slate-500">✕</span>
                <span>닫기</span>
              </button>
            </div>
            {/* NOTE: 이 영역이 "높이를 가진 flex 컨테이너"여야 하단 details 리스트가 flex로 남은 높이를 받아 스크롤된다. */}
            <div className="text-xs flex flex-1 min-h-0 flex-col overflow-hidden">
              {(() => {
                const keysInEpisode = detailEpisode ? Object.keys(detailEpisode) : []
                const allowedKeys = keysInEpisode.filter((k) => Boolean(fieldLabels[k]))
                const ordered = [
                  ...detailFieldOrder.filter((k) => allowedKeys.includes(k)),
                  ...allowedKeys.filter((k) => !detailFieldOrder.includes(k)),
                ]
                const nonDetailKeys = ordered
                  .filter((k) => k !== 'details')
                  .filter((k) => (k === 'hiddenGuide' ? detailShowHiddenGuide : true))
                  .filter((k) => !isEmptyDetailValue(detailEpisode[k]))

                const detailsValue = detailEpisode?.details
                const hasDetails = Array.isArray(detailsValue) && detailsValue.length > 0

                // (중요) 일부 summary/뷰 데이터는 details를 포함하지 않을 수 있다.
                // 이 경우 팝업에서 "세부내역"이 아예 안 보이는 문제가 생겨,
                // rawEpisodesFromSummary에서 기간/진단코드(가능하면 기관까지)로 역추정해 보여준다.
                const buildFallbackDetails = () => {
                  const raw = Array.isArray(rawEpisodesFromSummary) ? rawEpisodesFromSummary : []
                  if (!detailEpisode || raw.length === 0) return []

                  const ep = detailEpisode
                  const epStart0 = normalizeDateDigitsSafe(ep?.startDate || ep?.endDate)
                  const epEnd0 = normalizeDateDigitsSafe(ep?.endDate || ep?.startDate)
                  let epStart = epStart0
                  let epEnd = epEnd0
                  if (epStart && epEnd && epStart > epEnd) {
                    const tmp = epStart
                    epStart = epEnd
                    epEnd = tmp
                  }

                  const epCode = normalizeDiagnosisCodeForDisplay(ep?.diagnosisCode)
                  const epName = normalizeDiagnosisNameForDisplay(ep?.diagnosisName)
                  const epHosp = (ep?.hospitalName || '').toString().trim()
                  const requireHosp = ep?.groupType === 'HOSPITAL' || ep?.groupType === 'HOSPITAL_DX'

                  const inRange = (day) => {
                    if (!day) return false
                    if (epStart && day < epStart) return false
                    if (epEnd && day > epEnd) return false
                    return true
                  }

                  const nameLike = (a, b) => {
                    const x = (a || '').toString().trim()
                    const y = (b || '').toString().trim()
                    if (!x || !y) return false
                    return x.includes(y) || y.includes(x)
                  }

                  const out = []
                  for (const x of raw) {
                    if (!x) continue
                    const src = String(x?.source || '')
                    // 팝업은 "진료/투약 상세"이므로, 기본/상세 진료 + 투약 라인을 우선 대상으로 한다.
                    if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT') || src === 'PRESCRIBE_DRUG')) continue
                    const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
                    if (!inRange(day)) continue

                    if (epCode) {
                      const code = normalizeDiagnosisCodeForDisplay(x?.diagnosisCode)
                      if (code !== epCode) continue
                    } else if (epName) {
                      // 진단코드가 없으면 이름 기반으로만 좁힌다(완전 일치보다 부분일치 우선)
                      const name = normalizeDiagnosisNameForDisplay(x?.diagnosisName)
                      if (name && !nameLike(name, epName)) continue
                    }

                    if (requireHosp && epHosp) {
                      const hosp = (x?.hospitalName || '').toString().trim()
                      if (hosp && hosp !== epHosp) continue
                    }

                    out.push(x)
                    if (out.length >= 1200) break
                  }

                  return out
                }

                const effectiveDetailsValue = hasDetails ? detailsValue : buildFallbackDetails()
                const hasEffectiveDetails = Array.isArray(effectiveDetailsValue) && effectiveDetailsValue.length > 0
                const sumFromDetails = (key) => {
                  if (!hasEffectiveDetails) return null
                  // totalDrugDays는 단순 합산하면 과대계상될 수 있어,
                  // "일자별 최대 투약일수"만 합산한다.
                  if (key === 'totalDrugDays') {
                    const maxByDay = new Map()
                    let hasAny = false
                    for (const d of effectiveDetailsValue) {
                      const v = d ? d[key] : null
                      const n =
                        typeof v === 'number'
                          ? v
                          : v !== null && v !== undefined && String(v).trim() !== ''
                          ? Number(v)
                          : null
                      if (!Number.isFinite(n)) continue
                      const day = normalizeDateDigitsSafe(d?.startDate || d?.endDate) || '__NO_DAY__'
                      const prev = maxByDay.get(day)
                      maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                      hasAny = true
                    }
                    if (!hasAny) return null
                    let sum = 0
                    for (const x of maxByDay.values()) sum += Math.max(0, x)
                    return sum
                  }

                  // totalTreatmentDays: 약국(조제) 라인은 치료일수 집계에서 제외 + 일자별 최대치만 합산
                  if (key === 'totalTreatmentDays') {
                    const maxByDay = new Map()
                    let hasAny = false
                    for (const d of effectiveDetailsValue) {
                      if (isPharmacyLike(d)) continue
                      const v = d ? d[key] : null
                      const n =
                        typeof v === 'number'
                          ? v
                          : v !== null && v !== undefined && String(v).trim() !== ''
                          ? Number(v)
                          : null
                      if (!Number.isFinite(n)) continue
                      const day = normalizeDateDigitsSafe(d?.startDate || d?.endDate) || '__NO_DAY__'
                      const prev = maxByDay.get(day)
                      maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                      hasAny = true
                    }
                    if (!hasAny) return null
                    let sum = 0
                    for (const x of maxByDay.values()) sum += Math.max(0, x)
                    return sum
                  }

                  // 그 외는 단순 합산
                  let sum = 0
                  let hasAny = false
                  for (const d of effectiveDetailsValue) {
                    const v = d ? d[key] : null
                    const n =
                      typeof v === 'number'
                        ? v
                        : v !== null && v !== undefined && String(v).trim() !== ''
                        ? Number(v)
                        : null
                    if (Number.isFinite(n)) {
                      sum += Math.max(0, n)
                      hasAny = true
                    }
                  }
                  return hasAny ? sum : null
                }
                const popupSumTreatmentDays = sumFromDetails('totalTreatmentDays')
                const popupSumDrugDays = sumFromDetails('totalDrugDays')
                const popupEncounterCount = (() => {
                  if (!hasEffectiveDetails) return null
                  // 요약 카드와 동일 기준으로 계산(약국 제외, 기관+일자 dedupe, 필요 시 raw fallback)
                  const list = Array.isArray(effectiveDetailsValue) ? effectiveDetailsValue : []
                  if (list.length === 0) return null

                  const dayOf = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate)
                  const isPharm = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
                  const norm = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
                  const hospKey = (x) => {
                    const name = norm(x?.hospitalName || '')
                    const code = norm(x?.hospitalCode || '')
                    return name ? name : (code ? code : '_')
                  }

                  const visitSet = new Set()
                  const days = new Set()
                  for (const x of list) {
                    const d = dayOf(x)
                    if (d) days.add(d)
                    if (isPharm(x)) continue
                    if (!d) continue
                    visitSet.add(`${d}|${hospKey(x)}`)
                  }
                  if (visitSet.size > 0) return visitSet.size

                  if (!Array.isArray(rawEpisodesFromSummary) || rawEpisodesFromSummary.length === 0) return 0
                  for (const d of days) {
                    for (const x of rawEpisodesFromSummary) {
                      const src = String(x?.source || '')
                      if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT'))) continue
                      if (dayOf(x) !== d) continue
                      if (isPharm(x)) continue
                      visitSet.add(`${d}|${hospKey(x)}`)
                    }
                  }
                  return visitSet.size
                })()

                const derivedInpatientAssume1Info = (() => {
                  // BE에서 원본 종료일/기간 정보가 없어 endDate를 파생한 케이스를 내려준다.
                  // 그 중 visitDays가 0/누락이라 1일로 추정한 경우만 사용자에게 안내한다.
                  const reasons = new Set()
                  const pushReason = (x) => {
                    const r = String(x?.endDateDerivedFrom || '')
                    if (!r) return
                    if (r.includes('ASSUME_1')) reasons.add(r)
                  }
                  pushReason(detailEpisode)
                  if (Array.isArray(effectiveDetailsValue)) {
                    for (const x of effectiveDetailsValue) pushReason(x)
                  }
                  return {
                    show: reasons.size > 0,
                    reasons: Array.from(reasons.values()),
                  }
                })()

                return (
                  // NOTE: 부모가 flex 컨테이너이므로 flex-1로 남은 높이를 채운다.
                  <div className="min-h-0 flex flex-1 flex-col overflow-hidden">
                    {/* 상단 필드 영역(고정, 비스크롤) */}
                    <div className="space-y-2 shrink-0">
                      {nonDetailKeys.map((key) => {
                        // UX: "총 진료일/총 투약일"은 중복 노출 대신, 기존 필드의 표시값을 합산값으로 치환한다.
                        const value =
                          key === 'count'
                            ? (popupEncounterCount ?? detailEpisode?.encounterCount ?? detailEpisode?.count)
                            : key === 'totalTreatmentDays' && popupSumTreatmentDays !== null
                            ? popupSumTreatmentDays
                            : key === 'totalDrugDays' && popupSumDrugDays !== null
                            ? popupSumDrugDays
                            : detailEpisode[key]
                        const label = fieldLabels[key]
                        if (!label) return null // 영어 키/라벨 노출 방지
                        const displayValue = formatDetailValue(key, value)
                        if (isEmptyDetailValue(displayValue)) return null
                        return (
                          <div key={key} className="flex">
                            <div className="w-32 font-semibold text-slate-700">{label}</div>
                            <div className="flex-1 text-slate-600 break-all">{displayValue}</div>
                          </div>
                        )
                      })}
                    </div>
{/*
                    {derivedInpatientAssume1Info.show && (
                      <div className="mt-2 shrink-0 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                        원본 데이터에 퇴원일/기간 정보가 없어 <span className="font-semibold">입원기간을 1일로 추정</span>한 내역이 포함되어 있습니다.
                        <div className="mt-1 text-[11px] text-amber-700">
                          (추정 사유: {derivedInpatientAssume1Info.reasons.join(', ')})
                        </div>
                      </div>
                    )} */}

                    {/* 검사 탭: 영상진단 포함 토글(기본 OFF) */}
                    {String(detailEpisodeContext || '').toUpperCase() === 'EXAM' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={examIncludeImaging}
                            onChange={(e) => setExamIncludeImaging(Boolean(e.target.checked))}
                          />
                          영상진단 포함해서 보기
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 검사료/‘검사’ 키워드만 표시합니다. (X-ray/CT/MRI 등은 옵션)
                        </div>
                      </div>
                    )}

                    {/* 입원/수술 탭: 수술이 없을 때만(또는 필요 시) 입원 처치/치료 라인까지 확장 표시 */}
                    {String(detailEpisodeContext || '').toUpperCase() === 'INHOSP_SURGERY' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={inhospIncludeNonSurgery}
                            onChange={(e) => setInhospIncludeNonSurgery(Boolean(e.target.checked))}
                          />
                          수술 외 처치/치료/재료까지 모두 보기(참고용)
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 “수술명(봉합술/성형술/절제술 등)”만 표시합니다. 켜면 입원 중 처치/치료/재료 라인도 함께 표시됩니다.
                        </div>
                      </div>
                    )}

                    {/* 숨은보상(수술/시술) 탭: 처치 및 수술 전체 보기 토글 */}
                    {String(detailEpisodeContext || '').toUpperCase() === 'HIDDEN_SURGERY' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={hiddenIncludeAllProcedures}
                            onChange={(e) => setHiddenIncludeAllProcedures(Boolean(e.target.checked))}
                          />
                          처치 및 수술 전체 보기(재료/요법 포함, 참고용)
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 “수술명(봉합술/성형술/절제술 등)” 위주로만 표시합니다.
                        </div>
                      </div>
                    )}

                    {/* 투약(상세팝업): 모든 정보 보기 토글
                        - DRUGS 탭 뿐 아니라, '5년내 7일 치료/30일 투약' 등에서 상세팝업이 투약 모드로 열리는 경우에도 노출되어야 한다.
                    */}
                    {detailEpisodeMode === 'drugs' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={drugsShowAllInfo}
                            onChange={(e) => setDrugsShowAllInfo(Boolean(e.target.checked))}
                          />
                          조제료/수출명 포함 전체 보기(참고용)
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 실제 투약 약품명 위주로만 표시하며, 약국/조제료 성격 라인은 숨깁니다.
                        </div>
                      </div>
                    )}

                    {/* 하단 세부내역(일자별 그룹) 리스트만 스크롤 */}
                    {hasEffectiveDetails && (
                      <div className="mt-3 flex flex-1 min-h-0 flex-col overflow-hidden">
                        <div className="mb-2 font-semibold text-slate-700">{fieldLabels.details}</div>
                        <div
                          ref={detailListScrollRef}
                          data-detail-scroll
                          className="min-h-0 flex-1 overflow-y-auto pr-1 space-y-2 overscroll-contain"
                        >
                          {(() => {
                            const dayKey = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
                            const groups = new Map()
                            for (const x of effectiveDetailsValue) {
                              const k = dayKey(x)
                              if (!groups.has(k)) groups.set(k, [])
                              groups.get(k).push(x)
                            }
                            const days = Array.from(groups.keys()).sort((a, b) => {
                              if (a === '__NO_DAY__') return 1
                              if (b === '__NO_DAY__') return -1
                              return b.localeCompare(a)
                            })

                            return (
                              <div className="space-y-3">
                                {days.map((d) => {
                                  const lines = groups.get(d) || []
                                  const detailTreats = lines.filter((x) => x?.source === 'DETAIL_TREAT' || String(x?.source || '').includes('DETAIL_TREAT'))
                                  const basics = lines.filter((x) => x?.source === 'BASIC_TREAT' || String(x?.source || '').includes('BASIC_TREAT'))

                                  // (보강) details에 DETAIL_TREAT가 누락되는 케이스가 있어, allEpisodesRaw에서 같은 날짜의 DETAIL_TREAT를 합쳐서 보여준다.
                                  // - 진단코드 없는 처치/행위 행은 byPeriodGroupedDiagnosis.details에 빠지기 쉬움(CODEF resDetailTreatList 특성)
                                  const mergedDetailTreats = (() => {
                                    const base = Array.isArray(detailTreats) ? detailTreats : []
                                    if (!Array.isArray(rawEpisodesFromSummary) || rawEpisodesFromSummary.length === 0) return base
                                    const dayDigits = d === '__NO_DAY__' ? null : d
                                    if (!dayDigits) return base

                                    const keyOf = (x) => {
                                      const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || ''
                                      const org = (x?.hospitalCode || x?.hospitalName || '').toString().trim()
                                      const t = (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                      const cn = (x?.codeName || '').toString().trim()
                                      return `${day}|${org}|${t}|${cn}`
                                    }
                                    const seen = new Set(base.map(keyOf))

                                    const extra = rawEpisodesFromSummary
                                      .filter((x) => String(x?.source || '').includes('DETAIL_TREAT'))
                                      .filter((x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate) === dayDigits)
                                      .filter((x) => !seen.has(keyOf(x)))

                                    return [...base, ...extra]
                                  })()

                                  const titleDate = d === '__NO_DAY__' ? '일자 정보 없음' : (formatDate(d) || d)
                                  const isCollapsed = collapsedDetailDays.has(d)

                                  // NOTE: 하루 단위에서 "세부진료/투약"을 먼저 보여주고, 필요하면 기본진료는 하단에 붙인다.
                                  // (개선) 팝업 표시 정책
                                  // - 진료내역 모드: 병원 방문은 "병원 카드 1개 + (진료형태|코드명) 목록", 약국은 방문 요약만(조제료등 목록 숨김)
                                  // - 투약내역 모드: 병원 방문은 동일하게 합산 표시, 약국/병원 투약은 "투약 내역"만 목록화, 조제료등 목록 숨김
                                  const isPharmacyLine = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
                                  // (중요) hospitalCode 유무에 따라 동일 병원이 분리되는 문제 방지:
                                  // 병원명 우선(정규화)로 key를 만들고, 병원코드는 fallback으로만 사용한다.
                                  const normalizeOrgKey = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
                                  const hospitalKey = (x) => {
                                    const name = normalizeOrgKey(x?.hospitalName)
                                    if (name) return name
                                    const code = normalizeOrgKey(x?.hospitalCode)
                                    return code
                                  }
                                  const normalizeSlash = (s) => {
                                    if (!s) return ''
                                    const parts = String(s)
                                      .split('/')
                                      .map((p) => p.trim())
                                      .filter(Boolean)
                                    const out = []
                                    for (const p of parts) {
                                      if (out.length === 0 || out[out.length - 1] !== p) out.push(p)
                                    }
                                    return out.join(' / ')
                                  }
                                  const normalizeDetailContext = (ctx) => {
                                    if (!ctx) return 'ALL'
                                    const c = String(ctx).toUpperCase()
                                    return c === 'EXAM' ||
                                      c === 'INHOSP_SURGERY' ||
                                      c === 'HIDDEN_SURGERY' ||
                                      c === 'TREATMENTS' ||
                                      c === 'DRUGS'
                                      ? c
                                      : 'ALL'
                                  }
                                  /** 세부내역(codeName)이 "수술 전/후 …" 서술만인 경우 수술 탭에서 제외 */
                                  const isPrePostSurgeryNarrativeCodeName = (cn) => {
                                    const t = (cn || '').toString().trim()
                                    return t.length > 0 && /^수술\s*[전후]/.test(t)
                                  }
                                  const ctx = normalizeDetailContext(detailEpisodeContext)

                                  const isExamLine = (x) => {
                                    const tt = (x?.treatType || x?.treatTypeDetail || '').toString()
                                    const cn = (x?.codeName || '').toString()
                                    // "검사" 탭은 검사료/검사 키워드만 노출 (영상진단은 기본 제외, 토글 ON이면 포함)
                                    if (tt.includes('검사료') || cn.includes('검사')) return true
                                    if (examIncludeImaging && (tt.includes('영상진단') || tt.includes('방사선치료') || tt.includes('영상저장'))) return true
                                    return false
                                  }
                                  const isTreatmentLine = (x) => {
                                    const tt = (x?.treatType || x?.treatTypeDetail || '').toString()
                                    const cn = (x?.codeName || '').toString()
                                    // 치료/진료로 의미있는 라인 위주: 처치/수술/이학요법 계열만 남김
                                    if (tt.includes('처치') || cn.includes('처치')) return true
                                    if (tt.includes('수술') || cn.includes('수술') || cn.includes('시술')) return true
                                    if (tt.includes('이학요법료') || tt.includes('이학요법')) return true
                                    return false
                                  }
                                  const isInhospSurgeryLine = (x) => {
                                    const tt = (x?.treatType || x?.treatTypeDetail || '').toString()
                                    const cn = (x?.codeName || '').toString()
                                    if (isPrePostSurgeryNarrativeCodeName(cn)) return false
                                    // 입원/수술 탭: 진찰료 + 이학요법 + 처치/수술 위주로 축약(식대/입원료/검사/영상/재료 등 제외)
                                    if (tt.includes('진찰료')) return true
                                    if (tt.includes('이학요법료') || tt.includes('이학요법')) return true
                                    if (tt.includes('처치') || cn.includes('처치')) return true
                                    if (tt.includes('수술') || cn.includes('수술') || cn.includes('시술')) return true
                                    return false
                                  }
                                  const isLikelySurgeryLine = (x) => {
                                    // IMPORTANT:
                                    // treatType에는 "처치 및 수술" 문자열이 포함되어 있어,
                                    // 그 문자열만으로 수술로 판정하면 SURGIPRO 같은 재료/요법까지 오탐한다.
                                    // 따라서 '수술탭' UI 필터는 codeName(세부 진료명)만 보고 판정한다.
                                    const cn = (x?.codeName || '').toString().trim()
                                    if (!cn) return false
                                    if (isPrePostSurgeryNarrativeCodeName(cn)) return false

                                    const text = cn

                                    // 수술 확정/강하게 시사하는 키워드(명칭 기준)
                                    const kw =
                                      text.includes('제왕절개') ||
                                      text.includes('절제술') ||
                                      text.includes('제거술') ||
                                      text.includes('성형술') ||
                                      text.includes('재건술') ||
                                      text.includes('봉합술') ||
                                      text.includes('봉합') ||
                                      text.includes('절개') ||
                                      text.includes('적출') ||
                                      text.includes('소작')

                                    if (!kw) return false

                                    // 수술로 보기 어려운 요법/처치/재료성 라인은 제외
                                    const ex =
                                      text.includes('요법') ||
                                      text.includes('치료') ||
                                      text.includes('관리') ||
                                      text.includes('감시') ||
                                      // 마취/통증조절/주입 처치는 "수술명"으로 보지 않음 (예: 경막외 주입(Epidural PCA)-시술당일...)
                                      text.includes('PCA') ||
                                      text.toLowerCase().includes('epidural') ||
                                      text.toLowerCase().includes('infuser') ||
                                      text.includes('경막외') ||
                                      text.includes('주입') ||
                                      text.includes('카테터') ||
                                      text.includes('catheter') ||
                                      text.includes('단순처치') ||
                                      text.includes('체위변경') ||
                                      text.includes('배액') ||
                                      text.includes('산소') ||
                                      // 재료/제품명(영문 대문자) 및 흔한 키워드
                                      text.includes('SUTURE') ||
                                      text.includes('SILK') ||
                                      text.includes('VICRYL') ||
                                      text.includes('ETHIBOND') ||
                                      text.includes('PDS') ||
                                      text.includes('TIGHTROPE') ||
                                      text.includes('SCREW') ||
                                      text.includes('ANCHOR') ||
                                      text.includes('CANNULA') ||
                                      text.includes('WASHER') ||
                                      text.includes('FILTERLINE') ||
                                      text.includes('VAC') ||
                                      text.includes('치료재료') ||
                                      text.includes('수술팩')

                                    // 전부 대문자/숫자/기호에 가까운 라인은 제품 코드일 가능성이 높아 제외
                                    const looksLikeProduct = /^[A-Z0-9 _\\-\\.]+$/.test(text)

                                    return !(ex || looksLikeProduct)
                                  }
                                  const filterTreatDetailsByContext = (list) => {
                                    const arr = Array.isArray(list) ? list : []
                                    if (ctx === 'EXAM') return arr.filter(isExamLine)
                                    if (ctx === 'TREATMENTS') return arr.filter(isTreatmentLine)
                                    if (ctx === 'INHOSP_SURGERY') {
                                      const surgOnly = arr.filter(isLikelySurgeryLine)
                                      const base = surgOnly
                                      if (!inhospIncludeNonSurgery) return base

                                      // 토글 ON: 수술 라인은 유지하면서, 입원 처치/치료 라인(진찰/이학요법/처치/수술 계열)을 추가로 보여준다.
                                      const extra = arr.filter(isInhospSurgeryLine)
                                      const seen = new Set()
                                      const out = []
                                      const keyOf = (x) => {
                                        const t = (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                        const cn = (x?.codeName || '').toString().trim()
                                        return `${t}|${cn}`
                                      }
                                      for (const x of base) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      for (const x of extra) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      return out
                                    }
                                    if (ctx === 'HIDDEN_SURGERY') {
                                      const surgOnly = arr.filter(isLikelySurgeryLine)
                                      if (!hiddenIncludeAllProcedures) return surgOnly
                                      // 토글 ON: 처치 및 수술(카테고리) 전체를 보여준다(재료/요법 포함)
                                      const proc = arr.filter(
                                        (x) =>
                                          String(x?.treatType || x?.treatTypeDetail || '').includes('처치') &&
                                          !isPrePostSurgeryNarrativeCodeName(x?.codeName),
                                      )
                                      const seen = new Set()
                                      const out = []
                                      const keyOf = (x) => {
                                        const t = (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                        const cn = (x?.codeName || '').toString().trim()
                                        return `${t}|${cn}`
                                      }
                                      for (const x of surgOnly) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      for (const x of proc) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      return out
                                    }
                                    return arr
                                  }

                                  const stripExportNamesFromDrugName = (name) => {
                                    const s = (name || '').toString().trim()
                                    if (!s) return ''
                                    // [수출명: ...] 구간만 제거
                                    return s.replace(/\[수출명:[^\]]*\]/g, '').replace(/\s+/g, ' ').trim()
                                  }

                                  const drugs = lines.filter((x) => x?.source === 'PRESCRIBE_DRUG')
                                  const nonPharmacyBasics = basics.filter((x) => !isPharmacyLine(x))
                                  // IMPORTANT: 약국(조제료) DETAIL_TREAT를 hospitalCards에 섞으면 "약국이 2번" 보이는 문제가 생김.
                                  // 토글로 확장 표시할 때도 약국은 drug_group 카드 안에서만 확장 렌더링한다.
                                  const nonPharmacyDetailTreats = mergedDetailTreats.filter((x) => !isPharmacyLine(x))
                                  const pharmacyBasics = basics.filter((x) => isPharmacyLine(x))
                                  const pharmacyDetailTreats = mergedDetailTreats.filter((x) => isPharmacyLine(x))

                                  const hospitalGroups = (() => {
                                    const map = new Map()
                                    // 기본 진료(병원 라인)부터 그룹 생성
                                    for (const b of nonPharmacyBasics) {
                                      const k = hospitalKey(b)
                                      if (!k) continue
                                      if (!map.has(k)) map.set(k, { base: b, details: [] })
                                      else if (!map.get(k).base) map.get(k).base = b
                                    }
                                    // 상세 처치(DETAIL_TREAT) 붙이기
                                    for (const dt of nonPharmacyDetailTreats) {
                                      const k = hospitalKey(dt)
                                      if (!k) continue
                                      if (!map.has(k)) map.set(k, { base: null, details: [] })
                                      map.get(k).details.push(dt)
                                    }
                                    return Array.from(map.values())
                                  })()

                                  // 진료일수 표시:
                                  // - 통원/기본진료는 totalTreatmentDays(resVisitDays 성격)
                                  // - 입원건은 inpatientDays(입원일수)가 따로 내려오기도 하며, 이 경우 totalTreatmentDays가 0일 수 있다.
                                  // 따라서 화면 표시는 totalTreatmentDays > 0 우선, 아니면 inpatientDays > 0 를 fallback으로 사용한다.
                                  const formatTreatType = (x) => (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                  const formatCodeName = (x) => (x?.codeName || '').toString().trim()
                                  const detailKey = (x) => `${formatTreatType(x)}|${formatCodeName(x)}`

                                  const hospitalCards = hospitalGroups
                                    .map((g) => {
                                      const base = g.base
                                      const org = base?.hospitalName || g.details?.[0]?.hospitalName || base?.hospitalCode || g.details?.[0]?.hospitalCode || '-'
                                      const treatText = (() => {
                                        const t = base?.treatTypeDetail || base?.treatType || ''
                                        const dept = base?.department || ''
                                        const s = [t, dept].filter(Boolean).join(' / ')
                                        return s ? normalizeSlash(s) : ''
                                      })()
                                      const daysText = (() => {
                                        const assume1 = String(base?.endDateDerivedFrom || '').includes('ASSUME_1')
                                        const t = Number(base?.totalTreatmentDays)
                                        if (Number.isFinite(t) && t > 0) return assume1 ? `${t}일(추정)` : `${t}일`
                                        const inh = Number(base?.inpatientDays)
                                        if (Number.isFinite(inh) && inh > 0) return assume1 ? `${inh}일(추정)` : `${inh}일`
                                        return ''
                                      })()

                                      const list = filterTreatDetailsByContext(Array.isArray(g.details) ? g.details : [])
                                      const unique = []
                                      const seen = new Set()
                                      for (const x of list) {
                                        const k = detailKey(x)
                                        if (!k || seen.has(k)) continue
                                        seen.add(k)
                                        unique.push(x)
                                      }

                                      return { org, treatText, daysText, details: unique }
                                    })
                                    .filter((c) => c.org && c.org !== '-')

                                  const buildPharmacyVisitCards = () => {
                                    // 약국은 진료내역 모드에서 "방문 요약"만
                                    const map = new Map()
                                    for (const x of pharmacyBasics) {
                                      const k = hospitalKey(x)
                                      if (!k) continue
                                      if (!map.has(k)) map.set(k, x)
                                    }
                                    return Array.from(map.values()).map((x) => ({ kind: 'pharmacy_visit', x }))
                                  }

                                  const buildDrugGroupCards = () => {
                                    // 투약내역 모드: 병원/약국 모두 "투약 내역"만 묶어서 목록화 (조제료등 제외)
                                    const map = new Map()
                                    const add = (org, item) => {
                                      const k = org
                                      if (!k) return
                                      if (!map.has(k)) map.set(k, [])
                                      map.get(k).push(item)
                                    }
                                    for (const x of drugs) {
                                      const org = hospitalKey(x)
                                      add(org, x)
                                    }
                                    // 약국 DETAIL_TREAT(조제료등)은 기본 숨김이지만, 토글 ON이면 drug_group 카드 내부에 "추가 정보"로 붙여준다.
                                    const extraByOrg = new Map()
                                    if (drugsShowAllInfo) {
                                      for (const x of pharmacyDetailTreats) {
                                        const org = hospitalKey(x)
                                        if (!org) continue
                                        if (!extraByOrg.has(org)) extraByOrg.set(org, [])
                                        extraByOrg.get(org).push(x)
                                      }
                                    }
                                    const cards = []
                                    for (const [org, list] of map.entries()) {
                                      // 약품명 기준 dedupe
                                      const seen = new Set()
                                      const uniq = []
                                      for (const x of list) {
                                        const rawName = (x?.drugName || '').toString().trim()
                                        const name = drugsShowAllInfo ? rawName : stripExportNamesFromDrugName(rawName)
                                        const k = name
                                        if (!name || seen.has(k)) continue
                                        seen.add(k)
                                          uniq.push({ ...x, drugName: name })
                                      }
                                      const extras = drugsShowAllInfo ? (extraByOrg.get(org) || []) : []
                                      cards.push({ kind: 'drug_group', org, list: uniq, extras })
                                    }
                                    return cards
                                  }

                                  const ordered =
                                    detailEpisodeMode === 'drugs'
                                      ? [
                                          ...hospitalCards.map((h) => ({ kind: 'hospital', h })),
                                          ...buildDrugGroupCards(),
                                        ]
                                      : [
                                          ...hospitalCards.map((h) => ({ kind: 'hospital', h })),
                                          ...buildPharmacyVisitCards(),
                                        ]

                                  return (
                                    <div key={d} className="rounded border bg-slate-50 px-3 py-2">
                                      <button
                                        type="button"
                                        className="w-full flex items-center justify-between text-left"
                                        onClick={() => {
                                          setCollapsedDetailDays((prev) => {
                                            const next = new Set(prev)
                                            if (next.has(d)) next.delete(d)
                                            else next.add(d)
                                            return next
                                          })
                                        }}
                                      >
                                        <div className="text-xs font-semibold text-slate-900">{titleDate}</div>
                                        <div className="text-[11px] text-slate-500">
                                          {isCollapsed ? '펼치기' : '접기'} ({ordered.length})
                                        </div>
                                      </button>
                                      {!isCollapsed && (
                                        <div className="mt-2 space-y-2">
                                          {ordered.map((h, idx) => (
                                            <div key={idx} className="rounded border bg-white px-3 py-1.5">
                                              {h.kind === 'hospital' && (
                                                <>
                                                  <div className="text-xs text-slate-700">
                                                    <span className="font-medium">{h.h.org}</span>
                                                    {h.h.treatText ? <span className="text-slate-500"> • {h.h.treatText}</span> : null}
                                        </div>
                                                  {h.h.daysText ? (
                                                    <div className="mt-1 text-[11px] text-slate-500">진료일수: {h.h.daysText}</div>
                                                  ) : null}
                                                  {h.h.details && h.h.details.length > 0 ? (
                                                    <div className="mt-2 text-[11px] text-slate-700">
                                                      <div className="font-semibold text-slate-600">진료형태 | 코드명 목록 :</div>
                                                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-slate-700">
                                                        {h.h.details.map((x, i2) => (
                                                          <li key={i2}>
                                                            {formatTreatType(x) || '-'}
                                                            {'  |  '}
                                                            {formatCodeName(x) || '-'}
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  ) : ctx === 'INHOSP_SURGERY' ? (
                                                    <div className="mt-2 text-[11px] text-slate-500">
                                                      표시할 수술 내역이 없습니다.
                                                      {!inhospIncludeNonSurgery ? (
                                                        <span className="text-slate-500"> (필요하면 위 옵션에서 ‘수술 외 처치/치료/재료까지 모두 보기’를 켜세요)</span>
                                                      ) : null}
                                                    </div>
                                                  ) : ctx === 'HIDDEN_SURGERY' ? (
                                                    <div className="mt-2 text-[11px] text-slate-500">
                                                      표시할 수술 내역이 없습니다.
                                                      {!hiddenIncludeAllProcedures ? (
                                                        <span className="text-slate-500"> (필요하면 위 옵션에서 ‘처치 및 수술 전체 보기’를 켜세요)</span>
                                                      ) : null}
                                                    </div>
                                                  ) : null}
                                                </>
                                              )}
                                              {h.kind === 'drug_group' && (
                                                <>
                                                  <div className="text-xs text-slate-700">
                                                    <span className="font-medium">{h.org}</span>
                                                  </div>
                                                  {(() => {
                                                    const list = Array.isArray(h.list) ? h.list : []
                                                    let maxDays = null
                                                    for (const x of list) {
                                                      const v = x?.totalDrugDays
                                                      const n =
                                                        typeof v === 'number'
                                                          ? v
                                                          : v !== null && v !== undefined && String(v).trim() !== ''
                                                          ? Number(v)
                                                          : null
                                                      if (!Number.isFinite(n)) continue
                                                      maxDays = maxDays === null ? Math.max(0, n) : Math.max(maxDays, Math.max(0, n))
                                                    }
                                                    return maxDays !== null ? (
                                                      <div className="mt-1 text-[11px] text-slate-500">투약일 : {maxDays}일</div>
                                                    ) : null
                                                  })()}
                                                  {Array.isArray(h.list) && h.list.length > 0 ? (
                                                    <div className="mt-2 text-[11px] text-slate-700">
                                                      <div className="font-semibold text-slate-600">투약내역 목록 :</div>
                                                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-slate-700">
                                                        {h.list.map((x, i2) => (
                                                          <li key={i2}>{x?.drugName || '-'}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  ) : (
                                                    <div className="mt-1 text-[11px] text-slate-600">-</div>
                                                  )}
                                                  {Array.isArray(h.extras) && h.extras.length > 0 ? (
                                                    <div className="mt-2 text-[11px] text-slate-700">
                                                      <div className="font-semibold text-slate-600">조제료/기타(숨김 포함) :</div>
                                                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-slate-700">
                                                        {(() => {
                                                          const seen = new Set()
                                                          const uniq = []
                                                          for (const x of h.extras) {
                                                            const k = detailKey(x)
                                                            if (!k || seen.has(k)) continue
                                                            seen.add(k)
                                                            uniq.push(x)
                                                          }
                                                          return uniq.map((x, i2) => (
                                                            <li key={i2}>
                                                              {formatTreatType(x) || '-'}
                                                              {'  |  '}
                                                              {formatCodeName(x) || '-'}
                                                            </li>
                                                          ))
                                                        })()}
                                                      </ul>
                                                    </div>
                                                  ) : null}
                                                </>
                                              )}
                                              {h.kind === 'pharmacy_visit' && (
                                                <>
                                                  <div className="text-xs text-slate-700">
                                                    <span className="font-medium">{h.x?.hospitalName || h.x?.hospitalCode || '-'}</span>
                                                    {(h.x?.treatTypeDetail || h.x?.treatType) ? (
                                                      <span className="text-slate-500"> • {normalizeSlash(h.x?.treatTypeDetail || h.x?.treatType)}</span>
                                                    ) : null}
                                                  </div>
                                                  {h.x?.totalTreatmentDays ? (
                                                    <div className="mt-1 text-[11px] text-slate-500">진료일수: {h.x.totalTreatmentDays}일</div>
                                                  ) : null}
                                                </>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* 3NN 템플릿 추천 결과 팝업 */}
      {threeNnModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-lg p-4 text-sm max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-slate-900">
                {threeNnModalData?.kind === 'SIMPLE_3N5' ? '유병자 3N5 가능 여부(1차)' : '보험사별 예외 적용(예외질환/3NN)'}
              </div>
              <div className="flex items-center gap-2">
                {threeNnModalData?.kind !== 'SIMPLE_3N5' && (
                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                    onClick={() => {
                      const codes = Array.from(selectedInsurerCodesForRequest || [])
                      handleGoToRequestNewFrom3nn({
                        coverageType: 'substandard',
                        selectedInsurerCodes: codes.length > 0 ? codes : null,
                      })
                    }}
                  >
                    선택된 보험사로 설계의뢰
                  </button>
                )}
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => {
                  setThreeNnModalOpen(false)
                  setThreeNnModalMode('SIMPLE_3N5')
                  setThreeNnTemplateExpandedTemplates(new Set())
                  setThreeNnTemplateKcdExpanded(new Set())
                  setThreeNnTemplateCellDetail(null)
                }}
              >
                닫기
              </button>
              </div>
            </div>

            <div className="text-xs text-slate-500">
              기준일:{' '}
              <span className="font-mono">{threeNnModalData?.asOf || '-'}</span>
            </div>

            <div className="mt-3 space-y-3">
              {threeNnModalData?.kind === 'SIMPLE_3N5' && (
                <div className="rounded border bg-slate-50 p-3 text-sm text-slate-800">
                  <div className="font-semibold text-slate-900">안내</div>
                  <div className="mt-1 text-xs text-slate-700 whitespace-pre-wrap">{threeNnModalData?.message || '-'}</div>

                  {Array.isArray(threeNnModalData?.reasons) && threeNnModalData.reasons.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-900">조합을 이렇게 안내한 이유</div>
                      <div className="mt-1 space-y-0.5 text-[11px] text-slate-700">
                        {threeNnModalData.reasons.map((x, idx) => (
                          <div key={idx} className="text-slate-600">- {x}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Array.isArray(threeNnModalData?.possibleCombos) && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-900">가능한 조합</div>
                      {threeNnModalData.possibleCombos.length === 0 ? (
                        <div className="mt-1 text-xs text-slate-600">없음</div>
                      ) : (
                        <div className="mt-1 flex flex-wrap gap-2">
                          {threeNnModalData.possibleCombos.map((x) => (
                            <span key={x} className="rounded-full border bg-white px-2 py-0.5 text-xs font-mono text-slate-700">
                              {x}
                            </span>
                          ))}
                        </div>
                      )}
                      {threeNnModalData.possibleCombos.includes('3-7-5') && (
                        <div className="mt-2 text-[11px] text-slate-600 whitespace-pre-wrap">
                          * 최근 5년 내 입원/수술 내역에서 필터링되는 항목이 없다면, 보험사별로 3-7-5 / 3-10-5 / 3-10-10 등도 가입 가능성이 있으니 확인해주세요.
                        </div>
                      )}
                    </div>
                  )}

                  {threeNnModalData?.footnote && (
                    <div className="mt-3 text-[11px] text-slate-500 whitespace-pre-wrap">{threeNnModalData.footnote}</div>
                  )}
                </div>
              )}
              {threeNnModalMode === 'TEMPLATE_3NN' && (
                <>
                  {/* 필터 영역: 선택된 보험사 목록만 표시 */}
                  <div className="mt-2 rounded border bg-slate-50 p-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs text-slate-700">
                        선택된 보험사: <span className="font-mono font-semibold">{selectedInsurerCodesForRequest.size}</span>
                      </div>
                      <button
                        type="button"
                        className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40"
                        onClick={() => setSelectedInsurerCodesForRequest(new Set())}
                        disabled={selectedInsurerCodesForRequest.size === 0}
                      >
                        선택 해제
                      </button>
                    </div>
                    {selectedInsurerCodesForRequest.size > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Array.from(selectedInsurerCodesForRequest)
                          .slice(0, 12)
                          .map((code) => (
                            <span
                              key={code}
                              className="inline-flex items-center gap-2 rounded-full border bg-white px-2 py-0.5 text-[11px] text-slate-700"
                            >
                              <span className="font-semibold">{insurerNameKrOf(code)}</span>
                              <button
                                type="button"
                                className="text-slate-400 hover:text-slate-700"
                                onClick={() => {
                                  const next = new Set(selectedInsurerCodesForRequest)
                                  next.delete(code)
                                  setSelectedInsurerCodesForRequest(next)
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        {selectedInsurerCodesForRequest.size > 12 && (
                          <span className="text-[11px] text-slate-500">
                            … 외 {selectedInsurerCodesForRequest.size - 12}개
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 매트릭스 뷰(기본: 전체 보험사, 선택 체크박스는 설계의뢰용) */}
                  <div className="mt-3 rounded border bg-white">
                    {(() => {
                      const rawItems = Array.isArray(threeNnTemplateResult?.items) ? threeNnTemplateResult.items : []
                      const asOfForDetail = threeNnTemplateResult?.asOf || null

                      const statusRankLocal = (v) => {
                        const s = toStatusCode(v)
                        if (s === 'OK') return 4
                        if (s === 'WARN') return 3
                        if (s === 'REVIEW') return 2
                        if (s === 'FAIL') return 1
                        return 0
                      }

                      const pickBestLocal = (results) => {
                        const list = Array.isArray(results) ? results.slice() : []
                        list.sort((a, b) => {
                          const sa = statusRankLocal(a?.status)
                          const sb = statusRankLocal(b?.status)
                          if (sa !== sb) return sb - sa
                          const scA = Number.isFinite(Number(a?.score)) ? Number(a.score) : -1
                          const scB = Number.isFinite(Number(b?.score)) ? Number(b.score) : -1
                          if (scA !== scB) return scB - scA
                          const rkA = Number.isFinite(Number(a?.rankScore)) ? Number(a.rankScore) : -1
                          const rkB = Number.isFinite(Number(b?.rankScore)) ? Number(b.rankScore) : -1
                          if (rkA !== rkB) return rkB - rkA
                          return String(a?.templateCode || '').localeCompare(String(b?.templateCode || ''))
                        })
                        return list.length > 0 ? list[0] : null
                      }

                      // 중복 보험사(동일 insurerCode)가 내려오는 경우를 방어적으로 병합한다.
                      const mergedByCode = new Map()
                      for (const it of rawItems) {
                        const rawCode = String(it?.insurerCode || '').trim()
                        const code = canonicalInsurerCodeForUi(rawCode)
                        if (!code) continue
                        const prev = mergedByCode.get(code)
                        if (!prev) {
                          mergedByCode.set(code, {
                            ...it,
                            insurerCode: code,
                            __uiOriginalCodes: [rawCode].filter(Boolean),
                          })
                          continue
                        }
                        const prevResults = Array.isArray(prev?.results) ? prev.results : []
                        const nextResults = Array.isArray(it?.results) ? it.results : []
                        const prevResultsBefore = Array.isArray(prev?.resultsBeforeException) ? prev.resultsBeforeException : []
                        const nextResultsBefore = Array.isArray(it?.resultsBeforeException) ? it.resultsBeforeException : []
                        const mergedResultsMap = new Map()
                        for (const r of [...prevResults, ...nextResults]) {
                          const tc = String(r?.templateCode || '').trim()
                          if (!tc) continue
                          // 같은 템플릿코드가 중복이면 더 좋은 결과로 유지
                          const cur = mergedResultsMap.get(tc)
                          if (!cur) mergedResultsMap.set(tc, r)
                          else {
                            const best = pickBestLocal([cur, r])
                            mergedResultsMap.set(tc, best || r)
                          }
                        }
                        const mergedResults = Array.from(mergedResultsMap.values())

                        const mergedResultsBeforeMap = new Map()
                        for (const r of [...prevResultsBefore, ...nextResultsBefore]) {
                          const tc = String(r?.templateCode || '').trim()
                          if (!tc) continue
                          const cur = mergedResultsBeforeMap.get(tc)
                          if (!cur) mergedResultsBeforeMap.set(tc, r)
                          else {
                            const best = pickBestLocal([cur, r])
                            mergedResultsBeforeMap.set(tc, best || r)
                          }
                        }
                        const mergedResultsBefore = Array.from(mergedResultsBeforeMap.values())
                        const merged = {
                          ...prev,
                          ...it,
                          insurerCode: code,
                          insurerName: prev?.insurerName || it?.insurerName || null,
                          results: mergedResults,
                          best: pickBestLocal(mergedResults) || prev?.best || it?.best || null,
                          resultsBeforeException: mergedResultsBefore,
                          bestBeforeException:
                            pickBestLocal(mergedResultsBefore) || prev?.bestBeforeException || it?.bestBeforeException || null,
                          __uiOriginalCodes: Array.from(
                            new Set([...(Array.isArray(prev?.__uiOriginalCodes) ? prev.__uiOriginalCodes : []), rawCode].filter(Boolean)),
                          ),
                        }
                        mergedByCode.set(code, merged)
                      }

                      const items = Array.from(mergedByCode.values())
                      const filtered = items
                        .slice()
                        .sort((a, b) => {
                          const ca = insurerCategoryOf(a?.insurerCode)
                          const cb = insurerCategoryOf(b?.insurerCode)
                          if (ca !== cb) return ca === 'NONLIFE' ? -1 : 1
                          const na = insurerNameKrOf(a?.insurerCode, a?.insurerName)
                          const nb = insurerNameKrOf(b?.insurerCode, b?.insurerName)
                          return String(na).localeCompare(String(nb))
                        })
                      const byCode = mergedByCode

                      // 컬럼 리스트는 중복 제거(보험사 코드 기준)
                      const colsAll = (() => {
                        const out = []
                        const seen = new Set()
                        for (const it of filtered) {
                          const c = String(it?.insurerCode || '').trim()
                          if (!c) continue
                          if (!byCode.has(c)) continue
                          if (seen.has(c)) continue
                          seen.add(c)
                          out.push(c)
                        }
                        return out
                      })()

                      if (colsAll.length === 0) {
                        return <div className="p-3 text-xs text-slate-500">표시할 보험사가 없습니다.</div>
                      }

                      const nonlifeCols = colsAll.filter((c) => insurerCategoryOf(c) === 'NONLIFE')
                      const lifeCols = colsAll.filter((c) => insurerCategoryOf(c) === 'LIFE')

                      // 같은 상품코드(335/355/375 등)는 항상 동일 색상으로 표시
                      const templateClass = (templateCode) => {
                        const tc = String(templateCode || '').trim()
                        const fixed = {
                          '335': 'bg-emerald-50 text-emerald-900 border-emerald-200',
                          '345': 'bg-blue-50 text-blue-900 border-blue-200',
                          '355': 'bg-sky-50 text-sky-900 border-sky-200',
                          '375': 'bg-violet-50 text-violet-900 border-violet-200',
                        }
                        if (fixed[tc]) return fixed[tc]
                        const palette = [
                          'bg-amber-50 text-amber-900 border-amber-200',
                          'bg-rose-50 text-rose-900 border-rose-200',
                          'bg-teal-50 text-teal-900 border-teal-200',
                          'bg-lime-50 text-lime-900 border-lime-200',
                        ]
                        const digits = tc.replaceAll(/[^0-9]/g, '')
                        const n = digits ? Number(digits) : 0
                        const idx = Number.isFinite(n) && n > 0 ? n % palette.length : 0
                        return palette[idx] || 'bg-slate-50 text-slate-800 border-slate-200'
                      }

                      const catAllSelected = (codes) => codes.length > 0 && codes.every((c) => selectedInsurerCodesForRequest.has(String(c)))
                      const toggleCatAll = (codes) => {
                        const next = new Set(selectedInsurerCodesForRequest)
                        const allOn = catAllSelected(codes)
                        for (const c of codes) {
                          if (allOn) next.delete(String(c))
                          else next.add(String(c))
                        }
                        setSelectedInsurerCodesForRequest(next)
                      }

                      const renderCategoryTable = (label, cols) => {
                        if (!cols || cols.length === 0) return null
                        const COL_MIN_W = 120
                        return (
                          <div className="mt-3 first:mt-0">
                            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
                              <label className="inline-flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={catAllSelected(cols)}
                                  onChange={() => toggleCatAll(cols)}
                                />
                                <span className="font-semibold text-slate-900">{label}</span>
                                <span className="text-[11px] text-slate-600">(전체선택)</span>
                              </label>
                              <div className="text-[11px] text-slate-500">보험사 {cols.length}개</div>
                            </div>
                            <div className="overflow-auto">
                              <table className="w-max min-w-full text-xs">
                                <thead>
                                  <tr className="bg-slate-50">
                                    <th className="p-2 text-left border-b w-[90px]" rowSpan={2}>보험사</th>
                                    {cols.map((code) => {
                                      const it = byCode.get(code)
                                      const nameKr = insurerNameKrOf(code, it?.insurerName)
                                      return (
                                        <th key={code} className="p-2 text-center border-b min-w-[120px]" style={{ minWidth: COL_MIN_W }}>
                                          <label className="inline-flex items-center gap-2 justify-center text-[11px] text-slate-700">
                                            <input
                                              type="checkbox"
                                              checked={selectedInsurerCodesForRequest.has(String(code))}
                                              onChange={(e) => {
                                                const next = new Set(selectedInsurerCodesForRequest)
                                                if (Boolean(e.target.checked)) next.add(String(code))
                                                else next.delete(String(code))
                                                setSelectedInsurerCodesForRequest(next)
                                              }}
                                            />
                                            <span className="sr-only">선택</span>
                                          </label>
                                          <div className="mt-1 font-semibold text-slate-900">{nameKr}</div>
                                        </th>
                                      )
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-b last:border-b-0">
                                    <td className="p-2 font-semibold text-slate-800">상품</td>
                                    {cols.map((code) => {
                                      const it = byCode.get(code)
                                      const best = it?.best || null
                                      const bestCode = String(best?.templateCode || '')
                                      const r =
                                        bestCode
                                          ? (Array.isArray(it?.results) ? it.results : []).find((x) => String(x?.templateCode || '') === bestCode) || best
                                          : best
                                      return (
                                        <td key={`${code}__BEST`} className="p-2 text-center min-w-[120px]" style={{ minWidth: COL_MIN_W }}>
                                          <div className={`w-full rounded border px-3 py-3 ${templateClass(bestCode)}`}>
                                            <div className="font-mono font-semibold text-lg leading-none">{bestCode || '-'}</div>
                                            <div className="mt-2">
                                              <button
                                                type="button"
                                                className="rounded border bg-white/70 px-2 py-1 text-[11px] hover:bg-white whitespace-nowrap"
                                                onClick={() => {
                                                  setThreeNnTemplateCellDetail({
                                                    insurerCode: code,
                                                    insurerName: insurerNameKrOf(code),
                                                    templateCode: bestCode || '-',
                                                    r,
                                                    bestBeforeException: it?.bestBeforeException || null,
                                                    asOf: asOfForDetail,
                                                  })
                                                }}
                                              >
                                                [상세]
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                      )
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )
                      }

                      return (
                        <div>
                          {renderCategoryTable('손해보험', nonlifeCols)}
                          {renderCategoryTable('생명보험', lifeCols)}
                        </div>
                      )
                    })()}
                  </div>

                  {/* 셀 상세 팝업 */}
                  {threeNnTemplateCellDetail && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
                      <div className="w-full max-w-xl rounded-lg bg-white shadow-lg max-h-[80vh] overflow-auto">
                        <div className="sticky top-0 z-10 bg-white border-b px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div className="font-semibold text-slate-900">
                              {threeNnTemplateCellDetail.insurerName}{' '}
                              <span className="font-mono">{threeNnTemplateCellDetail.templateCode}</span>
                            </div>
                            <button
                              type="button"
                              className="text-xs text-slate-500 hover:text-slate-800"
                              onClick={() => {
                                setThreeNnTemplateCellDetail(null)
                                setThreeNnTemplateKcdExpanded(new Set())
                              }}
                            >
                              닫기
                            </button>
                          </div>
                        </div>

                        {(() => {
                          const r = threeNnTemplateCellDetail.r || {}
                          const st = toStatusCode(r?.status)
                          const matched = Array.isArray(r?.matchedRules) ? r.matchedRules : []
                          const evidences = Array.isArray(r?.evidences) ? r.evidences : []
                          const ruleRows = matched
                            .map((x) => ({
                              decisionType: toStatusCode(x?.decisionType),
                              rawDecisionType: toStatusCode(x?.rawDecisionType),
                              applied: x?.applied,
                              conditionKey: (x?.conditionKey || '').toString().trim(),
                              message: (x?.message || '').toString().trim(),
                              ruleId: x?.ruleId ?? null,
                              waitingDays: x?.waitingDays ?? null,
                              maxCount: x?.maxCount ?? null,
                              inpatientMaxDays: x?.inpatientMaxDays ?? null,
                              inpatientDaysMax: x?.inpatientDaysMax ?? null,
                              note: (x?.note || '').toString().trim(),
                              matchedCount: x?.matchedCount ?? null,
                              daysSinceEnd: x?.daysSinceEnd ?? null,
                            }))
                            .filter((x) => x.conditionKey && x.decisionType)

                          const nonOk = ruleRows.filter((x) => x.decisionType && x.decisionType !== 'OK')

                          const normalizeKcd = (value) => {
                            const s = (value || '').toString().trim().toUpperCase().replaceAll(/[^A-Z0-9.]/g, '')
                            if (!s) return ''
                            const noDot = s.replaceAll('.', '')
                            const m = noDot.match(/^([A-Z]\d{2,5})/)
                            return m ? m[1] : ''
                          }

                          const conditionKeyToUiCode = (conditionKey) => {
                            const raw = (conditionKey || '').toString().trim().toUpperCase()
                            // 예: KCD_K291_3 -> K291.3
                            const m = raw.match(/^KCD_([A-Z]\d{2,5})(?:_(\d+))?$/)
                            if (m && m[1]) {
                              const base = m[1]
                              const suf = m[2]
                              return suf ? `${base}.${suf}` : base
                            }
                            return raw || '-'
                          }

                          const extractKcdFromRule = (rule) => {
                            const pool = `${rule?.conditionKey || ''} ${rule?.message || ''}`.toString().toUpperCase()
                            const out = new Set()
                            // 1) KCD_XXX 형태 우선
                            ;(pool.matchAll(/KCD_([A-Z]\d{2,5})/g) || []).forEach((m) => {
                              if (m?.[1]) out.add(m[1])
                            })
                            // 2) 그래도 없으면 일반 KCD코드 패턴(문장에 노출된 경우)
                            if (out.size === 0) {
                              ;(pool.matchAll(/\b([A-Z]\d{2,5})\b/g) || []).forEach((m) => {
                                if (m?.[1]) out.add(m[1])
                              })
                            }
                            return Array.from(out)
                          }

                          const episodeSummary = (ep) => {
                            const start = (ep?.startDate || '').toString()
                            const end = (ep?.endDate || '').toString()
                            const dx = normalizeDiagnosisCodeForDisplay(ep?.diagnosisCode)
                            const name = normalizeDiagnosisNameForDisplay(ep?.diagnosisName)
                            const hosp = (ep?.hospitalName || ep?.hospitalCode || '').toString()
                            const inpatient = ep?.inpatient ? '입원' : '외래'
                            const treatDays = ep?.totalTreatmentDays != null ? String(ep.totalTreatmentDays) : ''
                            const drugDays = ep?.totalDrugDays != null ? String(ep.totalDrugDays) : ''
                            const range = start || end ? `${start || '-'}~${end || '-'}` : '-'
                            const daysPart = (treatDays || drugDays) ? ` / 진료${treatDays || '-'}일 / 투약${drugDays || '-'}일` : ''
                            return `${dx}${name ? `(${name})` : ''} - ${range} / ${inpatient}${daysPart}${hosp ? ` / ${hosp}` : ''}`
                          }

                          const whyText = (() => {
                            const tc = threeNnTemplateCellDetail.templateCode
                            const b = threeNnTemplateCellDetail?.bestBeforeException || null
                            const btc = (b?.templateCode || '').toString().trim()
                            const bst = toStatusCode(b?.status)
                            const afterSt = toStatusCode(r?.status)
                            const templateCodeToComboDot = (templateCode) => {
                              const s = (templateCode || '').toString().trim()
                              if (!s) return '-'
                              if (/^\d{3}$/.test(s)) return `${s[0]}.${s[1]}.${s[2]}`
                              if (/^\d{5}$/.test(s) && s.startsWith('3')) return `${s[0]}.${s.slice(1, 3)}.${s.slice(3, 5)}`
                              return s
                            }
                            const comboDashToDot = (combo) => {
                              const s = (combo || '').toString().trim()
                              if (!s) return '-'
                              const m = s.match(/^\s*(\d+)\s*-\s*(\d+)\s*-\s*(\d+)\s*$/)
                              if (m) return `${m[1]}.${m[2]}.${m[3]}`
                              return s.replaceAll('-', '.')
                            }
                            const statusToSentence = (stCode) => {
                              // 안전 정책: OK/WARN/REVIEW/FAIL은 내부 참고용이며 보험사의 공식 기준이 아님.
                              // "추천 사유"에서는 오해 소지가 있어 모두 OK 톤으로 통일한다.
                              return '가입이 가능합니다.'
                            }
                            const simple3n5StatusToSentence = (st) => {
                              // 위와 동일 정책 적용
                              return '가입이 가능합니다.'
                            }
                            const lines = []

                            // "미고려"는 유병자 3N5(단순) 기준을 우선 사용한다. (템플릿(335/345/355)만으로는 3.1.5/3.2.5 등이 표현되지 않음)
                            const beforeFrom3n5 = (() => {
                              if (threeNnResult?.kind !== 'SIMPLE_3N5') return null
                              const combos = Array.isArray(threeNnResult?.possibleCombos) ? threeNnResult.possibleCombos : []
                              const first = combos.length ? combos[0] : null
                              if (!first) return null
                              return {
                                comboDot: comboDashToDot(first),
                                sentence: simple3n5StatusToSentence(threeNnResult?.status),
                              }
                            })()

                            const afterCombo = templateCodeToComboDot(tc)
                            const afterSentence = statusToSentence(afterSt)

                            if (beforeFrom3n5) {
                              // 전/후가 동일하면 중복 문구를 줄여 UX를 개선한다.
                              if (String(beforeFrom3n5.comboDot) === String(afterCombo) && String(beforeFrom3n5.sentence) === String(afterSentence)) {
                                lines.push(`예외질환 미고려/적용 모두 ${afterCombo} 상품 ${afterSentence}`)
                                return lines.join('\n')
                              }
                              lines.push(`예외질환 미고려 시 ${beforeFrom3n5.comboDot} 상품 ${beforeFrom3n5.sentence}`)
                            } else if (btc) {
                              const beforeCombo = templateCodeToComboDot(btc)
                              // 전/후가 동일하면 중복 문구를 줄여 UX를 개선한다.
                              if (String(beforeCombo) === String(afterCombo) && String(bst) === String(afterSt)) {
                                lines.push(`예외질환 미고려/적용 모두 ${afterCombo} 상품 ${afterSentence}`)
                                return lines.join('\n')
                              }
                              lines.push(`예외질환 미고려 시 ${beforeCombo} 상품 ${statusToSentence(bst)}`)
                            }

                            lines.push(`예외질환 적용 후 ${afterCombo} 상품 ${afterSentence}`)
                            // lines.push(`아래는 해당 결과에 영향을 준 진료내역/룰 근거입니다.`)
                            return lines.join('\n')
                          })()

                          const episodeEvidences = evidences.filter((x) => (x?.type || '') === 'EPISODE')
                          const asOfDt = toDateObject(threeNnTemplateCellDetail?.asOf || null)

                          // 코드별(예: K291)로 진료내역을 요약/중복 묶기
                          const episodesByKcd = (() => {
                            const m = new Map()
                            for (const ep of episodeEvidences) {
                              const rawCode = (ep?.diagnosisCode || '').toString()
                              const kcd = normalizeKcd(rawCode)
                              if (!kcd) continue
                              const name = normalizeDiagnosisNameForDisplay(ep?.diagnosisName)
                              const start = (ep?.startDate || '').toString()
                              const end = (ep?.endDate || '').toString()
                              const inpatient = Boolean(ep?.inpatient)
                              const hosp = (ep?.hospitalName || ep?.hospitalCode || '').toString().trim()
                              const treatDays = ep?.totalTreatmentDays != null ? String(ep.totalTreatmentDays) : ''
                              const drugDays = ep?.totalDrugDays != null ? String(ep.totalDrugDays) : ''
                              const key = `${start}|${end}|${inpatient ? 'I' : 'O'}|${hosp || '-'}|${treatDays || '-'}|${drugDays || '-'}`

                              if (!m.has(kcd)) {
                                m.set(kcd, {
                                  kcd,
                                  diagnosisName: name || '',
                                  groups: new Map(),
                                  counts: { total: 0, inpatient: 0, outpatient: 0 },
                                  minStart: null,
                                  maxEnd: null,
                                  hospitals: new Map(), // hosp -> count
                                })
                              }
                              const cur = m.get(kcd)
                              cur.counts.total += 1
                              if (inpatient) cur.counts.inpatient += 1
                              else cur.counts.outpatient += 1
                              if (!cur.diagnosisName && name) cur.diagnosisName = name

                              const sdt = toDateObject(start)
                              const edt = toDateObject(end)
                              if (sdt && (!cur.minStart || sdt < cur.minStart)) cur.minStart = sdt
                              if (edt && (!cur.maxEnd || edt > cur.maxEnd)) cur.maxEnd = edt

                              const prevHosp = cur.hospitals.get(hosp || '-') || 0
                              cur.hospitals.set(hosp || '-', prevHosp + 1)

                              if (!cur.groups.has(key)) {
                                cur.groups.set(key, { count: 1, ep })
                              } else {
                                cur.groups.get(key).count += 1
                              }
                            }
                            return m
                          })()

                          // 룰을 KCD 코드별로 묶기 (예: conditionKey=KCD_K291_3)
                          const rulesByKcd = (() => {
                            const m = new Map()
                            for (const rule of ruleRows) {
                              const kcds = extractKcdFromRule(rule)
                              if (kcds.length === 0) {
                                const key = '__OTHER__'
                                if (!m.has(key)) m.set(key, [])
                                m.get(key).push(rule)
                                continue
                              }
                              for (const raw of kcds) {
                                const kcd = normalizeKcd(raw) || raw
                                if (!m.has(kcd)) m.set(kcd, [])
                                m.get(kcd).push(rule)
                              }
                            }
                            return m
                          })()

                          const exceptionKcdKeys = (() => {
                            const set = new Set()
                            for (const k of rulesByKcd.keys()) if (k !== '__OTHER__') set.add(k)
                            for (const k of episodesByKcd.keys()) set.add(k)
                            return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)))
                          })()

                          // 전체 진료내역(리포트 원본)에서 "예외질환 미해당" 코드를 간략 표시(코드/진단명)
                          const allEpisodeKcdMap = (() => {
                            const m = new Map() // kcd -> { kcd, diagnosisName }
                            const rawList = Array.isArray(rawEpisodesFromSummary) ? rawEpisodesFromSummary : []
                            for (const ep of rawList) {
                              const kcd = normalizeKcd(ep?.diagnosisCode || '')
                              if (!kcd) continue
                              const name = normalizeDiagnosisNameForDisplay(ep?.diagnosisName)
                              if (!m.has(kcd)) m.set(kcd, { kcd, diagnosisName: name || '' })
                              else {
                                const cur = m.get(kcd)
                                if (!cur.diagnosisName && name) cur.diagnosisName = name
                              }
                            }
                            return m
                          })()

                          const nonExceptionKcdKeys = (() => {
                            const exSet = new Set(exceptionKcdKeys)
                            const all = Array.from(allEpisodeKcdMap.keys())
                            return all
                              .filter((k) => !exSet.has(k))
                              .sort((a, b) => String(a).localeCompare(String(b)))
                          })()

                          return (
                            <div className="p-4 space-y-3 text-xs">
                              <div className="rounded border bg-slate-50 p-3">
                                <div className="font-semibold text-slate-900">추천 사유</div>
                                <div className="mt-1 text-slate-700 whitespace-pre-wrap">{whyText}</div>
                              </div>

                              <div className="rounded border bg-slate-50 p-3 text-[11px] text-slate-500">
                                * 예외질환의 세부 조건은 각 보험사별로 내부적인 규칙에 의해 변경될 수 있습니다. 따라서 각 진료내역에 따른 보장 및 상품 가입 가능성은 담당
                                매니저에게 문의해주세요.
                              </div>

                              <div className="rounded border bg-white p-3">
                                <details className="rounded border bg-white p-2">
                                  <summary className="cursor-pointer text-[11px] font-semibold text-slate-900">
                                    예외질환 해당 진료내역 ({exceptionKcdKeys.length})
                                  </summary>
                                  {exceptionKcdKeys.length === 0 ? (
                                    <div className="mt-2 text-[11px] text-slate-500">예외질환 해당 진료내역이 없습니다.</div>
                                  ) : (
                                    <div className="mt-2 space-y-2">
                                    {exceptionKcdKeys.map((kcd) => {
                                      const epPack = episodesByKcd.get(kcd) || null
                                      const rulePack = rulesByKcd.get(kcd) || []
                                      const name = epPack?.diagnosisName || ''
                                      const dateRange =
                                        epPack?.minStart || epPack?.maxEnd
                                          ? `${epPack?.minStart ? formatDate(epPack.minStart) : '-'}~${epPack?.maxEnd ? formatDate(epPack.maxEnd) : '-'}`
                                          : null
                                      const groups =
                                        epPack && epPack.groups.size > 0
                                          ? Array.from(epPack.groups.values()).sort((a, b) => b.count - a.count)
                                          : []

                                      const expandKey = `${threeNnTemplateCellDetail?.insurerCode || ''}__${threeNnTemplateCellDetail?.templateCode || ''}__${kcd}`
                                      const isExpanded = threeNnTemplateKcdExpanded.has(expandKey)

                                      const daysSinceEnd = (() => {
                                        if (!asOfDt || !epPack?.maxEnd) return null
                                        const ms = asOfDt.getTime() - epPack.maxEnd.getTime()
                                        const days = Math.floor(ms / (24 * 60 * 60 * 1000))
                                        return Number.isFinite(days) ? days : null
                                      })()

                                      return (
                                        <div key={kcd} className="rounded border bg-slate-50 p-2">
                                          <div className="font-mono font-semibold text-slate-900">
                                            {formatKcdDottedForMemo(kcd)}
                                            {name ? <span className="font-sans text-slate-700">({name})</span> : null}
                                          </div>

                                          {epPack ? (
                                            <div className="mt-1 text-[11px] text-slate-700 space-y-0.5">
                                              <div>
                                                - 건수: <span className="font-mono">{epPack.counts.total}</span>건 (외래{' '}
                                                <span className="font-mono">{epPack.counts.outpatient}</span>/입원{' '}
                                                <span className="font-mono">{epPack.counts.inpatient}</span>)
                                              </div>
                                              {dateRange ? <div>- 기간: <span className="font-mono">{dateRange}</span></div> : null}
                                            </div>
                                          ) : (
                                            <div className="mt-1 text-[11px] text-slate-500">- 진료내역 근거 없음</div>
                                          )}

                                          {rulePack.length > 0 ? (
                                            <div className="mt-2">
                                              <div className="text-[11px] font-semibold text-slate-900">관련 룰</div>
                                              <div className="mt-0.5 space-y-0.5 text-[11px] text-slate-700">
                                                {rulePack.slice(0, 8).map((x, idx) => (
                                                  <div key={idx}>
                                                    -{' '}
                                                    <span className="font-mono font-semibold">
                                                      {x.applied === false ? '미적용' : statusLabelKr(x.decisionType)}
                                                    </span>{' '}
                                                    <span className="font-mono">{conditionKeyToUiCode(x.conditionKey)}</span>
                                                    {name ? <span className="text-slate-700"> • {name}</span> : null}
                                                    {(() => {
                                                      const parts = []

                                                      // 핵심 조건만 요약(가독성 목적)
                                                      if (x.waitingDays !== null && x.waitingDays !== undefined && String(x.waitingDays).trim() !== '') {
                                                        const wd = Number(x.waitingDays)
                                                        const dse = x.daysSinceEnd !== null && x.daysSinceEnd !== undefined ? Number(x.daysSinceEnd) : null
                                                        const ok = Number.isFinite(wd) && dse !== null && Number.isFinite(dse) ? dse >= wd : null
                                                        // waitingDays는 "마지막 진료(치료종결로 간주) 이후 N일 경과" 조건으로 사용된다.
                                                        const suffix = ok === null ? `치료종결 후 ${x.waitingDays}일` : `치료종결 후 ${x.waitingDays}일`
                                                        parts.push(suffix)
                                                      }
                                                      if (x.maxCount !== null && x.maxCount !== undefined && String(x.maxCount).trim() !== '') {
                                                        const mc = Number(x.maxCount)
                                                        const cur =
                                                          x.matchedCount !== null && x.matchedCount !== undefined ? Number(x.matchedCount) : epPack?.counts?.total ?? null
                                                        const ok = Number.isFinite(mc) && cur !== null && Number.isFinite(Number(cur)) ? Number(cur) <= mc : null
                                                        const suffix = ok === null ? `최대 ${x.maxCount}` : `최대 ${x.maxCount}`
                                                        parts.push(suffix)
                                                      }
                                                      if (
                                                        x.inpatientMaxDays !== null &&
                                                        x.inpatientMaxDays !== undefined &&
                                                        String(x.inpatientMaxDays).trim() !== ''
                                                      ) {
                                                        const lim = Number(x.inpatientMaxDays)
                                                        const mx =
                                                          x.inpatientDaysMax !== null && x.inpatientDaysMax !== undefined
                                                            ? Number(x.inpatientDaysMax)
                                                            : null
                                                        const ok =
                                                          Number.isFinite(lim) && mx !== null && Number.isFinite(mx) ? mx <= lim : null
                                                        const suffix = ok === null ? `입원 ${x.inpatientMaxDays}일 이내` : `입원 ${x.inpatientMaxDays}일 이내`
                                                        parts.push(suffix)
                                                      }
                                                      const cleanedNote = (() => {
                                                        const raw = (x.note || '').toString().trim()
                                                        if (!raw) return ''
                                                        // note 예: "inpatient_max_days=10; source_row=18"
                                                        const tokens = raw
                                                          .split(';')
                                                          .map((t) => t.trim())
                                                          .filter(Boolean)
                                                          .filter((t) => !/^source_row\s*=\s*\d+$/i.test(t))
                                                          .filter((t) => !/^inpatient_max_days\s*=\s*\d+$/i.test(t))
                                                          .filter((t) => !/^waiting_months\s*=\s*\d+$/i.test(t))
                                                          .filter((t) => !/^waiting_days\s*=\s*\d+$/i.test(t))
                                                          .filter((t) => !/^surgery_condition_type\s*=\s*.+$/i.test(t))
                                                          .filter((t) => !/^requires_(no_)?surgery\s*=\s*(true|false)$/i.test(t))
                                                          // 내부/과거 값 보존 태그(운영 디버깅용) → 고객/설계 흐름에서는 노출하지 않음
                                                          .filter((t) => !/^legacy_decision\s*=\s*[A-Z_]+$/i.test(t))
                                                        return tokens.join('; ')
                                                      })()
                                                      if (cleanedNote) parts.push(cleanedNote)
                                                      if (parts.length === 0) return null
                                                      return <span className="text-slate-500"> • {parts.join(' • ')}</span>
                                                    })()}
                                                    {x.message ? <span className="text-slate-700"> • {x.message}</span> : null}
                                                  </div>
                                                ))}
                                              </div>
                                              {rulePack.length > 8 ? (
                                                <div className="mt-1 text-[11px] text-slate-500">* 룰은 상위 8건만 표시됩니다.</div>
                                              ) : null}
                                            </div>
                                          ) : (
                                            <div className="mt-2 text-[11px] text-slate-500">- 걸린 룰 없음</div>
                                          )}

                                          {groups.length > 0 ? (
                                            <div className="mt-2">
                                              <button
                                                type="button"
                                                className="rounded border bg-white px-2 py-1 text-[11px] hover:bg-slate-50"
                                                onClick={() => {
                                                  const next = new Set(threeNnTemplateKcdExpanded)
                                                  if (next.has(expandKey)) next.delete(expandKey)
                                                  else next.add(expandKey)
                                                  setThreeNnTemplateKcdExpanded(next)
                                                }}
                                              >
                                                {isExpanded ? '근거 진료내역 닫기' : `근거 진료내역 보기 (${groups.length})`}
                                              </button>
                                              {isExpanded ? (
                                                <div className="mt-2">
                                                  <div className="text-[11px] font-semibold text-slate-900">근거 진료내역(중복 요약)</div>
                                                  <div className="mt-0.5 space-y-0.5 text-[11px] text-slate-700">
                                                    {groups.slice(0, 6).map((g, idx) => (
                                                      <div key={idx}>
                                                        - {episodeSummary(g.ep)}
                                                        {g.count > 1 ? <span className="ml-1 font-mono text-slate-500">x{g.count}</span> : null}
                                                      </div>
                                                    ))}
                                                  </div>
                                                  {groups.length > 6 ? (
                                                    <div className="mt-1 text-[11px] text-slate-500">* 진료내역은 상위 6그룹만 표시됩니다.</div>
                                                  ) : null}
                                                </div>
                                              ) : null}
                                            </div>
                                          ) : null}
                                        </div>
                                      )
                                    })}
                                  </div>
                                  )}
                                </details>

                                {nonExceptionKcdKeys.length > 0 ? (
                                  <details className="mt-3 rounded border bg-white p-2">
                                    <summary className="cursor-pointer text-[11px] font-semibold text-slate-900">
                                      예외질환 미해당 진료내역 ({nonExceptionKcdKeys.length})
                                    </summary>
                                    <div className="mt-2 space-y-0.5 text-[11px] text-slate-700">
                                      {nonExceptionKcdKeys.slice(0, 20).map((k) => {
                                        const nm = allEpisodeKcdMap.get(k)?.diagnosisName || ''
                                        return (
                                          <div key={k}>
                                            - <span className="font-mono font-semibold">{formatKcdDottedForMemo(k)}</span>
                                            {nm ? <span className="text-slate-700"> ({nm})</span> : null}
                                          </div>
                                        )
                                      })}
                                      {nonExceptionKcdKeys.length > 20 ? (
                                        <div className="text-[11px] text-slate-500">… 외 {nonExceptionKcdKeys.length - 20}개</div>
                                      ) : null}
                                    </div>
                                  </details>
                                ) : null}

                                {rulesByKcd.get('__OTHER__')?.length > 0 ? (
                                  <div className="mt-3">
                                    <div className="text-[11px] font-semibold text-slate-900">기타(코드 매칭 불가) 룰</div>
                                    <div className="mt-1 space-y-0.5 text-[11px] text-slate-700">
                                      {rulesByKcd
                                        .get('__OTHER__')
                                        .slice(0, 6)
                                        .map((x, idx) => (
                                          <div key={idx}>
                                            - <span className="font-mono font-semibold">{statusLabelKr(x.decisionType)}</span>{' '}
                                            <span className="font-mono text-slate-500">({x.decisionType})</span> {x.conditionKey}
                                            {x.message ? <span className="text-slate-500"> • {x.message}</span> : null}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {threeNnModalData?.kind !== 'SIMPLE_3N5' && (
              <div className="mt-3 rounded border bg-slate-50 p-2 text-[11px] text-slate-600">
                각 보험사별 예외질환 규칙은 지속적으로 업데이트 중입니다. 예외질환 추가 요청은{' '}
                <a className="font-semibold text-brand-sky hover:underline" href="/support/inquiries">
                  고객문의
                </a>
                를 이용해 주세요.
              </div>
            )}
          </div>
        </div>
      )}

      {/* 설계의뢰 팝업 (표준체/유병자 선택) */}
      {designRequestModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-2 sm:p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-lg p-3 sm:p-4 text-sm max-h-[90vh] sm:max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-semibold text-slate-900">설계의뢰</div>
              <button
                type="button"
                className="shrink-0 text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setDesignRequestModalOpen(false)}
              >
                닫기
              </button>
            </div>

            <div className="rounded border bg-slate-50 p-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 whitespace-nowrap">설계 유형</div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <label className="inline-flex items-center gap-2 whitespace-nowrap">
                      <input
                        type="radio"
                        name="designCoverageType"
                        value="healthy"
                        checked={designCoverageType === 'healthy'}
                        onChange={() => setDesignCoverageType('healthy')}
                      />
                      <span>표준체(건강체)</span>
                    </label>
                    <label className="inline-flex items-center gap-2 whitespace-nowrap">
                      <input
                        type="radio"
                        name="designCoverageType"
                        value="substandard"
                        checked={designCoverageType === 'substandard'}
                        onChange={async () => {
                          setDesignCoverageType('substandard')
                          setSubstandardIgnoreRecent3mOpinion(true)
                          if (selectedReportId) {
                            await handleEvaluate3nn(selectedReportId, { openModal: false, ignoreRecent3mOpinion: true })
                          }
                        }}
                      />
                      <span>유병자(간편보험)</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:justify-end flex-shrink-0">
                  <button
                    type="button"
                    className="flex-1 sm:flex-none sm:w-auto whitespace-nowrap rounded border px-2 py-2 sm:px-2 sm:py-1 text-xs border-slate-300 bg-white hover:bg-slate-50"
                    onClick={() => openQuestionnaire(designCoverageType === 'healthy' ? 'healthy' : 'substandard')}
                  >
                    질문서 보기
                  </button>
                  <button
                    type="button"
                    className="flex-1 sm:flex-none sm:w-auto whitespace-nowrap rounded border px-2 py-2 sm:px-2 sm:py-1 text-xs border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40"
                    disabled={designCoverageType !== 'substandard' || threeNnTemplateLoading}
                    onClick={async () => {
                      if (designCoverageType !== 'substandard') return
                      if (selectedReportId) {
                        await handleEvaluate3nnTemplates(selectedReportId, { openModal: true })
                      } else {
                        show('리포트를 먼저 선택해 주세요.', 'error')
                      }
                    }}
                    title={designCoverageType !== 'substandard' ? '유병자(간편보험)에서만 사용 가능합니다.' : undefined}
                  >
                    {threeNnTemplateLoading ? '불러오는 중...' : '보험사별 예외 적용'}
                  </button>
                  <button
                    type="button"
                    className="flex-1 sm:flex-none sm:w-auto whitespace-nowrap rounded border px-2 py-2 sm:px-2 sm:py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40"
                    disabled={designCoverageType === 'substandard' && threeNnLoading}
                    onClick={() =>
                      handleGoToRequestNewFrom3nn({
                        coverageType: designCoverageType,
                        selectedInsurerCodes:
                          designCoverageType === 'substandard' ? Array.from(selectedInsurerCodesForRequest || []) : null,
                      })
                    }
                  >
                    {designCoverageType === 'substandard' && threeNnLoading ? '분석 중...' : '설계의뢰 생성'}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {designCoverageType === 'substandard' ? (
                <div className="rounded border bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    {/* <div className="text-xs font-semibold text-slate-900">1차 요약</div> */}
                    <span className="rounded bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">유병자 3N5</span>
                  </div>
                  {threeNnResult?.kind === 'SIMPLE_3N5' ? (
                    <div className="mt-2 text-xs text-slate-700 space-y-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 text-slate-700">
                          - <span className="font-semibold text-slate-900">인수기준 예외 처리</span>{' '}
                          <span className="text-slate-700">(최근 3개월 “입원/수술/재검사 소견” 제외)</span>
                          <div className="mt-0.5 ml-4 text-[11px] text-slate-500">
                            진료내역만으로 소견 여부 판단이 어려워, 체크 시 3N5의 “3개월” 조건을 판정에서 제외하고(경고로 표시) 나머지 기준으로 조합을 안내합니다.
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={substandardIgnoreRecent3mOpinion}
                          onChange={async (e) => {
                            const next = Boolean(e.target.checked)
                            setSubstandardIgnoreRecent3mOpinion(next)
                            if (selectedReportId) {
                              await handleEvaluate3nn(selectedReportId, { openModal: false, ignoreRecent3mOpinion: next })
                            }
                          }}
                        />
                      </div>
                      {substandardIgnoreRecent3mOpinion && (
                        <div className="ml-4 rounded border bg-white px-2 py-2 text-[11px] text-slate-700">
                          <div className="font-semibold text-slate-900">최근 3개월(투약 제외) 확인 근거</div>
                          {Array.isArray(threeNnResult?.exception?.recent3mNonDrugExamples) &&
                          threeNnResult.exception.recent3mNonDrugExamples.length > 0 ? (
                            <div className="mt-1 space-y-0.5 text-slate-600">
                              {threeNnResult.exception.recent3mNonDrugExamples.map((x, idx) => (
                                <div key={idx}>- {x}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-1 text-slate-500">표시할 내역이 없습니다.</div>
                          )}
                        </div>
                      )}
                      <div>
                        - 결과:{' '}
                        <span className="font-semibold">
                          {threeNnResult?.status === 'POSSIBLE'
                            ? '가능'
                            : threeNnResult?.status === 'DECLINED'
                              ? '불가'
                              : threeNnResult?.status || '-'}
                        </span>
                      </div>
                      {Array.isArray(threeNnResult?.possibleCombos) && (
                        <div>- 가능 플랜: {threeNnResult.possibleCombos.length ? threeNnResult.possibleCombos.join(', ') : '없음'}</div>
                      )}
                      {Array.isArray(threeNnResult?.reasons) && threeNnResult.reasons.length > 0 && (
                        <div className="mt-1">
                          <div className="font-semibold text-slate-900">이유</div>
                          <div className="mt-1 space-y-0.5">
                            {threeNnResult.reasons.map((x, idx) => (
                              <div key={idx} className="text-slate-600">
                                - {x}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {threeNnResult?.footnote && (
                        <div className="mt-2 text-[11px] text-slate-500 whitespace-pre-wrap">{threeNnResult.footnote}</div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-600">
                      아직 유병자 3N5(1차) 결과가 없습니다. (필요 시 다시 시도해 주세요)
                    </div>
                  )}

                  {selectedInsurerCodesForRequest && selectedInsurerCodesForRequest.size > 0 && (
                    <div className="mt-3 rounded border bg-white p-2 text-xs text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900">
                          선택된 보험사 ({selectedInsurerCodesForRequest.size})
                        </div>
                        <button
                          type="button"
                          className="text-[11px] text-slate-500 hover:text-slate-800"
                          onClick={() => setSelectedInsurerCodesForRequest(new Set())}
                        >
                          선택 해제
                        </button>
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {Array.from(selectedInsurerCodesForRequest)
                          .slice(0, 8)
                          .map((code) => (
                            <div key={code} className="text-[11px] text-slate-700">
                              - <span className="font-semibold">{insurerNameKrOf(code)}</span>{' '}
                              <span className="font-mono text-slate-500">({String(code)})</span>
                            </div>
                          ))}
                        {selectedInsurerCodesForRequest.size > 8 && (
                          <div className="text-[11px] text-slate-500">… 외 {selectedInsurerCodesForRequest.size - 8}개</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded border bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    {/* <div className="text-xs font-semibold text-slate-900">1차 요약</div> */}
                    <span className="rounded bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">표준체</span>
                  </div>
                  {(() => {
                    const hs = healthyAutoSummary || computeHealthyQuestionnaireSummary()
                    const row = (label, obj) => (
                      <div>
                        - {label}: <span className="font-semibold">{obj?.has ? '있음' : '없음'}</span>
                        {obj?.has && obj?.range && (
                          <div className="mt-0.5 ml-4 text-[11px] text-slate-600">기간: {obj.range}</div>
                        )}
                        {obj?.has && Array.isArray(obj?.examples) && obj.examples.length > 0 && (
                          <div className="mt-0.5 ml-4 text-[11px] text-slate-600">내역: {obj.examples.join(', ')}</div>
                        )}
                      </div>
                    )
                    return (
                      <div className="mt-2 text-xs text-slate-700 space-y-1">
                        {row('최근 3개월(진료/검사/치료/입원/수술/투약)', hs?.recent3m)}
                        {row('최근 1년(검사/추가검사/재검사 참고)', hs?.check1y)}
                        {row('최근 5년(같은 원인 7일+ 치료 또는 30일+ 투약)', hs?.treat7orDrug30_5y)}
                        {row('최근 5년(입원/수술)', hs?.hosp5y)}
                        {row('최근 5년(11대 중대질병)', hs?.major11_5y)}
                        <div className="mt-2 text-[11px] text-slate-500">
                          * 진료내역 중 추가검사/재검사 여부는 고객 확인 필요
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI 상담 가이드 팝업 (운영자 전용) */}
      {isOperator && counselingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-lg bg-white shadow-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <div className="font-semibold text-slate-900">AI 상담 가이드</div>
                {counseling && (
                  <div className="text-xs text-slate-500 mt-1">
                    모델: {counseling.modelVersion || '-'} • 생성일: {formatDateTime(counseling.generatedAt) || '-'}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {counseling && (
                  <>
                    <button
                      type="button"
                      className="rounded border px-3 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40"
                      onClick={() => generateCounseling(selectedReportId, null, null, true)}
                      disabled={counselingGenerating}
                    >
                      {counselingGenerating ? '재생성 중...' : '재생성'}
                    </button>
                    <button
                      type="button"
                      className="rounded border px-3 py-1 text-xs border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => {
                        if (window.confirm('AI 상담 가이드를 삭제할까요?\n삭제 후에는 이 리포트에서 해당 가이드를 다시 확인할 수 없습니다.')) {
                          deleteCounseling(selectedReportId)
                        }
                      }}
                    >
                      삭제
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
                  onClick={() => setCounselingModalOpen(false)}
                >
                  닫기
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <AiGeneratedNotice className="mb-3" />
              {counselingLoading ? (
                <div className="text-center py-8 text-slate-500">AI 상담 가이드를 불러오는 중...</div>
              ) : counseling ? (
                counseling.counseling ? (
                  <div className="space-y-4 text-sm">
                    {/* 3줄 요약 */}
                    {counseling.counseling.summary_3lines && (
                      <div className="rounded-lg border bg-blue-50 p-4">
                        <div className="font-semibold text-blue-900 mb-3">요약</div>
                        <div className="text-sm text-slate-700 space-y-2">
                          {counseling.counseling.summary_3lines.map((line, idx) => (
                            <div key={idx} className="text-slate-800">{idx + 1}. {line}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 질문 (우선순위별) */}
                    {counseling.counseling.questions && (
                      <div className="rounded-lg border bg-green-50 p-4">
                        <div className="font-semibold text-green-900 mb-3">상담 질문</div>
                        <div className="text-sm text-slate-700 space-y-3">
                          {counseling.counseling.questions.map((q, idx) => (
                            <div key={idx} className="rounded border bg-white p-3">
                              <div className="flex items-start gap-2 mb-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  q.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                                  q.priority === 'MID' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {q.priority || 'MID'}
                                </span>
                                <div className="flex-1 font-medium text-slate-800">{q.q}</div>
                              </div>
                              {q.why && (
                                <div className="text-xs text-slate-600 mt-1">→ {q.why}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 보상/청구 체크 */}
                    {counseling.counseling.claim_check && (
                      <div className="rounded-lg border bg-amber-50 p-4">
                        <div className="font-semibold text-amber-900 mb-3">보상/청구 체크 포인트</div>
                        <ul className="text-sm text-slate-700 list-disc list-inside space-y-2">
                          {counseling.counseling.claim_check.map((item, idx) => (
                            <li key={idx} className="text-slate-800">{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* 보장 공백 */}
                    {counseling.counseling.coverage_gaps && (
                      <div className="rounded-lg border bg-purple-50 p-4">
                        <div className="font-semibold text-purple-900 mb-3">보장 공백 의심 영역</div>
                        <div className="text-sm text-slate-700 space-y-3">
                          {counseling.counseling.coverage_gaps.map((gap, idx) => (
                            <div key={idx} className="rounded border bg-white p-3">
                              <div className="font-medium text-slate-800 mb-1">{gap.area}</div>
                              {gap.reason_keywords && gap.reason_keywords.length > 0 && (
                                <div className="text-xs text-slate-600">
                                  근거: {gap.reason_keywords.join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 동연령/동성별 참고 */}
                    {counseling.counseling.peer_reference && (
                      <div className="rounded-lg border bg-indigo-50 p-4">
                        <div className="font-semibold text-indigo-900 mb-3">참고 정보</div>
                        <div className="text-sm text-slate-700 space-y-2">
                          {counseling.counseling.peer_reference.note && (
                            <div className="text-slate-800 mb-2">{counseling.counseling.peer_reference.note}</div>
                          )}
                          {counseling.counseling.peer_reference.axes && (
                            <div className="flex flex-wrap gap-2">
                              {counseling.counseling.peer_reference.axes.map((axis, idx) => (
                                <span key={idx} className="px-2 py-1 rounded bg-indigo-100 text-indigo-800 text-xs">
                                  {axis}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 다음 액션 */}
                    {counseling.counseling.next_actions && (
                      <div className="rounded-lg border bg-teal-50 p-4">
                        <div className="font-semibold text-teal-900 mb-3">다음 액션</div>
                        <ul className="text-sm text-slate-700 list-disc list-inside space-y-2">
                          {counseling.counseling.next_actions.map((action, idx) => (
                            <li key={idx} className="text-slate-800">{action}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* 면책 조항 */}
                    {counseling.counseling.disclaimer && (
                      <div className="rounded-lg border bg-gray-100 p-3">
                        <div className="text-xs text-slate-600 italic">{counseling.counseling.disclaimer}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">상담 가이드 데이터가 없습니다.</div>
                )
              ) : (
                <div className="text-center py-8">
                  <div className="text-slate-500 mb-4">AI 상담 가이드가 생성되지 않았습니다.</div>
                  <button
                    type="button"
                    className="rounded border px-4 py-2 text-sm border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40"
                    onClick={() => generateCounseling(selectedReportId)}
                    disabled={counselingGenerating}
                  >
                    {counselingGenerating ? '생성 중...' : 'AI 상담 가이드 생성'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 건강체/유병자 질문서 팝업 */}
      {questionnaireModalType && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-lg p-4 max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-slate-900">
                {questionnaireModalType === 'healthy' ? '질문서: 건강체(표준체)' : '질문서: 유병자(간편보험)'}
              </div>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setQuestionnaireModalType(null)}
              >
                닫기
              </button>
            </div>

            {questionnaireModalType === 'healthy' ? (
              <div className="space-y-3 text-xs text-slate-700">
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 3개월</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>
                      진찰/검사(건강검진 포함) 후{' '}
                      <span className="font-semibold">질병확정진단·질병의심소견·치료·입원·수술(제왕절개 포함)·투약</span> 여부
                    </li>
                    <li>마약 사용 또는 혈압강하제/신경안정제/수면제/각성제/흥분제/진통제 등 약물 상시 복용 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 1년</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>진찰/검사 후 추가검사·재검사 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 5년</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>입원/수술(제왕절개 포함) 여부</li>
                    <li>같은 원인으로 계속 7일 이상 치료 또는 30일 이상 투약 여부</li>
                    <li>
                      11대 중대질병(암/백혈병/고혈압/협심증/심근경색/심장판막증/간경화/뇌졸중(뇌출혈·뇌경색)/당뇨병/에이즈(HIV)/만성폐질환) 관련{' '}
                      <span className="font-semibold">진단·치료·입원·수술·투약</span> 여부
                    </li>
                  </ol>
                </div>
                <div className="text-[11px] text-slate-500">
                  * 실제 고지/인수는 보험사·상품·예외질환/면책 등에 따라 달라질 수 있습니다.
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-xs text-slate-700">
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 3개월</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>입원소견/수술소견/추가검사·재검사 소견/질병확정진단·질병의심소견 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 N년</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>입원/수술 여부</li>
                    <li>암/뇌졸중/심근경색/협심증/심장판막증/간경화로 인한 진단/입원/수술 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">고지(예시)</div>
                  <div className="mt-1 text-xs text-slate-700">
                    당뇨/고혈압/고지혈/우울증/갑상선항진증 등은 약 투약 포함 고지 필요(보험사/상품별 상이)
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">
                  * 유병자 탭의 3개월/입원·수술 필터는 진료내역 기반 “1차 참고용”이며, 소견 여부는 추가 확인이 필요합니다.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 주민번호 재입력 팝업 (TTL 만료 재시도) */}
      {retryJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">재시도: 주민등록번호 재입력</div>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setRetryJobId(null)}
              >
                닫기
              </button>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">주민등록번호 (숫자만)</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="w-full rounded border px-2 py-1 text-sm font-mono"
                value={formatIdentityDigitsForUi(retryIdentity)}
                onChange={handleRetryIdentityChange}
                placeholder="숫자 13자리"
                aria-label="주민등록번호 13자리"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => setRetryJobId(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="rounded bg-brand-navy px-3 py-1 text-sm text-white hover:bg-brand-sky"
                onClick={handleRetryIdentity}
              >
                재시도 시작
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 실패 사유 팝업 */}
      {failInfoJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">실패 사유</div>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setFailInfoJob(null)}
              >
                닫기
              </button>
            </div>

            <div className="mt-3 space-y-2 text-sm">
              {(() => {
                const p = parseJobFailure(failInfoJob.lastError)
                return (
                  <>
                    <div className="text-xs text-slate-600">
                      Job ID: <span className="font-mono">{failInfoJob.jobId}</span> • 상태:{' '}
                      <span className="font-mono">{jobStatusLabel(failInfoJob.status)}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="rounded border p-2">
                        <div className="text-slate-500">실패 구간</div>
                        <div className="font-mono text-slate-900">{p.phase || '-'}</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="text-slate-500">HTTP</div>
                        <div className="font-mono text-slate-900">{p.httpStatus || '-'}</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="text-slate-500">CODEF 코드</div>
                        <div className="font-mono text-slate-900">{p.code || '-'}</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="text-slate-500">CODEF 메시지</div>
                        <div className="font-mono text-slate-900">{p.message || '-'}</div>
                      </div>
                    </div>
                    {p.summary ? (
                      <div className="rounded border bg-white p-2 text-xs">
                        <div className="text-slate-500 mb-0.5">요약</div>
                        <div className="text-slate-900">{p.summary}</div>
                      </div>
                    ) : null}
                    <div className="rounded border bg-slate-50 p-3">
                      <div className="text-xs text-slate-500 mb-1">원문(lastError)</div>
                      <pre className="whitespace-pre-wrap break-words text-xs font-mono text-slate-900">
                        {String(failInfoJob.lastError || '사유를 확인할 수 없습니다. (lastError가 비어있음)')}
                      </pre>
                    </div>
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                        onClick={async () => {
                          const lines = []
                          lines.push(`[HealthJob 실패]`)
                          lines.push(`- jobId: ${failInfoJob.jobId ?? '-'}`)
                          lines.push(`- status: ${failInfoJob.status ?? '-'}`)
                          if (failInfoJob.createdAt) lines.push(`- createdAt: ${failInfoJob.createdAt}`)
                          if (failInfoJob.updatedAt) lines.push(`- updatedAt: ${failInfoJob.updatedAt}`)
                          lines.push(`- phase: ${p.phase || '-'}`)
                          lines.push(`- httpStatus: ${p.httpStatus || '-'}`)
                          lines.push(`- codefCode: ${p.code || '-'}`)
                          lines.push(`- codefMessage: ${p.message || '-'}`)
                          if (p.summary) lines.push(`- summary: ${p.summary}`)
                          lines.push(`- lastError: ${p.raw || '-'}`)
                          const payload = lines.join('\n')
                          const ok = await copyToClipboard(payload)
                          if (ok) show('복사되었습니다. 운영자에게 전달해 주세요.', 'success')
                          else show('복사에 실패했습니다. (브라우저 권한/환경 문제)', 'error')
                        }}
                      >
                        운영 문의용 복사
                      </button>
                    </div>
                  </>
                )
              })()}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                  onClick={() => setFailInfoJob(null)}
                >
                  닫기
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy px-3 py-1 text-sm text-white hover:bg-brand-sky disabled:opacity-40"
                  onClick={async () => {
                    const j = failInfoJob
                    setFailInfoJob(null)
                    await handleRetrySameInfo(j)
                  }}
                  disabled={retryingJobId === failInfoJob.jobId}
                >
                  {retryingJobId === failInfoJob.jobId ? '재시도 중...' : '재시도'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

